import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  makeStyles,
  colors,
  Divider,
  Button,
  Collapse,
  CardHeader,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Menu,
  Select,
  Paper,
  MenuItem,
  Snackbar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import RemoveIcon from '@material-ui/icons/Remove';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import EditIcon from '@material-ui/icons/Edit';
import BuildIcon from '@material-ui/icons/Build';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import EpicrisisView from './EpicrisisView';
import StatusRapportView from './StatusRapportView';
import DevelopmentView from './DevelopmentView';
import ClinicianFollowupDialog from './ClinicianFollowupDialog';
import ExerciseEditDialog from '../exerciseLibraryFolder/ExerciseEditDialog';
import ExerciseLibrary from '../exerciseLibraryFolder/exerciseLibrary';
import { useFeature } from '../../../featureGating/featureGating';
import AdviceLibrary from '../exerciseLibraryFolder/adviceLibrary';
import { apiBaseUrl } from '../../../utilities/configureApiFetch';
// SaveIcon removed — global save applies on finish
// DiscreteSliderAdmin removed — not used in this component

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    borderRadius: 14,
    backgroundColor: '#f7faff',
    boxShadow: "0 6px 32px rgba(0,45,85,0.18)",
    background: '#f7faff',
    borderRadius: 14,
  },
  Button: {
    color: '#002D55',
    border: '1px solid #002D55',
    '&:hover': {
      backgroundColor: '#f5f7fb',
      color: '#002D55',
    },
  },
  Button2: {
    color: '#FFFFFF',
    backgroundColor: '#002D55',
    '&:hover': {
      backgroundColor: '#004b80',
    },
  },
  Button3: {
    color: '#FFFFFF',
    backgroundColor: '#d9534f',
    '&:hover': {
      backgroundColor: '#c9302c',
    },
  },
  
  compactText: {
    lineHeight: 1.5,
    marginBottom: 0,
    color: '#324158',
    marginLeft: 18,
  },
  
  headerViewSelect: {
    minWidth: 'clamp(110px, calc(66px + 3.2vw), 150px)',
    minHeight: 'clamp(24px, 2.15vw, 27.5px)',
    background: '#FFFFFF',
    color: '#002D55',
    fontWeight: 600,
    borderRadius: 14,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)',
      padding: '0 clamp(28px, 2.2vw, 34px) 0 clamp(8px, 0.7vw, 12px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      color: '#002D55',
      fontWeight: 'bold',
    },
    '& .MuiSelect-iconOutlined': {
      right: 6,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      '& fieldset': {
        borderColor: '#ccc',
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: '#FF7F27',
        borderWidth: 1,
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FF7F27',
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      outline: 'none',
    },
    '& *:focus': {
      outline: 'none',
    },
  },
  
  headerViewMenuItem: {
    fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)',
    color: '#002D55',
    '&:hover': {
      color: '#FF7F27',
      backgroundColor: '#f7faff',
    },
    '&.Mui-selected': {
      color: '#FF7F27',
      backgroundColor: '#f7faff',
      fontWeight: 700,
    },
    '&.Mui-selected:hover': {
      color: '#FF7F27',
      backgroundColor: '#f7faff',
    },
  },
  cardHeaderRoot: {
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    padding: 'clamp(8px, calc(1px + 0.55vw), 12px) clamp(10px, calc(2px + 0.625vw), 16px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #FF7F27',
    '& .MuiCardHeader-content': {
      overflow: 'hidden',
    },
    '& .MuiCardHeader-action': {
      marginTop: 0,
      marginRight: 'clamp(4px, calc(1px + 0.3125vw), 8px)',
      alignSelf: 'center',
    },
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerDateText: {
    fontSize: 'clamp(0.66rem, calc(0.29rem + 0.45vw), 0.78rem)',
    color: '#666',
    lineHeight: 1.2,
  },
  headerActionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: '100%',
  },
  headerControlButton: {
    marginTop: 0,
    marginBottom: 0,
    textTransform: 'none',
  },
  headerPrimaryButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(2px, 0.24vw, 4px) clamp(8px, 0.7vw, 12px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  headerSecondaryButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 18,
    border: '1px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(2px, 0.24vw, 4px) clamp(8px, 0.7vw, 12px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  headerNavButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 18,
    border: '1px solid transparent',
    boxShadow: 'none',
    minWidth: 36,
    padding: 'clamp(2px, 0.24vw, 4px) clamp(8px, 0.7vw, 12px)',
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
    lineHeight: 1,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  diagnosisDecisionButton: {
    borderRadius: 18,
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  diagnosisChangeSelect: {
    minWidth: 'clamp(110px, 11vw, 170px)',
    minHeight: 'clamp(24px, 2.15vw, 27.5px)',
    maxWidth: '100%',
    background: '#FFFFFF',
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    borderRadius: 14,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
      fontWeight: 'bold',
      color: '#002D55',
      padding: '0 clamp(16px, 1.25vw, 20px) 0 clamp(8px, 0.7vw, 12px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      display: 'flex',
      alignItems: 'center',
      background: 'transparent',
    },
    '& .MuiSelect-iconOutlined, & .MuiSelect-icon': {
      right: 6,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
      borderRadius: 14,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  diagnosisAutocompleteRoot: {
    minWidth: 'clamp(220px, 28vw, 320px)',
    '& .MuiOutlinedInput-root': {
      minHeight: 'clamp(24px, 2.15vw, 27.5px)',
      borderRadius: 14,
      backgroundColor: '#FFFFFF',
      color: '#002D55',
      fontWeight: 600,
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
      paddingTop: 'clamp(4px, 0.5vw, 6px) !important',
      paddingBottom: 'clamp(4px, 0.5vw, 6px) !important',
      paddingLeft: 'clamp(8px, 0.7vw, 12px) !important',
      color: '#002D55',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  diagnosisAutocompletePaper: {
    borderRadius: 14,
  },
  diagnosisAutocompleteOption: {
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    color: '#002D55',
  },
  baselineKpiSelect: {
    minWidth: 110,
    minHeight: 'clamp(24px, 2.15vw, 27.5px)',
    background: 'transparent',
    color: '#002D55',
    fontWeight: 600,
    borderRadius: 14,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    '& .MuiSelect-select, & .MuiSelect-select.MuiSelect-select': {
      fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: 'clamp(8px, 0.7vw, 12px) !important',
      paddingRight: '40px !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      color: '#002D55',
      fontWeight: 'bold',
    },
    '& .MuiSelect-iconOutlined, & .MuiSelect-icon': {
      right: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27 !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      outline: 'none',
    },
    '& .MuiSelect-select:focus': {
      background: 'transparent',
    },
    '& *:focus': {
      outline: 'none',
    },
  },
  customKpiTypeToggle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    height: '100%',
    width: 'clamp(28px, 3.1vw, 40px)',
    minWidth: 'clamp(28px, 3.1vw, 40px)',
    background: 'transparent',
    borderRadius: '0 14px 14px 0',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '& .MuiSelect-select, & .MuiSelect-select.MuiSelect-select': {
      padding: '0 !important',
      minHeight: 'unset !important',
      width: 0,
      color: 'transparent',
    },
    '& .MuiSelect-iconOutlined, & .MuiSelect-icon': {
      right: 'clamp(6px, 0.8vw, 10px)',
      color: '#324158',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  customKpiInlineField: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: 'clamp(24px, 2.15vw, 27.5px)',
    minHeight: 'clamp(24px, 2.15vw, 27.5px)',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    borderRadius: 14,
    paddingLeft: 'clamp(8px, 0.7vw, 12px)',
    paddingRight: 'clamp(34px, 3.9vw, 48px)',
    '&:hover': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputBase-root': {
      height: '100%',
    },
    '& .MuiInputBase-input': {
      paddingRight: 'clamp(8px, 1vw, 12px)',
      boxSizing: 'border-box',
    },
  },
  noNumberSpinner: {
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
  baselineKpiMenuItem: {
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    color: '#002D55',
    '&:hover': {
      color: '#FF7F27',
      backgroundColor: '#f7faff',
    },
    '&.Mui-selected': {
      color: '#FF7F27',
      backgroundColor: 'transparent',
      fontWeight: 700,
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#f7faff',
    },
  },
  diagnosisChangeCancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 700,
    borderRadius: 14,
    border: '1px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(10px, 0.95vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  diagnosisChangeConfirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 14,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(10px, 0.95vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#CBD5E1',
      color: '#64748B',
    },
  },
  diagnosisChangeMenuItem: {
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    color: '#002D55',
  },
  diagnosisActionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  diagnosisOtherTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#fff',
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiInputLabel-outlined': {
      color: '#002D55',
    },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    color: '#002D55',
    fontWeight: 600,
    paddingRight: 0,
  },
  dialogCloseButton: {
    color: '#002D55',
    backgroundColor: '#e6eaf0',
    border: '1px solid transparent',
    padding: 6,
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      borderColor: '#FF7F27',
    },
  },
  dialogActionsRow: {
    padding: '0 24px 20px',
    gap: 8,
  },
  diagnosisPreviewDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    maxHeight: '68vh',
    overflowY: 'auto',
  },
  diagnosisPreviewSummary: {
    border: '1px solid rgba(0,45,85,0.12)',
    borderRadius: 14,
    backgroundColor: '#f7faff',
    padding: 14,
  },
  diagnosisPreviewPhaseCard: {
    border: '1px solid rgba(0,45,85,0.12)',
    borderRadius: 14,
    backgroundColor: '#fff',
    padding: 14,
    boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
  },
  diagnosisPreviewPhaseHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  },
  diagnosisPreviewPhaseTabs: {
    minHeight: 34,
    marginBottom: 12,
    '& .MuiTabs-indicator': {
      backgroundColor: '#FF7F27',
      height: 3,
      borderRadius: 999,
    },
  },
  diagnosisPreviewPhaseTab: {
    minHeight: 34,
    minWidth: 96,
    textTransform: 'none',
    fontWeight: 700,
    color: '#486d86',
    borderRadius: 12,
    marginRight: 6,
    backgroundColor: '#f7faff',
    border: '1px solid rgba(0,45,85,0.08)',
    '&:hover': {
      color: '#002D55',
      opacity: 1,
    },
  },
  diagnosisPreviewPhaseTabSelected: {
    color: '#002D55',
    backgroundColor: '#eaf2fb',
  },
  diagnosisPreviewSectionLabel: {
    color: '#FF7F27',
    fontWeight: 700,
    marginBottom: 6,
  },
  diagnosisPreviewMuted: {
    color: '#486d86',
  },
  diagnosisPreviewList: {
    margin: '6px 0 0 18px',
    padding: 0,
  },
  diagnosisPreviewListItem: {
    marginBottom: 4,
  },
  diagnosisPreviewItemCard: {
    borderRadius: 14,
    padding: 'clamp(10px, 0.9vw, 14px)',
    background: '#ffffff',
    border: '1px solid #dce7f2',
    boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
  },
  diagnosisPreviewItemCardDimmed: {
    opacity: 0.5,
  },
  diagnosisPreviewItemCardActive: {
    cursor: 'pointer',
  },
  diagnosisPreviewItemTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'clamp(8px, 0.78vw, 12px)',
    flexWrap: 'wrap',
  },
  diagnosisPreviewItemTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'clamp(8px, 0.78vw, 12px)',
    flex: 1,
    minWidth: 180,
  },
  diagnosisPreviewItemAvatar: {
    width: 58,
    height: 58,
    backgroundColor: '#eef5fb',
    color: '#002D55',
    border: '1px solid #dce7f2',
    flexShrink: 0,
    objectFit: 'cover',
  },
  diagnosisPreviewItemTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)',
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  },
  diagnosisPreviewItemBody: {
    color: '#5f7287',
    fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  diagnosisPreviewItemBodyClamped: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    whiteSpace: 'normal',
    marginTop: 4,
  },
  diagnosisPreviewItemBadge: {
    backgroundColor: '#e3eef9',
    color: '#002D55',
    fontWeight: 700,
  },
  diagnosisPreviewCardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(4px, 0.39vw, 6px)',
    flexWrap: 'wrap',
    marginLeft: 'auto',
  },
  diagnosisPreviewActionButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(2px, 0.24vw, 4px) clamp(6px, 0.6vw, 10px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(58px, 4.6vw, 82px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  // styled buttons (see Button, Button2, Button3 earlier)
}));

  const PATIENT_CONSENT_B2C_DEFAULT_TEXT = 'Patient has given explicit consent to the treatment and expressed preference for this through active subscription, as well as the collection and sharing of health data with relevant healthcare professionals they are matched with for monitoring, quality, and safety of the remote physiotherapy service.';
  const PATIENT_CONSENT_CLINICIAN_DEFAULT_TEXT = 'The patient consents to treatment, record-keeping, remote monitoring, and the necessary sharing of health information in accordance with applicable law.';
  const PATIENT_CONSENT_PLACEHOLDER_TEXT = 'Patient consent note';

  const normalizeJournalText = (value) => (value || '')
    .toString()
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const isDefaultPatientConsentText = (value) => {
    const normalizedValue = normalizeJournalText(value);
    return normalizedValue === normalizeJournalText(PATIENT_CONSENT_B2C_DEFAULT_TEXT)
      || normalizedValue === normalizeJournalText(PATIENT_CONSENT_CLINICIAN_DEFAULT_TEXT);
  };

  const isPlaceholderPatientConsentText = (value) => normalizeJournalText(value) === normalizeJournalText(PATIENT_CONSENT_PLACEHOLDER_TEXT);

  const hasStaffAuthoredJournalNote = (note) => Boolean(note && !note.is_system && note.author != null);

  const HEADER_VIEW_STORAGE_KEY = 'vp_selectedPatientDiagnosisHeaderView';
  const HEADER_VIEW_OPTIONS = ['Assessment', 'Epicrisis', 'Status Rapport', 'Development', 'Questionnaires'];


const SelectedPatientDiagnosis = (props) => {
  const classes = useStyles();
  const className = props.className;
  const { selectedPatient, diagnosis, userToken, onOpenReportDialog, setActiveTab } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const canAdaptiveExercise = useFeature('adaptive_exercise');

  React.useEffect(() => {
    const uid = selectedPatient && (selectedPatient.user_id || selectedPatient.id || (selectedPatient.user && selectedPatient.user.id));
    // console.log('[SelectedPatientDiagnosis] props changed', {
    //   uid,
    //   treatmentId: selectedPatient && selectedPatient.treatment_id,
    //   selectedDiagId: selectedPatient && selectedPatient.selected_diagnosis_id,
    //   selectedDiagIdentifier: selectedPatient && selectedPatient.selected_diagnosis_identifier,
    //   diagnosisProp: diagnosis
    // });
  }, [selectedPatient, diagnosis]);

  // HoverableButton removed — styling now handled by classes.Button / classes.Button2 / classes.Button3

  // Fetch KPI history for improvement chips in baseline/goal groups
  React.useEffect(() => {
    const diagId = diagnosis && (diagnosis.diagnosis || diagnosis.diagnosis_identifier);
    const uid = selectedPatient && (selectedPatient.user_id || selectedPatient.id);
    if (!diagId || !uid || !userToken) return;
    fetch(`/question-handler/kpi-history/${diagId}/?user_id=${uid}`, {
      headers: { Authorization: `Token ${userToken}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setKpiHistory(data || {}))
      .catch(() => {});
  }, [diagnosis, selectedPatient, userToken]);

  // Helper: map kpi_type + kpi_subtype → question_identifier (mirrors backend)
  const kpiSubtypeToQId = React.useCallback((kpi_type, kpi_subtype) => {
    const st = (kpi_subtype || '').toLowerCase();
    if (kpi_type === 'pain_baseline') {
      if (st === 'current') return 'SelfRatedPain_Current';
      if (st === 'best' || st === 'lowest') return 'SelfRatedPain_AtBest';
      if (st === 'worst' || st === 'highest') return 'SelfRatedPain_AtWorst';
      if (st === 'frequency') return 'SelfRatedPain_Frequency';
      if (st === 'rest') return 'SelfRatedPain_AtRest';
      if (st === 'activity') return 'SelfRatedPain_WithActivity';
      if (st === 'difficulty') return 'SelfRatedPain_Difficulty';
      return null;
    }
    if (kpi_type === 'function_goal') {
      if (st === 'difficulty') return 'SelfRatedFunction_Difficulty';
      if (st === 'main_goal' || st === 'function') return 'SelfRatedFunction_MainGoal';
      return null;
    }
    return null;
  }, []);


  const buildSnapshotFromSections = React.useCallback((sections) => {
    const snap = {};
    try {
      Object.entries(sections || {}).forEach(([theme, subgroups]) => {
        Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
          (items || []).forEach(i => {
            try {
              const id = idForItem(i);
              const matched = (i.selected_answer || i.selected_answer_meta || null);
              const meta = (matched && (matched.metaJournalPhrase || matched.meta_journalPhrase)) || i.metaJournalPhrase || i.meta_journalPhrase || '';
              const ans = getHumanAnswerText(matched, i.answer_text || i.answerText || '');
              const journal = (matched && (matched.journalPhrase || matched.journal_phrase)) || i.journalPhrase || i.journal_phrase || i.journal_text || '';
              const num = (i.numeric_value !== undefined && i.numeric_value !== null) ? String(i.numeric_value) : (i.numericValue !== undefined ? String(i.numericValue) : '');
              snap[id] = { metaJournalPhrase: meta || '', journalPhrase: String(journal || '').trim(), numerical_value: num || '', answer_text: ans || '' };
            } catch (e) { /* swallow per-item errors */ }
          });
        });
      });
    } catch (e) {}
    return snap;
  }, []);

  // Fetch journal versions from backend and merge with an initial autogenerated snapshot.
  // Initial assessment date fetched from QuestionHandler AnsweredAssessment.assessment_date
  const [initialAssessmentDate, setInitialAssessmentDate] = React.useState(null);

  const fetchJournalVersions = React.useCallback(async (sections, baseSectionsOverride = null) => {
    if (!selectedPatient) return [];
    try { if (versionsAbortRef.current) versionsAbortRef.current.abort(); } catch (e) {}
    const controller = new AbortController();
    versionsAbortRef.current = controller;
    const { signal } = controller;
    const preferredTs = initialAssessmentDate || (selectedPatient && (selectedPatient.assessment_date || selectedPatient.initial_assessment_date || selectedPatient.answered_assessment_date || selectedPatient.answeredAssessmentDate)) || new Date().toISOString();
    const snapshotSource = baseSectionsOverride || baseJournalSectionsRef.current || sections;
    const initialVersion = { id: 'initial-assessment', ts: preferredTs, base: buildSnapshotFromSections(snapshotSource), changes: [] };
    const candidates = [];
    if (selectedPatient.treatment_id) candidates.push(selectedPatient.treatment_id);
    if (selectedPatient.treatment && selectedPatient.treatment.id) candidates.push(selectedPatient.treatment.id);
    // Do not push user_id into /treatments/<id>/journal_versions directly; resolve via helper below
    // ensure unique
    const uniq = [...new Set(candidates)];

    let serverVersions = [];
    // First try direct treatment id candidates (only actual treatment ids)
    for (const id of uniq) {
      try {
        const url = `${apiBaseUrl}/treatment/treatments/${id}/journal_versions/`;
        const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
        const res = await fetch(url, { method: 'GET', headers, signal });
        if (!res.ok) continue;
        const data = await res.json();
        if (data && Array.isArray(data.versions) && data.versions.length) {
          serverVersions = data.versions.map(v => ({ id: v.id, version: v.version || 0, ts: v.created_at || v.created, base: {}, changes: (v.payload && (v.payload.edits || v.payload.changes)) || (v.payload || []), note: v.note || '' }));
          break;
        }
      } catch (e) {
        if (e && e.name === 'AbortError') { return []; }
        // try next id candidate
      }
    }

    // If no server versions found yet, try the helper endpoint used elsewhere to resolve treatment by user id
    if ((!serverVersions || serverVersions.length === 0) && selectedPatient && selectedPatient.user_id) {
      try {
        // try with and without diagnosis identifier
        const diagId = (diagnosis && (diagnosis.identifier || diagnosis.diagnosis || diagnosis.id)) ? (diagnosis.identifier || diagnosis.diagnosis || diagnosis.id) : '';
        const probeUrls = [
          `${apiBaseUrl}/treatment/treatment_by_user_id/${selectedPatient.user_id}/${diagId}`,
          `${apiBaseUrl}/treatment/treatment_by_user_id/${selectedPatient.user_id}/`
        ];
        let resolvedTreatmentId = null;
        for (const pu of probeUrls) {
          try {
            const pr = await fetch(pu, { method: 'GET', headers: { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' }, signal });
            if (!pr.ok) continue;
            const pd = await pr.json();
            // expect { treatment_id: '<uuid>' }
            if (pd && (pd.treatment_id || pd.treatmentId || pd.treatment)) { resolvedTreatmentId = pd.treatment_id || pd.treatmentId || pd.treatment; break; }
          } catch (e) { if (e && e.name === 'AbortError') { return []; } }
        }
        if (resolvedTreatmentId) {
          const url = `${apiBaseUrl}/treatment/treatments/${resolvedTreatmentId}/journal_versions/`;
          const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
          const res2 = await fetch(url, { method: 'GET', headers, signal });
          if (res2.ok) {
            const data2 = await res2.json();
            if (data2 && Array.isArray(data2.versions) && data2.versions.length) {
              serverVersions = data2.versions.map(v => ({ id: v.id, version: v.version || 0, ts: v.created_at || v.created, base: {}, changes: (v.payload && (v.payload.edits || v.payload.changes)) || (v.payload || []), note: v.note || '' }));
            }
          }
        }
      } catch (e) {
        if (e && e.name === 'AbortError') { return []; }
        // ignore fallback errors
      }
    }

    // sort server versions in ascending chronological/version order so index 1 == first edit
    if (serverVersions && serverVersions.length) {
      serverVersions.sort((a, b) => (Number(a.version || 0) - Number(b.version || 0)));
    }
    const combined = serverVersions && serverVersions.length ? [initialVersion].concat(serverVersions) : [initialVersion];
    if (signal.aborted) { return []; }
    journalVersionsRef.current = combined;
    setJournalVersions(combined);
    // Keep the default journal view on the canonical live payload fetched from
    // `/journal/<assessment>/`. Historical reconstruction is only used when the
    // user explicitly navigates versions; otherwise replaying the latest delta
    // can resurrect superseded KPI rows that are already absent from the live
    // journal.
    try { setCurrentVersionIndex(-1); } catch (e) {}
    if (versionsAbortRef.current === controller) { versionsAbortRef.current = null; }
    return combined;
  }, [selectedPatient, userToken, buildSnapshotFromSections, initialAssessmentDate]);

  // Journal template state
  const [journalSections, setJournalSections] = React.useState(null);
  const [hasStaffAuthoredConsentNote, setHasStaffAuthoredConsentNote] = React.useState(false);
  // When true, automatically populate empty editable textfields with
  // available `journalPhrase`/`metaJournalPhrase` values from sections.
  const [autoPrefillJournalFields, setAutoPrefillJournalFields] = React.useState(true);
  const [journalLoading, setJournalLoading] = React.useState(false);
  const [journalError, setJournalError] = React.useState(null);
  const [patientAdvices, setPatientAdvices] = React.useState([]);
  const [advicesLoading, setAdvicesLoading] = React.useState(false);
  const [advicesError, setAdvicesError] = React.useState(null);
  const [expandedAdvicePhase, setExpandedAdvicePhase] = React.useState(null);
  const [expandedInfoKey, setExpandedInfoKey] = React.useState(null);
  const [patientPhases, setPatientPhases] = React.useState([]);
  const [phasesLoading, setPhasesLoading] = React.useState(false);
  const [phasesError, setPhasesError] = React.useState(null);
  const [phaseExercises, setPhaseExercises] = React.useState({});
  const [expandedTreatmentPhase, setExpandedTreatmentPhase] = React.useState(null);
  const [headerView, setHeaderView] = React.useState(() => {
    try {
      const persistedHeaderView = localStorage.getItem(HEADER_VIEW_STORAGE_KEY);
      return HEADER_VIEW_OPTIONS.includes(persistedHeaderView) ? persistedHeaderView : 'Assessment';
    } catch (e) {
      return 'Assessment';
    }
  });

  // Editable journal fields state
  const [editingItems, setEditingItems] = React.useState({}); // { [itemId]: true }
  const [editableValues, setEditableValues] = React.useState({}); // { [itemId]: { journalPhrase, metaJournalPhrase, numerical_value } }
  const [preparedSavePayload, setPreparedSavePayload] = React.useState(null); // prepared payload ready to send to backend
  const [journalVersions, setJournalVersions] = React.useState([]); // { id, ts, base: {id: vals}, changes: [...] }
  const [currentVersionIndex, setCurrentVersionIndex] = React.useState(-1); // -1 = live/current
  const [originalEditableSnapshot, setOriginalEditableSnapshot] = React.useState(null);
  const baseJournalSectionsRef = React.useRef(null);
  const liveJournalSectionsRef = React.useRef(null);
  const pendingLocalEditsRef = React.useRef(false);

  // Generated-draft state: when user clicks "Generate a new journal" we create a client-side draft
  // Cancel will re-fetch canonical data from the backend instead of restoring a frontend backup.
  const [generatedDraftActive, setGeneratedDraftActive] = React.useState(false);
  const pendingJournalActionRef = React.useRef(null);
  const [unsavedJournalDialogOpen, setUnsavedJournalDialogOpen] = React.useState(false);
  const [unsavedJournalDialogLabel, setUnsavedJournalDialogLabel] = React.useState('');
  const [unsavedJournalDialogBusy, setUnsavedJournalDialogBusy] = React.useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = React.useState(false);
  const [kpiHistory, setKpiHistory] = React.useState({});
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMsg, setSnackbarMsg] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');

  // Track last treatment_id for advices/phases to force refetch on treatment change
  const lastAdvicesTreatmentRef = React.useRef(null);
  const lastPhasesTreatmentRef = React.useRef(null);

  // ref to keep latest journalVersions for the delayed initial check
  const journalVersionsRef = React.useRef(journalVersions);
  React.useEffect(() => { journalVersionsRef.current = journalVersions; }, [journalVersions]);
  // Abort controller for overlapping journal version fetches
  const versionsAbortRef = React.useRef(null);
  React.useEffect(() => {
    return () => {
      try { if (versionsAbortRef.current) versionsAbortRef.current.abort(); } catch (e) {}
    };
  }, []);
  React.useEffect(() => {
    try {
      localStorage.setItem(HEADER_VIEW_STORAGE_KEY, headerView);
    } catch (e) {}
  }, [headerView]);
  // track whether a user explicitly navigated versions (prevent auto-overwriting their choice)
  const userSelectedVersionRef = React.useRef(false);
  const cloneSections = React.useCallback((sections) => {
    try {
      return JSON.parse(JSON.stringify(sections || {}));
    } catch (e) {
      return {};
    }
  }, []);
  React.useEffect(() => {
    pendingLocalEditsRef.current = Object.keys(editingItems || {}).length > 0 || Object.keys(editableValues || {}).length > 0;
  }, [editingItems, editableValues]);

  // ✅ Phase 1: Use assessmentDate prop from parent if available; otherwise fetch
  React.useEffect(() => {
    if (props.assessmentDate) {
      // Use assessment date from parent (consolidated fetch)
      try {
        if (props.assessmentDate && props.assessmentDate.assessment_date) {
          setInitialAssessmentDate(props.assessmentDate.assessment_date);
        } else if (props.assessmentDate && (props.assessmentDate.date || props.assessmentDate.assessmentDate)) {
          setInitialAssessmentDate(props.assessmentDate.date || props.assessmentDate.assessmentDate);
        } else if (Array.isArray(props.assessmentDate) && props.assessmentDate.length && props.assessmentDate[0].assessment_date) {
          setInitialAssessmentDate(props.assessmentDate[0].assessment_date);
        }
      } catch (e) {
        // ignore
      }
      return;
    }

    // Fallback: fetch locally if prop not provided
    async function fetchAssessmentDate() {
      try {
        if (!selectedPatient || !selectedPatient.user_id) return;
        const base = `${apiBaseUrl}/treatment/answered_assessment_date/${selectedPatient.user_id}/`;
        const params = new URLSearchParams();
        try {
          if (selectedPatient.treatment_id) params.set('treatment_id', selectedPatient.treatment_id);
          if (selectedPatient.selected_diagnosis_id) params.set('diagnosis_id', selectedPatient.selected_diagnosis_id);
        } catch (e) {}
        const url = params.toString() ? `${base}?${params.toString()}` : base;
        const resp = await fetch(url, { headers: { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' } });
        if (!resp || resp.status !== 200) return;
        const json = await resp.json();
        if (json && json.assessment_date) setInitialAssessmentDate(json.assessment_date);
      } catch (e) {
        // ignore
      }
    }
    fetchAssessmentDate();
  }, [props.assessmentDate, selectedPatient?.user_id, userToken]);
  const initialAppliedRef = React.useRef(false);
  // One-time delayed check: after 1.5s on initial render, if server versions exist, apply the latest
  React.useEffect(() => {
    const t = setTimeout(() => {
      if (initialAppliedRef.current) return;
      const v = journalVersionsRef.current || [];
      if (v.length > 1) {
        try {
          applyVersion(-1);
          setCurrentVersionIndex(-1);
        } catch (e) {
          // ignore
        }
        initialAppliedRef.current = true;
      }
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  // Also apply latest when `journalVersions` populates (covers versions arriving after mount)
  const journalVersionsCount = (journalVersions && journalVersions.length) || 0;
  React.useEffect(() => {
    if (initialAppliedRef.current) return;
    if (journalVersions && journalVersions.length > 1) {
      try { applyVersion(-1); setCurrentVersionIndex(-1); } catch (e) {}
      initialAppliedRef.current = true;
    }
  }, [journalVersionsCount]);

  // Editable Anamnesis (first part of journal) versioning state
  const [anamnesisVersions, setAnamnesisVersions] = React.useState([]); // { id, text, author, ts }
  const [anamnesisIndex, setAnamnesisIndex] = React.useState(0);
  const [anamnesisEditing, setAnamnesisEditing] = React.useState(false);
  const [anamnesisEditValue, setAnamnesisEditValue] = React.useState('');
  const [anamnesisSaving, setAnamnesisSaving] = React.useState(false);

  // Helper: build a simple text summary for the 'Anamnesis' group from journalSections
  const buildAnamnesisText = React.useCallback((sections) => {
    if (!sections) return '';
    const keys = new Set([
      'keySymptom',
      'painAnamnesis',
      'painAnamnesis_cause_how_issue_occured',
      'painAnamnesis_duration',
      'painAnamnesis_doctor',
      'painAnamnesis_doctorDiagnosis',
      'secondary_multimorbid_doctorDiagnosis'
    ]);
    const out = [];
    try {
      Object.entries(sections).forEach(([theme, subgroups]) => {
        Object.entries(subgroups).forEach(([subgroup, items]) => {
          items.forEach(i => {
            const qid = (i.identifier || (i.question && i.question.identifier) || '').toString();
            const sg = i.subgroup || (i.question && i.question.subgroup) || '';
            const tags = (i.selected_answer && i.selected_answer.tags) || (i.selected_answer_meta && i.selected_answer_meta.tags) || (i.question && i.question.tags) || i.tags || [];
            const match = keys.has(qid) || keys.has(sg) || (Array.isArray(tags) && tags.some(t => keys.has(t)));
            if (match) {
              const ans = i.selected_answer || i.selected_answer_meta || {};
              const rawText = (ans && (ans.metaJournalPhrase || ans.journalPhrase || ans.answerText || ans.answer_text)) || i.metaJournalPhrase || i.journalPhrase || i.answer_text || i.answerText || (i.question && (i.question.metaJournalPhrase || i.question.journalPhrase)) || '';
              const text = sanitizeDisplay(rawText);
              if (text && text.toString().trim()) out.push(text.toString().trim());
            }
          });
        });
      });
    } catch (e) {
      // swallow
    }
    return out.join('\n');
  }, []);

  // Persist/load versions in localStorage keyed by patient and diagnosis
  const getStorageKey = React.useCallback(() => {
    const pid = selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : 'unknown';
    const did = diagnosis && diagnosis.identifier ? diagnosis.identifier : 'unknown';
    return `anamnesis_versions_${pid}_${did}`;
  }, [selectedPatient, diagnosis]);

  // Save current anamnesis as a new version
  const handleSaveAnamnesis = React.useCallback(async () => {
    if (anamnesisSaving) return;
    const text = (anamnesisEditValue || '').toString();
    if (!text.trim()) return;
    setAnamnesisSaving(true);
    try {
      const newV = { id: `v-${Date.now()}`, text, author: 'me', ts: new Date().toISOString() };
      // append after current index (like version history branching)
      const head = anamnesisVersions && anamnesisVersions.length ? anamnesisVersions.slice(0, anamnesisIndex + 1) : [];
      const next = [...head, newV];
      setAnamnesisVersions(next);
      setAnamnesisIndex(next.length - 1);
      setAnamnesisEditValue(newV.text);
      // persistence moved to backend; do not store anamnesis versions in localStorage
    } finally {
      setAnamnesisSaving(false);
      setAnamnesisEditing(false);
    }
  }, [anamnesisEditValue, anamnesisIndex, anamnesisVersions, anamnesisSaving, getStorageKey]);

  // Load stored versions on mount / when patient/diagnosis changes
  React.useEffect(() => {
    const key = getStorageKey();
    // Do not load anamnesis versions from localStorage; rely on backend/journalSections
    // if no stored versions, wait for journalSections to initialize
    if (journalSections) {
      const initial = buildAnamnesisText(journalSections) || '';
      const v = [{ id: `v-${Date.now()}`, text: initial, author: 'me', ts: new Date().toISOString() }];
      setAnamnesisVersions(v);
      setAnamnesisIndex(v.length - 1);
      setAnamnesisEditValue(initial);
      // Do not persist anamnesis versions to localStorage; backend is source of truth
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalSections, getStorageKey, buildAnamnesisText]);

  const fetchJournal = React.useCallback(async () => {
    if (!selectedPatient || !selectedPatient.user_id) return;
    if (!userToken || (typeof userToken === 'string' && userToken.trim() === '')) {
      // Skip fetching journal when not authenticated
      return;
    }
    setJournalLoading(true);
    setJournalError(null);
    try {
      const headers = {
        'Authorization': `Token ${userToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };
      
      // Build query params to scope journal by selected treatment/diagnosis
      const params = new URLSearchParams();
      try {
        if (selectedPatient.treatment_id) params.set('treatment_id', selectedPatient.treatment_id);
        if (selectedPatient.selected_diagnosis_id) params.set('diagnosis_id', selectedPatient.selected_diagnosis_id);
      } catch (e) {}
      const queryStr = params.toString() ? `?${params.toString()}` : '';

      // console.log('[SelectedPatientDiagnosis] fetchJournal', {
      //   userId: selectedPatient.user_id,
      //   treatmentId: selectedPatient.treatment_id,
      //   diagnosisId: selectedPatient.selected_diagnosis_id,
      //   queryStr
      // });
      
      const latestRes = await fetch(`${apiBaseUrl}/journal/latest/${selectedPatient.user_id}/${queryStr}`, {
        headers,
        method: 'GET',
      });
      let latestData = null;
      if (latestRes.ok) {
        latestData = await latestRes.json();
      } else {
        let errorText = '';
        try { errorText = await latestRes.text(); } catch (e) {}
        throw new Error(`API error: ${latestRes.status} - ${errorText}`);
      }
      if (!latestData || !latestData.assessment_id) throw new Error(latestData?.error || 'No assessment found');

      const journalRes = await fetch(`${apiBaseUrl}/journal/${latestData.assessment_id}/`, {
        headers,
        method: 'GET',
      });
      let journalData = null;
      if (journalRes.ok) {
        journalData = await journalRes.json();
      } else {
        let errorText = '';
        try { errorText = await journalRes.text(); } catch (e) {}
        throw new Error(`API error: ${journalRes.status} - ${errorText}`);
      }
      if (!journalData || !journalData.sections) throw new Error(journalData?.error || 'No journal data');
      const baseJournalRes = await fetch(`${apiBaseUrl}/journal/${latestData.assessment_id}/?include_versions=0`, {
        headers,
        method: 'GET',
      });
      let baseJournalData = null;
      if (baseJournalRes.ok) {
        baseJournalData = await baseJournalRes.json();
      } else {
        let errorText = '';
        try { errorText = await baseJournalRes.text(); } catch (e) {}
        throw new Error(`API error: ${baseJournalRes.status} - ${errorText}`);
      }
      if (!baseJournalData || !baseJournalData.sections) throw new Error(baseJournalData?.error || 'No base journal data');

      const fetchedLiveSections = journalData.sections || {};
      const fetchedBaseSections = baseJournalData.sections || {};
      baseJournalSectionsRef.current = cloneSections(fetchedBaseSections);

      setJournalSections((prev) => {
        if (!pendingLocalEditsRef.current || !prev || Object.keys(prev).length === 0) {
          liveJournalSectionsRef.current = cloneSections(fetchedLiveSections);
          return fetchedLiveSections;
        }

        const merged = cloneSections(fetchedLiveSections);
        Object.entries(prev).forEach(([theme, subgroups]) => {
          Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
            (items || []).forEach((item) => {
              const isManual = item.manual === true || (item.id && String(item.id).startsWith('manual-'));
              if (!isManual) return;
              const itemId = idForItem(item);
              let exists = false;
              if (merged[theme] && merged[theme][subgroup]) {
                exists = merged[theme][subgroup].some((fetchedItem) => idForItem(fetchedItem) === itemId);
              }
              if (!exists) {
                if (!merged[theme]) merged[theme] = {};
                if (!Array.isArray(merged[theme][subgroup])) merged[theme][subgroup] = [];
                merged[theme][subgroup].push(item);
              }
            });
          });
        });
        liveJournalSectionsRef.current = cloneSections(merged);
        return merged;
      });
      try { await fetchJournalVersions(journalData.sections, fetchedBaseSections); } catch (e) { /* ignore */ }
    } catch (err) {
      setJournalError(err.message);
    } finally {
      setJournalLoading(false);
    }
    // ✅ FIXED: Removed fetchJournalVersions from deps (was causing cascade on initialAssessmentDate change)
  }, [selectedPatient?.user_id, selectedPatient?.treatment_id, selectedPatient?.selected_diagnosis_id, userToken]);

  // ✅ FIXED: Separate the delayed fetch into its own effect to avoid cascade
  // Trigger on component mount OR when user_id, treatment_id, or selected_diagnosis_id changes
  React.useEffect(() => {
    let mounted = true;
    const delayedFetch = async () => {
      await new Promise(r => setTimeout(r, 1000));
      if (mounted) await fetchJournal();
    };
    delayedFetch();
    return () => { mounted = false; };
    // Re-trigger when treatment/diagnosis selection changes
  }, [selectedPatient?.user_id, selectedPatient?.treatment_id, selectedPatient?.selected_diagnosis_id]);

  // Helper function for manual delayed fetches (used in multiple places)
  const fetchJournalDelayed = React.useCallback(async (delay = 1000) => {
    await new Promise(r => setTimeout(r, delay));
    return fetchJournal();
  }, [fetchJournal]);

  // Refresh journal when another view posts a new journal version (e.g. Development quick-add KPI).
  React.useEffect(() => {
    const handleJournalUpdated = (detail) => {
      try {
        const evt = detail && detail.detail ? detail.detail : detail;
        const evtUser = evt && evt.userId ? String(evt.userId) : null;
        const evtTreatment = evt && evt.treatmentId ? String(evt.treatmentId) : null;
        const currentUser = selectedPatient && selectedPatient.user_id ? String(selectedPatient.user_id) : null;
        const currentTreatment = selectedPatient && selectedPatient.treatment_id ? String(selectedPatient.treatment_id) : null;
        if (!currentUser) return;
        if (evtUser && evtUser !== currentUser) return;
        if (evtTreatment && currentTreatment && evtTreatment !== currentTreatment) return;
        fetchJournalDelayed(250);
      } catch (e) {}
    };

    const handleStorage = (event) => {
      if (!event || event.key !== 'vp_latest_journal_update' || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        handleJournalUpdated(parsed);
      } catch (e) {}
    };

    window.addEventListener('vp-journal-updated', handleJournalUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('vp-journal-updated', handleJournalUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchJournalDelayed, selectedPatient]);

  // ✅ Phase 1: Use patientAdvices prop from parent if available; otherwise fetch
  React.useEffect(() => {
    const currentTreatmentId = selectedPatient?.treatment_id || null;
    const treatmentChanged = currentTreatmentId !== lastAdvicesTreatmentRef.current;
    
    // If treatment changed, always refetch (don't use stale props)
    if (props.patientAdvices && !treatmentChanged) {
      // Use advices from parent (consolidated fetch) only if treatment hasn't changed
      const data = props.patientAdvices;
      if (Array.isArray(data)) {
        setPatientAdvices(data);
      } else if (data && Array.isArray(data.all_advices)) {
        setPatientAdvices(data.all_advices);
      } else if (data && Array.isArray(data.all_advices_list)) {
        setPatientAdvices(data.all_advices_list);
      } else {
        setPatientAdvices([]);
      }
      setAdvicesLoading(false);
      return;
    }

    // Fallback: fetch locally if prop not provided OR treatment changed
    async function fetchAdvices() {
      if (!selectedPatient || !selectedPatient.user_id) return;
      setAdvicesLoading(true);
      setAdvicesError(null);
      try {
        const headers = {
          'Authorization': `Token ${userToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        };
        // Include treatment_id in query params for treatment-scoped fetching
        const params = new URLSearchParams();
        if (selectedPatient.treatment_id) {
          params.append('treatment_id', selectedPatient.treatment_id);
        }
        const queryStr = params.toString() ? `?${params.toString()}` : '';
        const url = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/${queryStr}`;
        const res = await fetch(url, {
          method: 'GET',
          headers
        });
        if (!res.ok) {
          let errText = '';
          try { errText = await res.text(); } catch (e) {}
          throw new Error(`API error: ${res.status} - ${errText}`);
        }
        const data = await res.json();
        // API may return { all_advices: [ ... ] } or an array directly
        if (Array.isArray(data)) {
          setPatientAdvices(data);
        } else if (data && Array.isArray(data.all_advices)) {
          setPatientAdvices(data.all_advices);
        } else if (data && Array.isArray(data.all_advices_list)) {
          setPatientAdvices(data.all_advices_list);
        } else {
          setPatientAdvices([]);
        }
        // Update ref after successful fetch
        lastAdvicesTreatmentRef.current = currentTreatmentId;
      } catch (err) {
        setAdvicesError(err.message);
      } finally {
        setAdvicesLoading(false);
      }
    }
    fetchAdvices();
  }, [props.patientAdvices, selectedPatient?.user_id, selectedPatient?.treatment_id, userToken])

  // Fetch patient phases for treatment plan (used to list exercises per phase)
  React.useEffect(() => {
    const currentTreatmentId = selectedPatient?.treatment_id || null;
    const treatmentChanged = currentTreatmentId !== lastPhasesTreatmentRef.current;
    
    // If treatment changed, always refetch (don't use stale props)
    if (props.patientPhases && !treatmentChanged) {
      const data = props.patientPhases;
      setPatientPhases(Array.isArray(data) ? data : []);
      setPhasesLoading(false);
      return;
    }

    // Fallback: fetch locally if prop not provided OR treatment changed
    async function fetchPhases() {
      if (!selectedPatient || !selectedPatient.user_id) return;
      setPhasesLoading(true);
      setPhasesError(null);
      try {
        const headers = {
          'Authorization': `Token ${userToken}`,
          'Accept': 'application/json'
        };
        // Include treatment_id in query params for treatment-scoped fetching
        const params = new URLSearchParams();
        if (selectedPatient.treatment_id) {
          params.append('treatment_id', selectedPatient.treatment_id);
        }
        const queryStr = params.toString() ? `?${params.toString()}` : '';
        const url = `${apiBaseUrl}/treatment/phases/patient/${selectedPatient.user_id}/${queryStr}`;
        const res = await fetch(url, { headers, method: 'GET' });
        if (!res.ok) {
          let txt = '';
          try { txt = await res.text(); } catch (e) {}
          throw new Error(`API error: ${res.status} - ${txt}`);
        }
        const data = await res.json();
        setPatientPhases(Array.isArray(data.phases) ? data.phases : []);
        // Update ref after successful fetch
        lastPhasesTreatmentRef.current = currentTreatmentId;
      } catch (err) {
        setPhasesError(err.message);
      } finally {
        setPhasesLoading(false);
      }
    }
    fetchPhases();
  }, [props.patientPhases, selectedPatient?.user_id, selectedPatient?.treatment_id, userToken]);

  // Helper: fetch exercises for a given phase on demand
  const fetchExercisesForPhase = React.useCallback(async (phaseId) => {
    if (!selectedPatient || !selectedPatient.user_id || !phaseId) return;
    if (phaseExercises[phaseId]) return; // already loaded
    const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
    try {
      // Include treatment_id in query params for treatment-scoped fetching
      const params = new URLSearchParams();
      if (selectedPatient.treatment_id) {
        params.append('treatment_id', selectedPatient.treatment_id);
      }
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const url = `${apiBaseUrl}/treatment/exercises/phase/${phaseId}/${selectedPatient.user_id}/${queryStr}`;
      const res = await fetch(url, { headers, method: 'GET' });
      if (!res.ok) {
        let txt = '';
        try { txt = await res.text(); } catch (e) {}
        throw new Error(`API error: ${res.status} - ${txt}`);
      }
      const data = await res.json();
      // controller returns { exercises: [...] } or an array
      const exs = Array.isArray(data) ? data : (data && Array.isArray(data.exercises) ? data.exercises : []);
      setPhaseExercises(prev => ({ ...prev, [phaseId]: exs }));
    } catch (err) {
      setPhaseExercises(prev => ({ ...prev, [phaseId]: { __error: err.message } }));
    }
  }, [patientPhases, phaseExercises, selectedPatient, userToken]);

  const idForItem = (i) => {
    return i.question_id || i.id || i.identifier || JSON.stringify(i);
  };

  // POST prognosis change for a diagnosis
  const postPrognosisChange = async (diagId, prognosisObj, reason) => {
    try {
      const url = `${apiBaseUrl}/treatment/diagnoses/${diagId}/prognosis/`;
      const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json', 'Content-Type': 'application/json' };
      const bodyObj = { prognosis: prognosisObj, reason: reason || '' };
      if (selectedPatient && selectedPatient.user_id) bodyObj.user_id = selectedPatient.user_id;
      const body = JSON.stringify(bodyObj);
      const res = await fetch(url, { method: 'POST', headers, body });
      if (!res.ok) {
        let txt = '';
        try { txt = await res.text(); } catch (e) {}
        console.error('postPrognosisChange failed', res.status, txt);
        return null;
      }
      const data = await res.json();
      return data;
    } catch (e) {
      console.error('postPrognosisChange error', e);
      return null;
    }
  };

  const looksLikeAnswerId = (v) => {
    if (v === undefined || v === null) return false;
    const s = String(v).trim();
    if (s === '') return false;
    if (/^(?:answer|ans)[_-]?\d+$/i.test(s)) return true;
    return false;
  };

  const removeAnswerIdTokens = (s) => {
    if (s === undefined || s === null) return '';
    try {
      let out = String(s);
      // remove explicit answer id tokens like 'answer_123' or 'ans-45' but DO NOT strip plain numbers
      out = out.replace(/\b(?:answer[_-]?\d+|ans[_-]?\d+)\b/gi, '');
      // remove standalone single-letter lines, including quoted/punctuated variants like a" / "f
      out = out.replace(/(^|\r?\n)\s*["'`“”‘’(\[]?\s*[A-Za-z]\s*["'`“”‘’\)\].,:;!?-]*\s*(?=\r?\n|$)/g, '$1');
      // collapse multiple blank lines and normalize spacing
      out = out.replace(/\r?\n\s*\r?\n+/g, '\n');
      out = out.replace(/[ \t]{2,}/g, ' ').trim();
      return out;
    } catch (e) { return String(s); }
  };

  const sanitizeDisplay = (v) => {
    if (v === undefined || v === null) return '';
    const s = String(v).trim();
    if (s === '') return '';
    if (looksLikeAnswerId(s)) return '';
    return removeAnswerIdTokens(s);
  };

  const getHumanAnswerText = (matched, fallbackValue = '') => {
    const tokenValues = [
      matched && matched.value,
      matched && matched.id,
      matched && matched.answerId,
    ]
      .filter(v => v !== undefined && v !== null)
      .map(v => String(v).trim().toLowerCase())
      .filter(Boolean);

    const candidates = [
      matched && matched.answerText,
      matched && matched.text,
      matched && matched.label,
      fallbackValue,
    ];

    for (const candidate of candidates) {
      const cleaned = sanitizeDisplay(candidate);
      if (!cleaned) continue;
      const normalized = cleaned.toLowerCase();
      if (/^[a-z]$/i.test(cleaned)) continue;
      if (tokenValues.includes(normalized)) continue;
      return cleaned;
    }

    return '';
  };

  // Extract trailing integer token from a string (e.g. "pain 5" -> { text: "pain", num: "5" })
  const extractTrailingNumber = (s) => {
    try {
      const str = (s === undefined || s === null) ? '' : String(s).trim();
      if (!str) return { text: '', num: '' };
      const m = str.match(/^(.*?)[\s·–—:\-]*([0-9]+)\s*$/);
      if (m) return { text: (m[1] || '').trim(), num: m[2] || '' };
      return { text: str, num: '' };
    } catch (e) { return { text: String(s || ''), num: '' }; }
  };

  // Safely parse numeric input, return null for empty/invalid values (avoid NaN)
  const parseNumericSafe = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const parseBooleanSafe = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    const s = String(v).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  };

  const normalizePainSubtypeForSave = React.useCallback((rawSubtype = '', subgroupHint = '', mappedKey = '') => {
    const blob = [rawSubtype, subgroupHint, mappedKey]
      .filter(Boolean)
      .map(v => String(v).trim().toLowerCase())
      .join(' ');
    if (!blob) return '';
    if (blob.includes('custom')) return 'custom';
    if (blob.includes('selfratedfunction') || blob.includes('main_goal') || blob.includes('maingoal')) return 'custom';
    if (blob.includes('frequency')) return 'frequency';
    if (blob.includes('atbest') || blob.includes('painatbest') || /\bbest\b/.test(blob)) return 'best';
    if (blob.includes('atworst') || blob.includes('painatworst') || /\bworst\b/.test(blob)) return 'worst';
    if (blob.includes('atrest') || blob.includes('painatrest') || /\brest\b/.test(blob)) return 'rest';
    if (blob.includes('withactivity') || blob.includes('painwithactivity') || /\bactivity\b/.test(blob)) return 'activity';
    if (blob.includes('difficulty')) return 'difficulty';
    if (blob.includes('current')) return 'current';
    return String(rawSubtype || '').trim();
  }, []);

  const normalizePainSubtypeForSection = React.useCallback((rawSubtype = '', subgroupHint = '', mappedKey = '') => {
    const canonical = normalizePainSubtypeForSave(rawSubtype, subgroupHint, mappedKey);
    const sectionMap = {
      best: 'painAtBest',
      worst: 'painAtWorst',
      current: 'painCurrent',
      frequency: 'frequency',
      rest: 'painAtRest',
      activity: 'painWithActivity',
      difficulty: 'difficulty',
      custom: 'custom',
    };
    return sectionMap[canonical] || '';
  }, [normalizePainSubtypeForSave]);

  const normalizeFunctionGoalSubtypeForSave = React.useCallback((rawSubtype = '') => {
    const value = String(rawSubtype || '').trim().toLowerCase();
    if (!value) return '';
    if (value.includes('maingoal') || value.includes('main_goal')) return 'main_goal';
    if (value.includes('difficulty')) return 'difficulty';
    if (value.includes('custom')) return 'custom';
    return value;
  }, []);

  const inferKpiTypeFromItem = (item, groupName) => {
    const explicit = (item && item.kpi_type) ? String(item.kpi_type).trim() : '';
    if (explicit) return explicit;
    if (groupName === 'Pain baseline levels:') return 'pain_baseline';
    if (groupName === 'Patient goal for treatment:') return 'function_goal';
    // Legacy custom baseline items (created via the in-page builder) live
    // under theme=painAnamnesis_custom but historically saved without an
    // explicit kpi_type. Without inferring it, deletion / pause flags get
    // dropped during save — causing removed custom KPIs to silently
    // resurrect on the next journal version.
    const dispGroup = (item && (item.display_group || item.displayGroup)) || '';
    if (dispGroup === 'Pain baseline levels:') return 'pain_baseline';
    if (dispGroup === 'Patient goal for treatment:') return 'function_goal';
    const candidates = [item && item.mapped_key, item && item.identifier, item && item.subgroup, item && item.theme]
      .filter(Boolean)
      .map(v => String(v).toLowerCase());
    if (candidates.some(v => v.includes('painanamnesis_selfratedfunction_maingoal') || v === 'function_goal')) return 'function_goal';
    if (candidates.some(v => v === 'painanamnesis_custom' || v.startsWith('painanamnesis_custom'))) return 'pain_baseline';
    return '';
  };

  const seedKpiEditableEntry = (item, groupName, current = {}) => {
    const id = idForItem(item);
    const kpiType = current.kpi_type || inferKpiTypeFromItem(item, groupName);
    const mappedKey = current.mapped_key || item.mapped_key || item._mappedKey || item.identifier || '';
    const numericalRaw = current.numerical_value !== undefined
      ? current.numerical_value
      : (item.numeric_value !== undefined ? item.numeric_value : item.numerical_value);
    return {
      id,
      theme: current.theme || item.theme || '',
      subgroup: current.subgroup || item.subgroup || '',
      mapped_key: mappedKey,
      display_group: current.display_group || item.display_group || groupName || '',
      metaJournalPhrase: current.metaJournalPhrase !== undefined ? current.metaJournalPhrase : (item.metaJournalPhrase || item.meta_journalPhrase || ''),
      journalPhrase: current.journalPhrase !== undefined ? current.journalPhrase : (item.journalPhrase || item.journal_text || item.journal_phrase || ''),
      answer_text: current.answer_text !== undefined ? current.answer_text : (item.answer_text || item.answerText || ''),
      numerical_value: parseNumericSafe(numericalRaw),
      kpi_type: kpiType,
      kpi_subtype: kpiType === 'pain_baseline'
        ? normalizePainSubtypeForSave(current.kpi_subtype || item.kpi_subtype || item.subgroup || '', current.subgroup || item.subgroup || '', mappedKey)
        : (kpiType === 'function_goal'
          ? (normalizeFunctionGoalSubtypeForSave(current.kpi_subtype || item.kpi_subtype || item.subgroup || '') || 'main_goal')
          : (current.kpi_subtype || item.kpi_subtype || item.subgroup || '')),
      is_deleted: current.is_deleted !== undefined ? parseBooleanSafe(current.is_deleted) : parseBooleanSafe(item && item.is_deleted),
      paused_from_followup: current.paused_from_followup !== undefined ? parseBooleanSafe(current.paused_from_followup) : parseBooleanSafe(item && item.paused_from_followup),
    };
  };

  const getKpiItemState = (item, groupName) => {
    const id = idForItem(item);
    const local = (editableValues && editableValues[id]) ? editableValues[id] : {};
    const kpiType = (local && local.kpi_type) ? String(local.kpi_type).trim() : inferKpiTypeFromItem(item, groupName);
    return {
      isKpi: !!kpiType,
      kpiType,
      isDeleted: local && local.is_deleted !== undefined ? parseBooleanSafe(local.is_deleted) : parseBooleanSafe(item && item.is_deleted),
      isPaused: local && local.paused_from_followup !== undefined ? parseBooleanSafe(local.paused_from_followup) : parseBooleanSafe(item && item.paused_from_followup),
    };
  };

  const markExistingKpiDeleted = (item, groupName) => {
    const id = idForItem(item);
    setEditingItems(prev => ({ ...(prev || {}), [id]: true }));
    setEditableValues(prev => {
      const current = prev && prev[id] ? prev[id] : {};
      const seeded = seedKpiEditableEntry(item, groupName, current);
      return {
        ...(prev || {}),
        [id]: {
          ...seeded,
          is_deleted: true,
        }
      };
    });
  };

  const toggleExistingKpiPaused = (item, groupName) => {
    const id = idForItem(item);
    setEditingItems(prev => ({ ...(prev || {}), [id]: true }));
    setEditableValues(prev => {
      const current = prev && prev[id] ? prev[id] : {};
      const seeded = seedKpiEditableEntry(item, groupName, current);
      const nextPaused = !parseBooleanSafe(seeded.paused_from_followup);
      return {
        ...(prev || {}),
        [id]: {
          ...seeded,
          paused_from_followup: nextPaused,
          is_deleted: false,
        }
      };
    });
  };

  // Remove any of the given prefixes from the start of text (case-insensitive), cleaning separators
  const stripLeadingPrefixes = (text, prefixes) => {
    try {
      let out = String(text || '').trim();
      if (!out) return out;
      // sort prefixes by length descending to remove longest first
      const ps = (prefixes || []).map(p => (p || '').toString().trim()).filter(p => p).sort((a, b) => b.length - a.length);
      for (const p of ps) {
        const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp('^' + esc + '[\\s\-:–—]*', 'i');
        if (re.test(out)) {
          out = out.replace(re, '').trim();
        }
      }
      return out;
    } catch (e) { return String(text || '').trim(); }
  };

  const buildSectionsForVersion = React.useCallback((index) => {
    const liveSections = liveJournalSectionsRef.current || {};
    const baseSections = cloneSections(baseJournalSectionsRef.current || liveSections);

    if (index === null || index === undefined) return baseSections;
    if (index < 0) return cloneSections(liveSections);

    const versions = journalVersionsRef.current || [];
    const ver = versions[index];
    if (!ver) return baseSections;

    const applyEditToItem = (item, change) => {
      if (change.metaJournalPhrase !== undefined) {
        item.metaJournalPhrase = change.metaJournalPhrase || '';
      }
      if (change.journalPhrase !== undefined) {
        item.journalPhrase = change.journalPhrase || '';
        item.journal_text = change.journalPhrase || '';
      }
      if (change.answer_text !== undefined) {
        item.answer_text = change.answer_text || '';
        item.answerText = change.answer_text || '';
      }

      const rawNumeric = change.numerical_value !== undefined ? change.numerical_value : change.numeric_value;
      if (rawNumeric !== undefined) {
        const parsedNumeric = parseNumericSafe(rawNumeric);
        item.numeric_value = parsedNumeric;
        item.numericValue = parsedNumeric;
      }
      if (change.is_deleted !== undefined) {
        item.is_deleted = parseBooleanSafe(change.is_deleted);
      }
      if (change.paused_from_followup !== undefined) {
        item.paused_from_followup = parseBooleanSafe(change.paused_from_followup);
      }

      const answerMeta = item.selected_answer_meta ? { ...item.selected_answer_meta } : {};
      if (change.metaJournalPhrase !== undefined) answerMeta.metaJournalPhrase = change.metaJournalPhrase || '';
      if (change.journalPhrase !== undefined) answerMeta.journalPhrase = change.journalPhrase || '';
      if (change.answer_text !== undefined) {
        answerMeta.answerText = change.answer_text || '';
        answerMeta.text = change.answer_text || '';
      }
      if (rawNumeric !== undefined) {
        const parsedNumeric = parseNumericSafe(rawNumeric);
        answerMeta.numeric_value = parsedNumeric;
        answerMeta.numericValue = parsedNumeric;
      }
      item.selected_answer_meta = Object.keys(answerMeta).length ? answerMeta : item.selected_answer_meta;
      item.selected_answer = item.selected_answer_meta ? { ...item.selected_answer_meta } : item.selected_answer;
    };

    const applyChangesToSections = (changes) => {
      (Array.isArray(changes) ? changes : []).forEach((change) => {
        const changeId = change && (change.id || change.question_id);
        if (!changeId) return;

        let found = false;
        Object.values(baseSections).forEach((subgroups) => {
          Object.values(subgroups || {}).forEach((items) => {
            (items || []).forEach((item) => {
              if (found) return;
              if (idForItem(item) === changeId) {
                applyEditToItem(item, change);
                found = true;
              }
            });
          });
        });
        if (found) return;

        const journalPhrase = change.journalPhrase || '';
        const metaJournalPhrase = change.metaJournalPhrase || '';
        const answerText = change.answer_text || '';
        const rawNumeric = change.numerical_value !== undefined ? change.numerical_value : change.numeric_value;
        const parsedNumeric = parseNumericSafe(rawNumeric);
        // Don't skip items being deleted - they need to be in sections for render to hide them with isDeleted check
        const isBeingDeleted = parseBooleanSafe(change.is_deleted);
        if (!journalPhrase.trim() && !metaJournalPhrase.trim() && !answerText.trim() && parsedNumeric === null && !isBeingDeleted) return;

        const isManualChange = String(changeId).startsWith('manual-');
        const rawSubtype = String(change.kpi_subtype || '').trim().toLowerCase();
        const normalizedSubtype = normalizePainSubtypeForSection(rawSubtype, change.subgroup || '', change.mapped_key || '');
        const mappedKeyRaw = String(change.mapped_key || '').trim();
        const mappedSubtype = mappedKeyRaw.startsWith('painAnamnesis_') ? mappedKeyRaw.replace('painAnamnesis_', '') : '';
        const normalizedMappedSubtype = normalizePainSubtypeForSection(mappedSubtype, change.subgroup || '', mappedKeyRaw);
        const inferredBaselineSubtype = normalizedSubtype || normalizedMappedSubtype || 'painCurrent';

        let theme = change.theme || '';
        let subgroup = change.subgroup || '';
        if ((!theme || theme === 'ungrouped') && change.kpi_type === 'function_goal') {
          theme = isManualChange ? 'painAnamnesis_SelfRatedFunction_MainGoal_manual' : 'painAnamnesis_SelfRatedFunction_MainGoal';
        }
        if ((!subgroup || subgroup === 'other') && change.kpi_type === 'function_goal') {
          subgroup = isManualChange ? 'painAnamnesis_SelfRatedFunction_MainGoal_manual' : 'painAnamnesis_SelfRatedFunction_MainGoal';
        }
        if ((!theme || theme === 'ungrouped') && (change.kpi_type === 'pain_baseline' || normalizedSubtype || normalizedMappedSubtype)) {
          const baseTheme = `painAnamnesis_${inferredBaselineSubtype}`;
          theme = isManualChange ? `${baseTheme}_manual` : baseTheme;
        }
        if ((!subgroup || subgroup === 'other') && (change.kpi_type === 'pain_baseline' || normalizedSubtype || normalizedMappedSubtype)) {
          subgroup = inferredBaselineSubtype;
        }

        theme = theme || 'ungrouped';
        subgroup = subgroup || 'other';
        if (!baseSections[theme]) baseSections[theme] = {};
        if (!Array.isArray(baseSections[theme][subgroup])) baseSections[theme][subgroup] = [];

        baseSections[theme][subgroup].push({
          id: changeId,
          question_id: changeId,
          manual: String(changeId).startsWith('manual-'),
          theme,
          subgroup,
          mapped_key: change.mapped_key || '',
          journalPhrase,
          metaJournalPhrase,
          journal_text: journalPhrase || metaJournalPhrase || '',
          tags: [],
          identifier: change.identifier || null,
          answer_text: answerText,
          numeric_value: parsedNumeric,
          answer_id: null,
          is_deleted: isBeingDeleted,
          selected_answer_meta: {
            metaJournalPhrase,
            journalPhrase,
            answerText,
            text: answerText,
            numeric_value: parsedNumeric,
            numericValue: parsedNumeric,
          },
          priority: 0,
        });
      });
    };

    for (let versionIndex = 1; versionIndex <= index; versionIndex += 1) {
      const nextVersion = versions[versionIndex];
      if (!nextVersion) continue;
      applyChangesToSections(nextVersion.changes);
    }

    return baseSections;
  }, [cloneSections, normalizePainSubtypeForSection]);

  // Global toggle to enable editing of all journal fields
  const [globalEditMode, setGlobalEditMode] = React.useState(false);
  const [prognosisEdits, setPrognosisEdits] = React.useState(null);
  const [appliedPrognosis, setAppliedPrognosis] = React.useState(null);

  const enableGlobalEdit = () => {
    if (!journalSections) {
      setGlobalEditMode(true);
      return;
    }
    const vals = {};
    const originals = {};
    try {
      Object.entries(journalSections).forEach(([theme, subgroups]) => {
        Object.entries(subgroups).forEach(([subgroup, items]) => {
          items.forEach(i => {
            const id = idForItem(i);
            const matched = (i.selected_answer || i.selected_answer_meta || null);
            const meta = (matched && (matched.metaJournalPhrase || matched.meta_journalPhrase)) || i.metaJournalPhrase || i.meta_journalPhrase || '';
            const ans = getHumanAnswerText(matched, i.answer_text || i.answerText || '');
            const journal = (matched && (matched.journalPhrase || matched.journal_phrase)) || i.journalPhrase || i.journal_phrase || i.journal_text || '';
            const num = (i.numeric_value !== undefined && i.numeric_value !== null) ? String(i.numeric_value) : (i.numericValue !== undefined ? String(i.numericValue) : '');
            // Keep answer_text separate and use journal/meta separately to avoid duplicating answer_text in the journalPhrase
            try {
              const finalJournal = (journal || '').toString().trim();
              const finalNum = (num || '');
              vals[id] = { metaJournalPhrase: meta, journalPhrase: finalJournal, numerical_value: finalNum, answer_text: ans };
              originals[id] = { metaJournalPhrase: meta, journalPhrase: finalJournal, numerical_value: finalNum, answer_text: ans };
            } catch (e) {
              vals[id] = { metaJournalPhrase: meta, journalPhrase: (journal || '').toString().trim(), numerical_value: (num || ''), answer_text: ans };
              originals[id] = { metaJournalPhrase: meta, journalPhrase: (journal || '').toString().trim(), numerical_value: (num || ''), answer_text: ans };
            }
          });
        });
      });
    } catch (e) {}
    setEditableValues(vals);
    setOriginalEditableSnapshot(originals);
    setEditingItems(Object.keys(vals).reduce((acc, k) => { acc[k] = true; return acc; }, {}));
    setGroupDrafts({});
    setGroupExtraFields({});
    setGlobalEditMode(true);
  };

  // Grouping map (display group -> journal section keys)
  const GROUPING_MAP = {
    'Evaluation and indication for physiotherapy:': ['diagnosis'],
    'Treatmentplan': ['treatmentPlan'],
    'Initial Advice': ['riskFactors', 'riskFactors_advice'],
    'Information to the patient': [],
    'Patient consent': ['patientConsent']
  };

  const makeManualItem = (labelHint = '', theme = '', subgroup = 'default', mapped_key = '') => ({
    id: `manual-${Date.now()}-${Math.floor(Math.random()*10000)}`,
    question_id: null,
    manual: true,
    metaJournalPhrase: labelHint || '',
    journalPhrase: '',
    numeric_value: null,
    answer_text: '',
    theme: theme || '',
    subgroup: subgroup || 'default',
    mapped_key: mapped_key || ''
  });

  // State for inline pain baseline creation
  const [painBaselineInputs, setPainBaselineInputs] = React.useState({});
  const [functionGoalInputs, setFunctionGoalInputs] = React.useState({});
  // State to track arrays of creator IDs for each section (allows multiple creators)
  const [painBaselineCreators, setPainBaselineCreators] = React.useState({});
  const [functionGoalCreators, setFunctionGoalCreators] = React.useState({});

  const addManualItem = (themeKey, subgroupKey = 'default', labelHint = '') => {
    try {
      // create a stable manual id and insert exactly one item into journalSections
      const manualId = `manual-${Date.now()}-${Math.floor(Math.random()*10000)}`;
      // derive canonical `theme` and `subgroup` by splitting `themeKey` when it contains an underscore
      let theme = themeKey;
      let subgroup = subgroupKey;
      if (typeof themeKey === 'string' && themeKey.includes('_') && (subgroupKey === 'default' || !subgroupKey)) {
        const idx = themeKey.indexOf('_');
        theme = themeKey.substring(0, idx) || themeKey;
        subgroup = themeKey.substring(idx + 1) || 'default';
      }
      // Store with canonical theme; _manual suffix added only in payload
      setJournalSections(prev => {
        const clone = prev ? JSON.parse(JSON.stringify(prev)) : {};
        if (!clone[theme]) clone[theme] = {};
        if (!Array.isArray(clone[theme][subgroup])) clone[theme][subgroup] = [];
        const item = makeManualItem(labelHint, theme, subgroup, themeKey);
        item.id = manualId;
        item.manual = true;  // flag as manually-created
        clone[theme][subgroup].push(item);
        return clone;
      });
      setEditingItems(prev => ({ ...(prev || {}), [manualId]: true }));
      setEditableValues(prev => ({ ...(prev || {}), [manualId]: { metaJournalPhrase: labelHint || '', journalPhrase: '', numerical_value: '', theme: theme, subgroup: subgroup } }));
      setGlobalEditMode(true);
      return manualId;
    } catch (e) { /* ignore */ return null; }
  };

  // Helper: find theme/subgroup keys for a given item id from current `journalSections`
  const findThemeSubgroupForId = (targetId) => {
    try {
      if (!journalSections) return null;
      for (const [theme, subgroups] of Object.entries(journalSections || {})) {
        for (const [subgroup, items] of Object.entries(subgroups || {})) {
          for (const item of (items || [])) {
            const iid = idForItem(item);
            if (iid === targetId) return { theme, subgroup };
          }
        }
      }
    } catch (e) {}
    return null;
  };

  React.useEffect(() => {
    let isActive = true;

    const fetchConsentAuthorship = async () => {
      const patientId = selectedPatient && selectedPatient.user_id;
      const diagnosisIdentifier = (diagnosis && (diagnosis.identifier || diagnosis.diagnosis_identifier || diagnosis.diagnosis))
        || (selectedPatient && selectedPatient.selected_diagnosis_identifier)
        || null;
      const treatmentId = (selectedPatient && (selectedPatient.treatment_id || (selectedPatient.treatment && selectedPatient.treatment.id)))
        || null;

      if (!patientId || !userToken || !treatmentId) {
        if (isActive) {
          setHasStaffAuthoredConsentNote(false);
        }
        return;
      }

      try {
        const headers = {
          'Authorization': `Token ${userToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        };
        const collectedNotes = [];

        const patientNotesResponse = await fetch(`${apiBaseUrl}/treatment/patient/journal_notes/${patientId}/?treatment_id=${encodeURIComponent(treatmentId)}`, {
          headers,
          method: 'GET',
        });
        if (patientNotesResponse.ok) {
          const patientNotes = await patientNotesResponse.json();
          if (Array.isArray(patientNotes)) {
            collectedNotes.push(...patientNotes);
          }
        }

        if (!collectedNotes.some(hasStaffAuthoredJournalNote) && diagnosisIdentifier) {
          const treatmentQuery = treatmentId ? `?treatment_id=${treatmentId}` : '';
          const sessionsResponse = await fetch(`${apiBaseUrl}/treatment/sessions/all/${patientId}/${diagnosisIdentifier}/${treatmentQuery}`, {
            headers,
            method: 'GET',
          });

          if (sessionsResponse.ok) {
            const sessionsPayload = await sessionsResponse.json();
            const sessions = Array.isArray(sessionsPayload)
              ? sessionsPayload
              : (Array.isArray(sessionsPayload && sessionsPayload.sessions) ? sessionsPayload.sessions : []);
            const sessionIds = sessions
              .map(session => session && session.id)
              .filter(Boolean);

            if (sessionIds.length > 0) {
              const batchResponse = await fetch(`${apiBaseUrl}/treatment/session/journal_notes/batch/`, {
                headers,
                method: 'POST',
                body: JSON.stringify({ session_ids: sessionIds, treatment_id: treatmentId }),
              });
              if (batchResponse.ok) {
                const batchPayload = await batchResponse.json();
                if (batchPayload && typeof batchPayload === 'object') {
                  Object.values(batchPayload).forEach((notes) => {
                    if (Array.isArray(notes)) {
                      collectedNotes.push(...notes);
                    }
                  });
                }
              }
            }
          }
        }

        if (isActive) {
          setHasStaffAuthoredConsentNote(collectedNotes.some(hasStaffAuthoredJournalNote));
        }
      } catch (e) {
        if (isActive) {
          setHasStaffAuthoredConsentNote(false);
        }
      }
    };

    fetchConsentAuthorship();
    return () => {
      isActive = false;
    };
  }, [diagnosis, selectedPatient, userToken]);

  React.useEffect(() => {
    if (!journalSections) return;

    const defaultConsentText = hasStaffAuthoredConsentNote
      ? PATIENT_CONSENT_CLINICIAN_DEFAULT_TEXT
      : PATIENT_CONSENT_B2C_DEFAULT_TEXT;

    setJournalSections(prev => {
      if (!prev) return prev;

      const next = cloneSections(prev);
      if (!next.patientConsent) {
        next.patientConsent = {};
      }
      if (!Array.isArray(next.patientConsent.default)) {
        next.patientConsent.default = [];
      }

      let changed = false;
      let consentItem = next.patientConsent.default[0] || null;
      if (!consentItem) {
        consentItem = makeManualItem('', 'patientConsent', 'default');
        consentItem.manual = true;
        next.patientConsent.default = [consentItem];
        changed = true;
      }

      const currentConsentText = [
        consentItem.journalPhrase,
        consentItem.journal_text,
        consentItem.journal_phrase,
        consentItem.metaJournalPhrase,
        consentItem.meta_journalPhrase,
      ].find(value => value !== undefined
        && value !== null
        && String(value).trim() !== ''
        && !isPlaceholderPatientConsentText(value)) || '';

      if (!currentConsentText || isDefaultPatientConsentText(currentConsentText)) {
        if (String(consentItem.journalPhrase || '') !== defaultConsentText
          || String(consentItem.journal_text || '') !== defaultConsentText
          || String(consentItem.journal_phrase || '') !== defaultConsentText
          || String(consentItem.metaJournalPhrase || '') !== ''
          || String(consentItem.meta_journalPhrase || '') !== '') {
          consentItem.journalPhrase = defaultConsentText;
          consentItem.journal_text = defaultConsentText;
          consentItem.journal_phrase = defaultConsentText;
          consentItem.metaJournalPhrase = '';
          consentItem.meta_journalPhrase = '';
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [cloneSections, hasStaffAuthoredConsentNote, journalSections]);

  React.useEffect(() => {
    const consentItem = journalSections && journalSections.patientConsent && Array.isArray(journalSections.patientConsent.default)
      ? journalSections.patientConsent.default[0]
      : null;

    if (!consentItem) return;

    const consentId = idForItem(consentItem);
    const consentText = [
      consentItem.journalPhrase,
      consentItem.journal_text,
      consentItem.journal_phrase,
    ].find(value => value !== undefined && value !== null && String(value).trim() !== '') || '';

    if (!consentId || !consentText) return;

    setEditableValues((prev) => {
      const current = prev && prev[consentId] ? prev[consentId] : null;
      const localText = current && Object.prototype.hasOwnProperty.call(current, 'journalPhrase')
        ? String(current.journalPhrase || '')
        : '';
      const shouldReplaceLocal = !localText
        || isPlaceholderPatientConsentText(localText)
        || isDefaultPatientConsentText(localText);

      if (!shouldReplaceLocal) {
        return prev;
      }

      return {
        ...(prev || {}),
        [consentId]: {
          ...(current || {}),
          journalPhrase: consentText,
          metaJournalPhrase: '',
          theme: consentItem.theme || 'patientConsent',
          subgroup: consentItem.subgroup || 'default',
        },
      };
    });
  }, [journalSections]);

  const generateJournalFromTemplate = () => {
    // create a client-side skeleton draft; restore will re-fetch from server on cancel
    // Build a minimal skeleton with some commonly used groups and placeholders
    const skeleton = {
      painAnamnesis: { default: [ makeManualItem('Primary complaint','painAnamnesis','default') ] },
      patientConsent: { default: [ makeManualItem('Patient consent note','patientConsent','default') ] }
    };
    setJournalSections(skeleton);
    setAppliedDiagnosis({ id: 'manual-placeholder', title: 'Select or create diagnosis', indication_for_physiotherapy: '' });
    // reset versions view since this is a client-side generated draft
    setJournalVersions(prev => {
      const preferredTs = initialAssessmentDate || (selectedPatient && (selectedPatient.assessment_date || selectedPatient.initial_assessment_date || selectedPatient.answered_assessment_date || selectedPatient.answeredAssessmentDate)) || new Date().toISOString();
      const init = { id: 'initial-assessment', ts: preferredTs, base: buildSnapshotFromSections(skeleton), changes: [] };
      return [init];
    });
    setCurrentVersionIndex(0);
    // Enter edit mode so the user can finish/save the generated draft
    try {
      setTimeout(() => { try { enableGlobalEdit(); } catch (e) { setGlobalEditMode(true); } }, 0);
    } catch (e) {}
    setGeneratedDraftActive(true);
  };

  const cancelGeneratedDraft = async () => {
    // Discard client-side draft and reload canonical journal from backend after a short delay
    try {
      await fetchJournalDelayed(1000);
    } catch (e) {
      console.warn('Failed to re-fetch journal on cancelGeneratedDraft', e);
    }
    setGeneratedDraftActive(false);
    // exit global edit mode and clear local edit state
    setGlobalEditMode(false);
    setEditableValues({});
    setEditingItems({});
    setPreparedSavePayload(null);
    setPrognosisEdits(null);
    setAppliedPrognosis(null);
  };

  // Auto-prefill editable textfields from canonical `journalSections` when enabled.
  React.useEffect(() => {
    try {
      if (!journalSections) return;
      if (!autoPrefillJournalFields) return;
      const toAdd = {};
      Object.entries(journalSections || {}).forEach(([theme, subgroups]) => {
        Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
          (items || []).forEach(item => {
            try {
              const iid = item.question_id || item.id || item.identifier || idForItem(item);
              if (!iid) return;
              if (editableValues && Object.prototype.hasOwnProperty.call(editableValues, iid)) return;
              const meta = (item.metaJournalPhrase || item.meta_journalPhrase || '') || '';
              const jp = (item.journalPhrase || item.journal_text || '') || '';
              let journalPhraseToSet = (jp && String(jp).trim()) ? String(jp).trim() : (meta && String(meta).trim() ? String(meta).trim() : '');
              // Support pain baseline numeric-only items: build a sensible default text
              const numericVal = (item.numeric_value !== undefined && item.numeric_value !== null) ? String(item.numeric_value) : ((item.numericValue !== undefined && item.numericValue !== null) ? String(item.numericValue) : '');
              if (!journalPhraseToSet && numericVal) {
                // Only prefill with descriptive text for the 'pain at best' subgroup
                if (String(theme).toLowerCase().includes('painatbest') || String(theme).toLowerCase().includes('painat_best') || String(theme).toLowerCase().includes('painanamnesis_painatbest') || String(subgroup).toLowerCase().includes('painatbest')) {
                  journalPhraseToSet = `Pain at best: ${numericVal}/100`;
                }
              }
              if (!journalPhraseToSet) return;
              const extracted = (typeof extractTrailingNumber === 'function') ? extractTrailingNumber(journalPhraseToSet) : { text: journalPhraseToSet, num: numericVal || '' };
              toAdd[iid] = {
                metaJournalPhrase: meta || '',
                journalPhrase: extracted.text || '',
                numerical_value: (extracted.num || numericVal || ''),
                answer_text: item.answer_text || '',
                theme,
                subgroup
              };
            } catch (e) {
              // ignore per-item errors
            }
          });
        });
      });
      if (Object.keys(toAdd).length) {
        setEditableValues(prev => ({ ...(prev || {}), ...toAdd }));
      }
    } catch (e) {
      // non-fatal
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalSections, autoPrefillJournalFields]);

  React.useEffect(() => {
    if (!journalSections || !globalEditMode) return;

    const latestPausedById = {};
    try {
      Object.entries(journalSections || {}).forEach(([, subgroups]) => {
        Object.entries(subgroups || {}).forEach(([, items]) => {
          (items || []).forEach((item) => {
            const itemId = idForItem(item);
            if (!itemId) return;
            latestPausedById[itemId] = parseBooleanSafe(item && item.paused_from_followup);
          });
        });
      });
    } catch (e) {
      return;
    }

    if (!Object.keys(latestPausedById).length) return;

    setOriginalEditableSnapshot((prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      Object.entries(latestPausedById).forEach(([itemId, paused]) => {
        if (!Object.prototype.hasOwnProperty.call(next, itemId)) return;
        const prior = next[itemId] || {};
        const priorPaused = parseBooleanSafe(prior.paused_from_followup);
        if (priorPaused === paused) return;
        next[itemId] = { ...prior, paused_from_followup: paused };
        changed = true;
      });
      return changed ? next : prev;
    });

    setEditableValues((prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      Object.entries(latestPausedById).forEach(([itemId, paused]) => {
        if (!Object.prototype.hasOwnProperty.call(next, itemId)) return;
        const prior = next[itemId] || {};
        const snapshotPaused = originalEditableSnapshot && originalEditableSnapshot[itemId]
          ? parseBooleanSafe(originalEditableSnapshot[itemId].paused_from_followup)
          : undefined;
        const localHasOwnPause = Object.prototype.hasOwnProperty.call(prior, 'paused_from_followup');
        const localPaused = localHasOwnPause ? parseBooleanSafe(prior.paused_from_followup) : snapshotPaused;
        const locallyChangedPause = snapshotPaused !== undefined && localHasOwnPause && localPaused !== snapshotPaused;
        if (locallyChangedPause || localPaused === paused) return;
        next[itemId] = { ...prior, paused_from_followup: paused };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [journalSections, globalEditMode, originalEditableSnapshot]);

  // Expose a quick toggle for testing in the browser console:
  // call `window.__vp_setPrefillJournalFields(false)` to disable.
  React.useEffect(() => {
    try { window.__vp_setPrefillJournalFields = setAutoPrefillJournalFields; } catch (e) {}
    return () => { try { delete window.__vp_setPrefillJournalFields; } catch (e) {} };
  }, [setAutoPrefillJournalFields]);

  const inferKpiDisplayGroup = React.useCallback((theme = '', subgroup = '', mappedKey = '') => {
    const candidateKeys = [mappedKey, `${theme}_${subgroup}`, theme].filter(Boolean);
    if (candidateKeys.some(key => String(key).startsWith('painAnamnesis_SelfRatedFunction_MainGoal'))) {
      return 'Patient goal for treatment:';
    }
    if (candidateKeys.some(key => String(key).startsWith('painAnamnesis_') && [
      'frequency',
      'painAtBest',
      'painCurrent',
      'painAtWorst',
      'painWithActivity',
      'painAtRest',
      'difficulty',
      'function'
    ].some(part => String(key).toLowerCase().includes(part.toLowerCase())))) {
      return 'Pain baseline levels:';
    }
    return '';
  }, []);

  const buildFullSavePayload = React.useCallback((editableOverride = editableValues || {}, sectionsOverride = journalSections || {}) => {
    const payload = [];
    try {
      Object.entries(sectionsOverride || {}).forEach(([theme, subgroups]) => {
        Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
          (items || []).forEach((item) => {
            const id = idForItem(item);
            if (!id) return;

            const editVals = editableOverride && editableOverride[id] ? editableOverride[id] : null;
            const matched = item.selected_answer || item.selected_answer_meta || null;
            const priorMeta = removeAnswerIdTokens(String((matched && (matched.metaJournalPhrase || matched.meta_journalPhrase)) || item.metaJournalPhrase || item.meta_journalPhrase || ''));
            const meta = editVals && editVals.metaJournalPhrase !== undefined
              ? removeAnswerIdTokens(String(editVals.metaJournalPhrase || ''))
              : priorMeta;
            const priorJournal = removeAnswerIdTokens(String((matched && (matched.journalPhrase || matched.journal_phrase)) || item.journalPhrase || item.journal_phrase || item.journal_text || ''));
            let journal = editVals && editVals.journalPhrase !== undefined
              ? removeAnswerIdTokens(String(editVals.journalPhrase || ''))
              : priorJournal;
            if (editVals && editVals.custom_label !== undefined && editVals.custom_label !== '') {
              const customLabel = String(editVals.custom_label).trim();
              // For custom pain baselines the editable "describe activity"
              // textbox lives in `editVals.answer_text`. When the clinician
              // renames the metric (e.g. "pain with benchpress" → "pain with
              // squat"), rebuild the journal phrase from `<label> <activity>`
              // so the new name actually persists into KpiMeasurement.label
              // and propagates downstream into the patient/clinician follow-up
              // titles. Without this, the prior activity stayed embedded in
              // `journal` and the prepend-only logic merely doubled the label.
              const editedActivity = (editVals.answer_text !== undefined && editVals.answer_text !== null)
                ? String(editVals.answer_text).trim()
                : '';
              if (editedActivity) {
                journal = (customLabel + ' ' + editedActivity).replace(/\s{2,}/g, ' ').trim();
              } else if (!journal.startsWith(customLabel)) {
                journal = customLabel + (journal ? ' ' + journal : '');
              }
            }
            const priorAnswerText = removeAnswerIdTokens(String(getHumanAnswerText(matched, item.answer_text || item.answerText || '')));
            const answerText = editVals && editVals.answer_text !== undefined
              ? removeAnswerIdTokens(String(editVals.answer_text || ''))
              : priorAnswerText;

            let numerical = '';
            if (editVals && editVals.numerical_value !== undefined && editVals.numerical_value !== null && editVals.numerical_value !== '') numerical = String(editVals.numerical_value);
            else if (item.numeric_value !== undefined && item.numeric_value !== null && item.numeric_value !== '') numerical = String(item.numeric_value);
            else if (item.numericValue !== undefined && item.numericValue !== null && item.numericValue !== '') numerical = String(item.numericValue);
            if (numerical === 'null') numerical = '';
            let priorNumerical = '';
            if (item.numeric_value !== undefined && item.numeric_value !== null && item.numeric_value !== '') priorNumerical = String(item.numeric_value);
            else if (item.numericValue !== undefined && item.numericValue !== null && item.numericValue !== '') priorNumerical = String(item.numericValue);
            if (priorNumerical === 'null') priorNumerical = '';

            const mappedKey = (item && (item.mapped_key || item._mappedKey)) || (editVals && editVals.mapped_key) || '';
            const displayGroup = (editVals && editVals.display_group) || inferKpiDisplayGroup(theme, subgroup, mappedKey);
            const isManualItem = !!(item && item.manual) || String(id).startsWith('manual-');
            let kpiType = '';
            let kpiSubtype = '';
            if (editVals && editVals.kpi_type) {
              kpiType = String(editVals.kpi_type);
              kpiSubtype = (editVals.kpi_subtype || '').toString();
            } else if (item && item.kpi_type) {
              kpiType = String(item.kpi_type);
              kpiSubtype = (item.kpi_subtype || '').toString();
            }
            if (kpiType === 'pain_baseline') {
              kpiSubtype = normalizePainSubtypeForSave(
                kpiSubtype,
                (item && item.subgroup) || subgroup || '',
                mappedKey || (item && item.identifier) || (item && item.theme) || ''
              );
            } else if (kpiType === 'function_goal') {
              kpiSubtype = normalizeFunctionGoalSubtypeForSave(kpiSubtype);
            }
            // Backfill kpi_subtype when missing. Custom pain baselines created
            // via the in-page builder persist `subgroup: "custom"` on the item
            // but historically left `kpi_subtype` empty. Without this fallback
            // the backend resolver maps `(pain_baseline, "")` to (None, None)
            // and silently drops the edit \u2014 wiping the active baseline on the
            // very next save.
            if (kpiType && !kpiSubtype) {
              const itemSubgroup = item && item.subgroup ? String(item.subgroup) : '';
              const normalizedPainSubtype = kpiType === 'pain_baseline'
                ? normalizePainSubtypeForSave(
                  '',
                  itemSubgroup || subgroup || '',
                  mappedKey || (item && item.identifier) || (item && item.theme) || ''
                )
                : '';
              if (normalizedPainSubtype) {
                kpiSubtype = normalizedPainSubtype;
              } else if (kpiType === 'function_goal') {
                kpiSubtype = 'main_goal';
              } else if (itemSubgroup) {
                kpiSubtype = itemSubgroup;
              }
            }
            if (false) { /* legacy displayGroup branch retained below */ }
            else if (!kpiType && displayGroup === 'Pain baseline levels:') {
              // Phase 3: assessment-sourced pain items also get kpi_type so the
              // backend can route the edit through KpiMeasurement.set_active_baseline,
              // surgically superseding the patient-assessment baseline (one active
              // row at a time, full audit chain) instead of creating a duplicate.
              kpiType = 'pain_baseline';
              kpiSubtype = normalizePainSubtypeForSave(
                (item && item.kpi_subtype) || subgroup || '',
                subgroup || '',
                mappedKey || (item && item.identifier) || (item && item.theme) || ''
              );
            } else if (!kpiType && displayGroup === 'Patient goal for treatment:') {
              kpiType = 'function_goal';
              kpiSubtype = normalizeFunctionGoalSubtypeForSave((item && item.kpi_subtype) || 'main_goal') || 'main_goal';
            } else if (!kpiType) {
              // Last-chance heuristic — handles items missing displayGroup hints.
              const inferred = inferKpiTypeFromItem(item, '');
              if (inferred) {
                kpiType = inferred;
                if (inferred === 'pain_baseline') {
                  kpiSubtype = normalizePainSubtypeForSave(
                    (item && item.kpi_subtype) || '',
                    subgroup || (item && item.subgroup) || '',
                    mappedKey || (item && item.identifier) || (item && item.theme) || ''
                  );
                } else if (inferred === 'function_goal') {
                  kpiSubtype = normalizeFunctionGoalSubtypeForSave((item && item.kpi_subtype) || '') || 'main_goal';
                } else {
                  kpiSubtype = (item && item.kpi_subtype) || '';
                }
              }
            }
            // Mode hint lets the backend distinguish surgical override of an
            // assessment baseline from creation of a brand-new custom KPI.
            const editMode = isManualItem ? 'create_kpi' : 'override_baseline';

            const outObj = {
              id,
              theme,
              subgroup,
              metaJournalPhrase: meta,
              journalPhrase: journal && journal.trim() ? journal.trim() : '',
              answer_text: answerText,
              numerical_value: numerical,
              mapped_key: mappedKey || `${theme}_${subgroup}`,
            };
            if (displayGroup) outObj.display_group = displayGroup;
            if (kpiType) {
              outObj.kpi_type = kpiType;
              outObj.kpi_subtype = kpiSubtype;
              outObj.mode = editMode;
              const isDeleted = editVals && editVals.is_deleted !== undefined
                ? parseBooleanSafe(editVals.is_deleted)
                : parseBooleanSafe(item && item.is_deleted);
              const isPaused = editVals && editVals.paused_from_followup !== undefined
                ? parseBooleanSafe(editVals.paused_from_followup)
                : parseBooleanSafe(item && item.paused_from_followup);
              outObj.is_deleted = isDeleted;
              outObj.paused_from_followup = isPaused;
            }

            const priorHasContent = !!(
              (priorMeta && String(priorMeta).trim()) ||
              (priorJournal && String(priorJournal).trim()) ||
              (priorAnswerText && String(priorAnswerText).trim()) ||
              priorNumerical !== ''
            );
            const hasContent = !!(
              (outObj.metaJournalPhrase && String(outObj.metaJournalPhrase).trim()) ||
              (outObj.journalPhrase && String(outObj.journalPhrase).trim()) ||
              (outObj.answer_text && String(outObj.answer_text).trim()) ||
              outObj.numerical_value !== ''
            );
            const shouldDeleteGeneric = !kpiType && !hasContent && priorHasContent;
            if (shouldDeleteGeneric) {
              outObj.is_deleted = true;
            }
            if (hasContent || kpiType || shouldDeleteGeneric) payload.push(outObj);
          });
        });
      });
    } catch (e) {
      console.error('buildFullSavePayload error', e);
    }
    // Also include creator items (pain-baseline-* / function-goal-*) that live in
    // editableValues but are never added to journalSections.
    try {
      Object.entries(editableOverride || {}).forEach(([editKey, editVals]) => {
        if (!editVals || (!editKey.startsWith('pain-baseline-') && !editKey.startsWith('function-goal-'))) return;
        const displayGroup = (editVals.display_group) || '';
        const isBaseline = displayGroup === 'Pain baseline levels:';
        const isGoal = displayGroup === 'Patient goal for treatment:';
        if (!isBaseline && !isGoal) return;
        // Use a stable id: prefer editVals.id, else the editKey itself
        const id = editVals.id || editKey;
        const numerical = (editVals.numerical_value !== undefined && editVals.numerical_value !== null && editVals.numerical_value !== '')
          ? String(editVals.numerical_value) : '';
        const journal = removeAnswerIdTokens(String(editVals.journalPhrase || '')).trim();
        const answerText = removeAnswerIdTokens(String(editVals.answer_text || '')).trim();
        if (!numerical && !journal) return; // nothing meaningful to save
        const kpiType = isBaseline ? 'pain_baseline' : 'function_goal';
        const kpiSubtype = isBaseline
          ? (normalizePainSubtypeForSave(
            editVals.kpi_subtype || editVals.subgroup || '',
            editVals.subgroup || '',
            editVals.mapped_key || editVals.theme || ''
          ) || 'custom')
          : (normalizeFunctionGoalSubtypeForSave(editVals.kpi_subtype || editVals.subgroup || 'main_goal') || 'main_goal');
        const outObj = {
          id,
          theme: editVals.theme || '',
          subgroup: editVals.subgroup || '',
          metaJournalPhrase: removeAnswerIdTokens(String(editVals.metaJournalPhrase || '')).trim(),
          journalPhrase: journal,
          answer_text: answerText,
          numerical_value: numerical,
          mapped_key: editVals.mapped_key || '',
          display_group: displayGroup,
          kpi_type: kpiType,
          kpi_subtype: kpiSubtype,
          is_deleted: parseBooleanSafe(editVals.is_deleted),
          paused_from_followup: parseBooleanSafe(editVals.paused_from_followup),
        };
        payload.push(outObj);
      });
    } catch (e) {
      console.error('buildFullSavePayload creator items error', e);
    }
    // Dedupe by id: the journalSections loop and the editableOverride loop
    // can both emit a row for the same `creator-*` custom baseline. Without
    // dedup, the backend writes two `set_active_baseline` calls per save —
    // one with the canonical `kpi_subtype: 'custom'` and one with a stale
    // value — producing parasitic active rows at mismatched identifiers and
    // making the original benchpress baseline disappear from kpi_items.
    // Prefer the entry that carries an explicit canonical (kpi_type +
    // kpi_subtype='custom') over a generic fallback.
    try {
      const seen = new Map();
      payload.forEach((row) => {
        if (!row || !row.id) return;
        const key = String(row.id);
        const prior = seen.get(key);
        if (!prior) { seen.set(key, row); return; }
        const priorScore = (prior.kpi_type ? 2 : 0) + (String(prior.kpi_subtype || '').toLowerCase() === 'custom' ? 1 : 0);
        const rowScore = (row.kpi_type ? 2 : 0) + (String(row.kpi_subtype || '').toLowerCase() === 'custom' ? 1 : 0);
        if (rowScore > priorScore) seen.set(key, row);
      });
      return Array.from(seen.values());
    } catch (e) {
      console.error('buildFullSavePayload dedup error', e);
      return payload;
    }
  }, [editableValues, inferKpiDisplayGroup, journalSections, normalizeFunctionGoalSubtypeForSave, normalizePainSubtypeForSave]);

  const disableGlobalEdit = async () => {
    // Persist any pending editableValues into journalSections in a single bulk update
    try {
      const edits = Object.entries(editableValues || {}).map(([id, vals]) => ({ id, updates: { metaJournalPhrase: vals.metaJournalPhrase, journalPhrase: vals.journalPhrase, numerical_value: vals.numerical_value, answer_text: vals.answer_text } }));
      if (edits.length) applyBulkEdits(edits);
    } catch (e) { console.error('disableGlobalEdit bulk apply error', e); }
    // Prepare a payload object for later saving to the backend
    try {
      const payload = buildFullSavePayload(editableValues, journalSections);
      setPreparedSavePayload(payload.length ? payload : null);
      // placeholder: backend not ready; this payload is ready to be sent when API is available
      // console.log('Prepared journal edits payload:', payload);
    } catch (e) {}
    // Do NOT create/apply a client-side journal version here. We rely on the backend
    // to create canonical journal versions and provide version numbers. The frontend
    // will append the server-returned version after successfully POSTing below.
    // persist prognosis edits locally
    if (prognosisEdits) setAppliedPrognosis(Object.assign({}, prognosisEdits));
    // compute payload snapshot now (before clearing editableValues)
    let payloadToSendLocal = null;
    try {
      // Ensure we include journalPhrase for manual/empty fields:
      // - prefer explicit journalPhrase
      // - fallback to metaJournalPhrase when journalPhrase is empty
      // - normalize 'null' / undefined numeric values to empty string
      // Debug: snapshot current journalSections keys and groupExtraFields for missing-theme diagnosis
      try {
        const presentIds = new Set();
        Object.entries(journalSections || {}).forEach(([t, sgs]) => { Object.entries(sgs || {}).forEach(([sg, items]) => { (items||[]).forEach(it=> presentIds.add(idForItem(it))); }); });
        const missingPreview = Object.keys(editableValues || {}).filter(k => !presentIds.has(k) && !(groupExtraFields && Object.values(groupExtraFields).some(arr=> (arr||[]).includes(k)))).slice(0,10);
        if (missingPreview.length) console.warn('Editable values not present in journalSections or groupExtraFields (sample up to 10):', missingPreview, 'groupExtraFields keys:', Object.keys(groupExtraFields||{}));
      } catch (e) {}

      payloadToSendLocal = buildFullSavePayload(editableValues, journalSections);

      if (!payloadToSendLocal || !payloadToSendLocal.length) payloadToSendLocal = preparedSavePayload || null;
    } catch (e) { payloadToSendLocal = preparedSavePayload || null; }

    setGlobalEditMode(false);
    try { setExpandedInfoKey(null); } catch (e) {}
    setEditableValues({});
    setEditingItems({});
    setGroupDrafts({});
    setGroupExtraFields({});
    setPrognosisEdits(null);

    // Attempt to persist the prepared payload as an immutable journal version on the server
    try {
      if (payloadToSendLocal && payloadToSendLocal.length && selectedPatient && selectedPatient.user_id) {
        // console.log('Posting journal version payload:', { user_id: selectedPatient.user_id, payload: { edits: payloadToSendLocal }, note: 'Saved from frontend Finish editing' });
        const res = await postJournalVersion({ user_id: selectedPatient.user_id, payload: { edits: payloadToSendLocal }, note: 'Saved from frontend Finish editing' });
        if (res && res.id) {
          setSnackbarMsg('Journal saved successfully.');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          // Append the returned server version locally for history browsing,
          // but keep the visible journal in live mode until the canonical
          // delayed refresh lands.
          setJournalVersions(prev => {
            const hasInit = prev && prev.length && prev[0] && prev[0].id === 'initial-assessment';
            const init = hasInit ? prev[0] : null;
            const rest = prev ? (hasInit ? prev.slice(1) : prev) : [];
            const nextRest = rest.concat([res]);
            const next = init ? [init].concat(nextRest) : nextRest;
            journalVersionsRef.current = next;
            userSelectedVersionRef.current = false;
            try { setCurrentVersionIndex(-1); } catch (e) {}
            return next;
          });

          // Then re-fetch canonical data after a short delay to ensure backend has processed
          (async () => {
            try {
              await fetchJournalDelayed(1000);
            } catch (e) {
              console.warn('Delayed refresh after save failed', e);
            }
          })();

          // Notify patientList to bust the KPI metrics cache for this patient
          try {
            const resolvedTreatmentId =
              (selectedPatient && selectedPatient.treatment_id)
              || (selectedPatient && selectedPatient.treatment && selectedPatient.treatment.id)
              || (appliedDiagnosis && (appliedDiagnosis.treatment_id || appliedDiagnosis.id))
              || (diagnosis && !Array.isArray(diagnosis) && (diagnosis.treatment_id || diagnosis.id))
              || null;
            window.dispatchEvent(new CustomEvent('kpiBaselinesUpdated', {
              detail: {
                user_id: selectedPatient.user_id,
                treatment_id: resolvedTreatmentId
              }
            }));
          } catch (e) {}
        }
      }
    } catch (e) { console.error('Failed to persist journal version', e); }

    // Persist prognosis edits if present
    try {
      if (prognosisEdits) {
        // resolve diagnosis id from appliedDiagnosis, diagnosis prop or selectedPatient
        let diagId = null;
        if (appliedDiagnosis && appliedDiagnosis.id) diagId = appliedDiagnosis.id;
        else if (diagnosis && diagnosis.id) diagId = diagnosis.id;
        else if (Array.isArray(diagnosis) && diagnosis.length && diagnosis[0].id) diagId = diagnosis[0].id;
        else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length && selectedPatient.diagnosis[0].id) diagId = selectedPatient.diagnosis[0].id;
        if (diagId) {
          try {
            const resp = await postPrognosisChange(diagId, prognosisEdits, 'Updated via journal edit');
            if (resp && resp.prognosis) setAppliedPrognosis(resp.prognosis);
          } catch (e) {
            console.error('postPrognosisChange failed', e);
          }
        }
      }
    } catch (e) { console.error('postPrognosisChange failed', e); }
  };

  const toggleGlobalEdit = () => globalEditMode ? disableGlobalEdit() : enableGlobalEdit();

  // Cancel editing: discard local edits and load newest saved version
  const cancelEditing = () => {
    try {
      setGlobalEditMode(false);
      try { setExpandedInfoKey(null); } catch (e) {}
      // Restore the last known snapshot so empty manual patients repopulate after cancel
      if (originalEditableSnapshot && Object.keys(originalEditableSnapshot).length) {
        setEditableValues(originalEditableSnapshot);
        // Don't set editingItems - we're exiting edit mode
        setEditingItems({});
      } else {
        setEditableValues({});
        setEditingItems({});
      }
      setGroupDrafts({});
      setGroupExtraFields({});
      setPreparedSavePayload(null);
      setPrognosisEdits(null);
      // Clear special creator states
      setPainBaselineCreators({});
      setFunctionGoalCreators({});
      setPainBaselineInputs({});
      setFunctionGoalInputs({});
      // revert applied prognosis as well then refresh canonical prognosis from server
      setAppliedPrognosis(null);
      (async () => {
        try {
          let diagId = null;
          if (appliedDiagnosis && appliedDiagnosis.id) diagId = appliedDiagnosis.id;
          else if (diagnosis && diagnosis.id) diagId = diagnosis.id;
          else if (Array.isArray(diagnosis) && diagnosis.length && diagnosis[0].id) diagId = diagnosis[0].id;
          else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length && selectedPatient.diagnosis[0].id) diagId = selectedPatient.diagnosis[0].id;
          if (!diagId) return;
          if (!diagBelongsToPatient(diagId)) return;
          if (!userToken || (typeof userToken === 'string' && userToken.trim() === '')) return;
          const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
          const diagUrl = `${apiBaseUrl}/treatment/diagnoses/${diagId}/` + (selectedPatient && selectedPatient.user_id ? (`?user_id=${selectedPatient.user_id}`) : '');
          const res = await fetch(diagUrl, { method: 'GET', headers });
          if (!res.ok) return;
          const data = await res.json();
          const prog = data && (data.prognosis || (data.diagnosis && data.diagnosis.prognosis)) ? (data.prognosis || (data.diagnosis && data.diagnosis.prognosis)) : null;
          setAppliedPrognosis(prog);
          setAppliedDiagnosisIfChanged(data);
        } catch (e) {}
      })();
      // apply newest version if available, otherwise restore live
      if (journalVersions && journalVersions.length) {
        const idx = journalVersions.length - 1;
        try { applyVersion(idx); } catch (e) {}
        setCurrentVersionIndex(idx);
      } else {
        setCurrentVersionIndex(-1);
      }
    } catch (e) { console.error('cancelEditing error', e); }
  };

  const hasUnsavedJournalChanges = React.useMemo(() => {
    return generatedDraftActive || globalEditMode;
  }, [generatedDraftActive, globalEditMode]);

  const clearPendingJournalAction = React.useCallback(() => {
    pendingJournalActionRef.current = null;
    setUnsavedJournalDialogOpen(false);
    setUnsavedJournalDialogLabel('');
  }, []);

  const executePendingJournalAction = React.useCallback(async () => {
    const pendingAction = pendingJournalActionRef.current;
    clearPendingJournalAction();
    if (typeof pendingAction === 'function') {
      await pendingAction();
    }
  }, [clearPendingJournalAction]);

  const requestUnsavedJournalAction = React.useCallback((label, action) => {
    if (typeof action !== 'function') return;
    if (!hasUnsavedJournalChanges) {
      action();
      return;
    }
    pendingJournalActionRef.current = action;
    setUnsavedJournalDialogLabel(label || 'continuing');
    setUnsavedJournalDialogOpen(true);
  }, [hasUnsavedJournalChanges]);

  const handleUnsavedJournalCancel = React.useCallback(() => {
    if (unsavedJournalDialogBusy) return;
    clearPendingJournalAction();
  }, [clearPendingJournalAction, unsavedJournalDialogBusy]);

  const handleUnsavedJournalDiscard = React.useCallback(async () => {
    if (unsavedJournalDialogBusy) return;
    setUnsavedJournalDialogBusy(true);
    try {
      if (generatedDraftActive) {
        await cancelGeneratedDraft();
      } else {
        cancelEditing();
      }
      await executePendingJournalAction();
    } finally {
      setUnsavedJournalDialogBusy(false);
    }
  }, [unsavedJournalDialogBusy, generatedDraftActive, cancelGeneratedDraft, cancelEditing, executePendingJournalAction]);

  const handleUnsavedJournalSave = React.useCallback(async () => {
    if (unsavedJournalDialogBusy) return;
    setUnsavedJournalDialogBusy(true);
    try {
      await disableGlobalEdit();
      await executePendingJournalAction();
    } finally {
      setUnsavedJournalDialogBusy(false);
    }
  }, [unsavedJournalDialogBusy, disableGlobalEdit, executePendingJournalAction]);

  React.useEffect(() => {
    if (!hasUnsavedJournalChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedJournalChanges]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const guard = {
      hasUnsavedChanges: () => hasUnsavedJournalChanges,
      isEditingMode: () => generatedDraftActive || globalEditMode,
      request: (label, action) => requestUnsavedJournalAction(label, action),
    };
    window.__vpJournalUnsavedGuard = guard;
    return () => {
      if (window.__vpJournalUnsavedGuard === guard) {
        delete window.__vpJournalUnsavedGuard;
      }
    };
  }, [hasUnsavedJournalChanges, generatedDraftActive, globalEditMode, requestUnsavedJournalAction]);

  const handleHeaderViewChange = React.useCallback((event) => {
    const nextView = event.target.value;
    if (nextView === headerView) return;
    requestUnsavedJournalAction(`switching to ${nextView}`, () => {
      setHeaderView(nextView);
    });
  }, [headerView, requestUnsavedJournalAction]);

  const openProgramBuilder = React.useCallback((phaseId, showAdvice = false) => {
    if (!canAdaptiveExercise) {
      return;
    }
    requestUnsavedJournalAction('opening the program builder', () => {
      if (props.setShowAdviceInExerciseTab) props.setShowAdviceInExerciseTab(showAdvice);
      if (typeof props.onEditInProgramBuilder === 'function') {
        props.onEditInProgramBuilder(phaseId);
      } else if (props.setActiveTab) {
        props.setActiveTab('exerciseProgram');
      }
    });
  }, [requestUnsavedJournalAction, props.setShowAdviceInExerciseTab, props.onEditInProgramBuilder, props.setActiveTab, canAdaptiveExercise]);

  const queueDiagnosisChangeConfirmation = React.useCallback((change) => {
    requestUnsavedJournalAction('changing the diagnosis', () => {
      setPendingDiagnosisChange(change);
      setDiagnosisPreviewData(null);
      setDiagnosisPreviewError('');
      if (change && change.type === 'existing' && change.diagId) {
        setShowAutoGenDialog(true);
        loadDiagnosisProgramPreview(change.diagId);
        return;
      }
      setShowAutoGenDialog(true);
    });
  }, [loadDiagnosisProgramPreview, requestUnsavedJournalAction]);

  // Placeholder: Save prepared payload to backend when API is ready
  const savePendingEdits = async () => {
    if (!preparedSavePayload || !Array.isArray(preparedSavePayload) || preparedSavePayload.length === 0) return;
    try {
      // Use the journal versions endpoint to persist an immutable snapshot
      const body = { user_id: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : undefined, payload: { edits: preparedSavePayload }, note: 'Saved via savePendingEdits' };
      const res = await postJournalVersion(body);
      if (res && res.id) {
        setJournalVersions(prev => {
          const hasInit = prev && prev.length && prev[0] && prev[0].id === 'initial-assessment';
          const init = hasInit ? prev[0] : null;
          const rest = prev ? (hasInit ? prev.slice(1) : prev) : [];
          const nextRest = rest.concat([res]);
          const next = init ? [init].concat(nextRest) : nextRest;
          const newIndex = next.length - 1;
          journalVersionsRef.current = next;
          setCurrentVersionIndex(newIndex);
          try { applyVersion(newIndex); } catch (e) {}
          return next;
        });
      }
    } catch (e) {
      console.error('Failed to save pending edits', e);
    }
  };

  // POST a journal version to the backend. The backend serializer accepts either treatment_id or user_id in body.
  const postJournalVersion = async (body) => {
    try {
      // Normalize edits to ensure theme/subgroup present when possible
      try {
        if (body && body.payload && Array.isArray(body.payload.edits)) {
          const normalized = body.payload.edits.map(e => {
            try {
              const edit = Object.assign({}, e);
              // If edit is a full item shape (has question_id or journal_text), map to expected keys
              if (!edit.id && edit.question_id) edit.id = edit.question_id;
              if (!edit.metaJournalPhrase && edit.meta_journalPhrase) edit.metaJournalPhrase = edit.meta_journalPhrase;
              if (!edit.journalPhrase && edit.journal_text) edit.journalPhrase = edit.journal_text;
              // Ensure theme/subgroup via journalSections lookup
              if ((!edit.theme || edit.theme === '') && edit.id) {
                const loc = findThemeSubgroupForId(edit.id) || {};
                if (loc.theme) {
                  edit.theme = loc.theme;
                  edit.subgroup = loc.subgroup || (edit.subgroup || 'default');
                }
              }
              // Prefer client-recorded mapped_key for manually-created items when present
              if ((!edit.theme || edit.theme === '') && edit.id && String(edit.id).startsWith('manual-')) {
                try {
                  const matchedItem = findItemInSections(journalSections || {}, edit.id) || null;
                  const mk = matchedItem && (matchedItem.mapped_key || matchedItem._mappedKey || null);
                  if (mk && typeof mk === 'string') {
                    if (mk.includes('_')) {
                      const idx = mk.indexOf('_');
                      edit.theme = mk.substring(0, idx) || mk;
                      edit.subgroup = mk.substring(idx + 1) || (edit.subgroup || 'default');
                    } else {
                      edit.theme = mk;
                      edit.subgroup = edit.subgroup || 'default';
                    }
                  }
                } catch (e) {}
              }
              // If still missing (or set to 'ungrouped'), check groupExtraFields mapping
              if ((!edit.theme || edit.theme === '' || edit.theme === 'ungrouped') && edit.id) {
                // broader display group -> keys mapping used for inference
                const highLevelMapping = {
                  'Primary complaint and cause of contact:': [
                    'keySymptom','painAnamnesis','painAnamnesis_cause_how_issue_occured','painAnamnesis_duration','painAnamnesis_doctor','painAnamnesis_doctorDiagnosis','secondary_multimorbid_doctorDiagnosis'
                  ],
                  'Location and character of symptoms:': ['painAnamnesis_location','painAnamnesis_knownCondition','painAnamnesis_painCharacteristics'],
                  'Affected activities, pattern and development of symptoms:': ['painAnamnesis_pattern','painAnamnesis_affectedActivities','painAnamnesis_episodes','painAnamnesis_development'],
                  'Pain baseline levels:': ['painAnamnesis_frequency','painAnamnesis_painAtBest','painAnamnesis_painCurrent','painAnamnesis_painAtWorst','painAnamnesis_function', 'painAnamnesis_painAtRest','painAnamnesis_painWithActivity', 'painAnamnesis_difficulty','painAnamnesis_custom','painAnamnesis_function'],
                  'Patient goal for treatment:': ['painAnamnesis_SelfRatedFunction_MainGoal'],
                  'Redflags:': ['redflags'],
                  'Movement tests': ['movement_test'],
                  'Symptom response on clinical tests': ['clinicalTest','SymptomProvoking_clinicalTest','SymptomImproving_clinicalTest','keyFunctionalLimitations']
                };
                for (const [gName, ids] of Object.entries(groupExtraFields || {})) {
                  if (Array.isArray(ids) && ids.includes(edit.id)) {
                    const mapped = (GROUPING_MAP && GROUPING_MAP[gName]) || highLevelMapping[gName] || [];
                    if (Array.isArray(mapped) && mapped.length) {
                      const themeKey = mapped[0];
                      if (typeof themeKey === 'string' && themeKey.includes('_')) {
                        const idx = themeKey.indexOf('_');
                        edit.theme = themeKey.substring(0, idx) || themeKey;
                        edit.subgroup = themeKey.substring(idx + 1) || (edit.subgroup || 'default');
                      } else {
                        edit.theme = themeKey;
                        edit.subgroup = edit.subgroup || 'default';
                      }
                    }
                    break;
                  }
                }
              }
                  // Try keyword-based inference using high-level grouping as a fallback
                  if ((!edit.theme || edit.theme === '') && edit.journalPhrase) {
                    try {
                      const highLevelGrouping = {
                        'Primary complaint and cause of contact:': [
                          'keySymptom','painAnamnesis','painAnamnesis_cause_how_issue_occured','painAnamnesis_duration','painAnamnesis_doctor','painAnamnesis_doctorDiagnosis','secondary_multimorbid_doctorDiagnosis'
                        ],
                        'Location and character of symptoms:': ['painAnamnesis_location','painAnamnesis_knownCondition','painAnamnesis_painCharacteristics'],
                        'Affected activities, pattern and development of symptoms:': ['painAnamnesis_pattern','painAnamnesis_affectedActivities','painAnamnesis_episodes','painAnamnesis_development'],
                        'Pain baseline levels:': ['painAnamnesis_frequency','painAnamnesis_painAtBest','painAnamnesis_painCurrent','painAnamnesis_painAtWorst','painAnamnesis_function', 'painAnamnesis_painAtRest','painAnamnesis_painWithActivity','painAnamnesis_custom'],
                        'Patient goal for treatment:': ['painAnamnesis_SelfRatedFunction_MainGoal'],
                        'Redflags:': ['redflags'],
                        'Movement tests': ['movement_test'],
                        'Symptom response on clinical tests': ['clinicalTest','SymptomProvoking_clinicalTest','SymptomImproving_clinicalTest','keyFunctionalLimitations']
                      };
                      const keywordsFor = {
                        'Primary complaint and cause of contact:': ['complaint','primary','pain','symptom','cause','contact','neck','arm','back'],
                        'Location and character of symptoms:': ['location','character','radiat','behind','forearm','thumb','index','radiating','location'],
                        'Affected activities, pattern and development of symptoms:': ['activity','work','lift','pattern','develop','episode','developed','worsen','worse'],
                        'Pain baseline levels:': ['best','worst','current','baseline','pain at','score','level','/ 10',' /100'],
                        'Patient goal for treatment:': ['goal','psfs','goal for','desired','aim'],
                        'Redflags:': ['red flag','redflag','neurolog','systemic','fever','weight loss'],
                        'Movement tests': ['movement','flexion','extension','rotation','range of motion','rom'],
                        'Symptom response on clinical tests': ['provok','improv','reliev','test','spurling','bakody','ulntt']
                      };
                      const text = String(edit.journalPhrase || '').toLowerCase();
                      let matchedGroup = null;
                      for (const [gName, kws] of Object.entries(keywordsFor)) {
                        for (const kw of kws) {
                          if (kw && text.includes(kw)) { matchedGroup = gName; break; }
                        }
                        if (matchedGroup) break;
                      }
                      if (matchedGroup) {
                        const mapped = highLevelGrouping[matchedGroup];
                        if (Array.isArray(mapped) && mapped.length) {
                          const themeKey = mapped[0];
                          if (typeof themeKey === 'string' && themeKey.includes('_')) {
                            const idx = themeKey.indexOf('_');
                            edit.theme = themeKey.substring(0, idx) || themeKey;
                            edit.subgroup = themeKey.substring(idx + 1) || (edit.subgroup || 'default');
                          } else {
                            edit.theme = themeKey;
                            edit.subgroup = edit.subgroup || 'default';
                          }
                          try { console.log('Keyword-inferred group', matchedGroup, '->', edit.theme, edit.subgroup, 'for id', edit.id); } catch (e) {}
                        }
                      }
                    } catch (e) {}
                  }
                    // Do NOT heuristically assign an internal theme here.
                    // If theme is missing at this point, prefer leaving it unset and record a warning.
                    if ((!edit.theme || edit.theme === '') && edit.journalPhrase && String(edit.journalPhrase).trim()) {
                      try { console.warn('postJournalVersion: theme missing for manual edit id', edit.id, 'display_group:', edit.display_group || null); } catch (e) {}
                      // leave edit.theme empty — backend or a later explicit mapping should provide canonical keys
                    }
              // final fallback: set subgroup default
              if (!edit.subgroup) edit.subgroup = edit.subgroup || 'default';

              // Do NOT replace internal `edit.theme` with display-group titles.
              // Preserve the internal theme key; record the display-group when available.
              try {
                const highLevelGrouping = {
                  'Primary complaint and cause of contact:': [
                    'keySymptom','painAnamnesis','painAnamnesis_cause_how_issue_occured','painAnamnesis_duration','painAnamnesis_doctor','painAnamnesis_doctorDiagnosis','secondary_multimorbid_doctorDiagnosis'
                  ],
                  'Location and character of symptoms:': ['painAnamnesis_location','painAnamnesis_knownCondition','painAnamnesis_painCharacteristics'],
                  'Affected activities, pattern and development of symptoms:': ['painAnamnesis_pattern','painAnamnesis_affectedActivities','painAnamnesis_episodes','painAnamnesis_development'],
                  'Pain baseline levels:': ['painAnamnesis_frequency','painAnamnesis_painAtBest','painAnamnesis_painCurrent','painAnamnesis_painAtWorst','painAnamnesis_function', 'painAnamnesis_painAtRest','painAnamnesis_difficulty','painAnamnesis_custom','painAnamnesis_function'],
                  'Patient goal for treatment:': ['painAnamnesis_SelfRatedFunction_MainGoal'],
                  'Redflags:': ['redflags'],
                  'Movement tests': ['movement_test'],
                  'Symptom response on clinical tests': ['clinicalTest','SymptomProvoking_clinicalTest','SymptomImproving_clinicalTest','keyFunctionalLimitations']
                };
                // Prefer an exact theme_subgroup match when available, otherwise fall back to theme-only.
                let foundDisplay = null;
                for (const [gName, keys] of Object.entries(highLevelGrouping)) {
                  if (!Array.isArray(keys)) continue;
                  if (keys.includes(`${edit.theme}_${edit.subgroup}`)) { foundDisplay = gName; break; }
                }
                if (!foundDisplay) {
                  for (const [gName, keys] of Object.entries(highLevelGrouping)) {
                    if (!Array.isArray(keys)) continue;
                    if (keys.includes(edit.theme)) { foundDisplay = gName; break; }
                  }
                }
                if (foundDisplay) edit.display_group = foundDisplay;
              
              // If this is a client-created manual item, mark the theme as a manual variant
              // so it matches grouping keys like `keySymptom_manual` and renders after
              // canonical items. Do not overwrite if already marked.
              try {
                if (edit.id && String(edit.id).startsWith('manual-') && edit.theme && typeof edit.theme === 'string' && !edit.theme.endsWith('_manual')) {
                  edit.theme = `${edit.theme}_manual`;
                }
              } catch (e) {}
              } catch (e) {}

              return edit;
            } catch (err) { return e; }
          });
          body.payload.edits = normalized;
        }
      } catch (err) { /* ignore normalization errors */ }
      // Prefer using the selected treatment id for isolation; fall back to body.treatment_id or 0000... as last resort
      const treatmentId = (selectedPatient && selectedPatient.treatment_id)
        || (selectedPatient && selectedPatient.treatment && selectedPatient.treatment.id)
        || (body && body.treatment_id)
        || '00000000-0000-0000-0000-000000000000';
      const fullBody = {
        ...body,
        treatment_id: treatmentId,
        user_id: (selectedPatient && selectedPatient.user_id) ? selectedPatient.user_id : body?.user_id,
      };
      const url = `${apiBaseUrl}/treatment/treatments/${treatmentId}/journal_versions/`;
      const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json', 'Content-Type': 'application/json' };
      // try { console.log('postJournalVersion sending', { url, body: fullBody }); } catch (e) {}
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(fullBody) });
      if (!res.ok) {
        let txt = '';
        try { txt = await res.text(); } catch (e) {}
        console.error('postJournalVersion failed', res.status, txt);
        return null;
      }
      const data = await res.json();
      try {
        const updateDetail = {
          treatmentId,
          userId: fullBody.user_id || null,
          ts: Date.now(),
        };
        localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
        window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
      } catch (e) {}
      return data;
    } catch (e) {
      console.error('postJournalVersion error', e);
      return null;
    }
  };

  const findItemInSections = (sections, targetId) => {
    try {
      for (const [theme, subgroups] of Object.entries(sections || {})) {
        for (const [sg, items] of Object.entries(subgroups || {})) {
          for (const it of items) {
            if (idForItem(it) === targetId) return it;
          }
        }
      }
    } catch (e) {}
    return null;
  };

  const applyVersion = (index) => {
    if (index === null || index === undefined) return;
    if (index < 0) {
      const liveSections = cloneSections(liveJournalSectionsRef.current || {});
      setJournalSections(liveSections);
      setCurrentVersionIndex(-1);
      return;
    }
    const versions = journalVersionsRef.current && journalVersionsRef.current.length ? journalVersionsRef.current : journalVersions;
    const ver = versions && versions[index];
    if (!ver) return;
    try {
      const nextSections = buildSectionsForVersion(index);
      setJournalSections(nextSections);
      setCurrentVersionIndex(index);
    } catch (e) { console.error(e); }
  };

  const goPrevVersion = () => {
    const total = journalVersions ? journalVersions.length : 0;
    if (!total) return;
    if (currentVersionIndex === -1) {
      const idx = total - 1;
      userSelectedVersionRef.current = true;
      applyVersion(idx);
      return;
    }
    const idx = Math.max(0, currentVersionIndex - 1);
    userSelectedVersionRef.current = true;
    applyVersion(idx);
  };

  const goNextVersion = () => {
    const total = journalVersions ? journalVersions.length : 0;
    if (!total) return;
    if (currentVersionIndex === -1) {
      userSelectedVersionRef.current = true;
      applyVersion(0);
      return;
    }
    const idx = Math.min(total - 1, currentVersionIndex + 1);
    userSelectedVersionRef.current = true;
    applyVersion(idx);
  };

  // Auto-apply latest version when `journalVersions` updates, unless the user navigated versions
  React.useEffect(() => {
    if (globalEditMode) return; // don't override while editing
    if (userSelectedVersionRef.current) return;
    if (journalVersions && journalVersions.length) {
      try { applyVersion(-1); setCurrentVersionIndex(-1); } catch (e) {}
    }
  }, [journalVersions, globalEditMode]);

  const startEdit = (item) => {
    const id = idForItem(item);
    const matched = (item.selected_answer || item.selected_answer_meta || null);
    const meta = (matched && (matched.metaJournalPhrase || matched.meta_journalPhrase)) || item.metaJournalPhrase || item.meta_journalPhrase || '';
    const ans = getHumanAnswerText(matched, item.answer_text || item.answerText || '');
    const journal = (matched && (matched.journalPhrase || matched.journal_phrase)) || item.journalPhrase || item.journal_phrase || item.journal_text || '';
    const num = (item.numeric_value !== undefined && item.numeric_value !== null) ? String(item.numeric_value) : (item.numericValue !== undefined ? String(item.numericValue) : '');
    // store combined journalPhrase for editing (meta + answer_text + journal) without duplicate answer_text
      try {
      let combined = '';
      if (meta) combined = String(meta).trim();
      if (ans) {
        const a = String(ans).trim();
        const j = String(journal || '').trim();
        if (j && j.toLowerCase().startsWith(a.toLowerCase())) {
          combined = combined ? combined + ' ' + j : j;
        } else {
          combined = (combined ? combined + ' ' : '') + a;
          if (journal) combined = combined + ' ' + String(journal).trim();
        }
      } else {
        if (journal) combined = (combined ? combined + ' ' : '') + String(journal).trim();
      }
      combined = (combined || '').trim();
      const extracted = extractTrailingNumber(combined);
      setEditableValues(prev => ({ ...prev, [id]: { metaJournalPhrase: meta, journalPhrase: extracted.text, numerical_value: (extracted.num || num || ''), answer_text: ans } }));
    } catch (e) {
      const fallback = ((meta ? meta + ' ' : '') + (ans ? ans + ' ' : '') + journal).trim();
      const extracted = extractTrailingNumber(fallback);
      setEditableValues(prev => ({ ...prev, [id]: { metaJournalPhrase: meta, journalPhrase: extracted.text, numerical_value: (extracted.num || num || ''), answer_text: ans } }));
    }
    setEditingItems(prev => ({ ...prev, [id]: true }));
  };

  // (moved) Fetch diagnosis list when entering change mode — inserted after approveState declaration

  // Apply multiple edits in a single cloned update to `journalSections` to avoid race conditions
  const applyBulkEdits = (editsArray) => {
    if (!journalSections) return;
    try {
      const newSections = {};
      Object.entries(journalSections).forEach(([theme, subgroups]) => {
        newSections[theme] = { ...subgroups };
        Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
          newSections[theme][subgroup] = Array.isArray(items) ? items.slice() : items;
        });
      });

      const clean = (v) => {
        if (v === undefined || v === null) return v;
        return removeAnswerIdTokens(String(v));
      };

      let didAny = false;
      editsArray.forEach(({ id: targetId, updates }) => {
        try {
          for (const [theme, subgroups] of Object.entries(newSections)) {
            for (const [subgroup, items] of Object.entries(subgroups || {})) {
              for (let idx = 0; idx < (items || []).length; idx++) {
                const it = items[idx];
                  if (idForItem(it) === targetId) {
                  const newItem = { ...it };
                  if (updates.metaJournalPhrase !== undefined) newItem.metaJournalPhrase = clean(updates.metaJournalPhrase);
                  if (updates.journalPhrase !== undefined) {
                    const cleaned = clean(updates.journalPhrase);
                    // extract trailing numeric token if present  ||  consider removing this sanitization to preserve user intent
                    const ext = extractTrailingNumber(cleaned);
                    newItem.journalPhrase = ext.text;
                    newItem.journal_text = ext.text;
                    if (ext.num) { const nn = parseNumericSafe(ext.num); newItem.numeric_value = nn; newItem.numericValue = nn; }
                  }
                  if (updates.answer_text !== undefined) { newItem.answer_text = clean(updates.answer_text); newItem.answerText = clean(updates.answer_text); }
                  if (updates.numerical_value !== undefined) { const n = parseNumericSafe(updates.numerical_value); newItem.numeric_value = n; newItem.numericValue = n; }
                  // update cloned selected_answer_meta/selected_answer
                  try {
                    const existingAnsMeta = it.selected_answer_meta || it.selected_answer || null;
                    const clonedAns = Object.assign({}, existingAnsMeta || {});
                    if (updates.metaJournalPhrase !== undefined) clonedAns.metaJournalPhrase = clean(updates.metaJournalPhrase);
                    if (updates.journalPhrase !== undefined) {
                      const cleaned = clean(updates.journalPhrase);
                      const ext = extractTrailingNumber(cleaned);
                      clonedAns.journalPhrase = ext.text;
                      if (ext.num) { const nn = parseNumericSafe(ext.num); clonedAns.numeric_value = nn; clonedAns.numericValue = nn; }
                    }
                    if (updates.answer_text !== undefined) { clonedAns.answerText = clean(updates.answer_text); clonedAns.text = clean(updates.answer_text); }
                    if (updates.numerical_value !== undefined) { const nn = parseNumericSafe(updates.numerical_value); clonedAns.numeric_value = nn; clonedAns.numericValue = nn; }
                    newItem.selected_answer_meta = clonedAns;
                    newItem.selected_answer = Object.assign({}, clonedAns);
                  } catch (e) {console.error('applyBulkEdits cloning selected_answer_meta error', e); }

                  // Remove duplicate leading meta/answer_text from journal fields to avoid duplication in display
                  try {
                    const prefixes = [];
                    if (newItem.metaJournalPhrase) prefixes.push(newItem.metaJournalPhrase);
                    if (newItem.answer_text) prefixes.push(newItem.answer_text);
                    const jp = (newItem.journalPhrase || '').toString().trim();
                    let cleanedJP = stripLeadingPrefixes(jp, prefixes);
                    // ensure any trailing numeric token is separated
                    const extJP = extractTrailingNumber(cleanedJP);
                    cleanedJP = extJP.text;
                    newItem.journalPhrase = cleanedJP;
                    newItem.journal_text = cleanedJP;
                    if (extJP.num) {
                      const nn = parseNumericSafe(extJP.num);
                      if (nn !== null) {
                        newItem.numeric_value = nn;
                        newItem.numericValue = nn;
                        if (newItem.selected_answer_meta) { newItem.selected_answer_meta.numeric_value = nn; newItem.selected_answer_meta.numericValue = nn; }
                      }
                    }
                    if (newItem.selected_answer_meta && newItem.selected_answer_meta.journalPhrase) newItem.selected_answer_meta.journalPhrase = cleanedJP;
                    if (newItem.selected_answer && newItem.selected_answer.journalPhrase) newItem.selected_answer.journalPhrase = cleanedJP;
                  } catch (e) {console.error('applyBulkEdits cleaning journalPhrase error', e); }
                  items[idx] = newItem;
                  didAny = true;
                  // found and applied, break out to next edit
                  idx = items.length;
                }
              }
            }
          }
        } catch (e) { console.error('applyBulkEdits inner error', e); }
      });

      if (didAny) {
          console.log('applyBulkEdits applied', editsArray.map(e => e.id));
          setJournalSections(newSections);
        } else {
          console.warn('applyBulkEdits: no edits applied', editsArray.map(e => e.id)); // 
        }
    } catch (e) { console.error('applyBulkEdits error', e); }
  };

  const cancelEdit = (item) => {
    const id = idForItem(item);
    setEditingItems(prev => { const n = { ...prev }; delete n[id]; return n; });
    setEditableValues(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleEditableChange = (item, field, value) => {
    const id = idForItem(item);
    setEditableValues(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  // Remove simple duplicated phrases like "X X" or "X: X:"
  const dedupePhrase = (s) => {
    const str = (s || '').trim();
    if (!str) return str;
    // pattern: identical phrase repeated twice with optional colon
    const m = str.match(/^(.*?:?)\s+\1$/);
    if (m && m[1]) return m[1].trim();
    return str;
  };

  // Combined editor: single textfield holds journalPhrase and numeric value (trailing number)
  const handleCombinedChange = (item, value) => {
    const id = idForItem(item);
    const s = (value || '').toString();
    // detect trailing numeric token (allow separators like space, ·, -, :, —)
    const m = s.match(/^(.*?)[\s·–—:\-]*([0-9]+)\s*$/);
    let journalPart = s;
    let numPart = '';
    if (m) {
      journalPart = (m[1] || '').trim();
      numPart = m[2] || '';
    }
    setEditableValues(prev => ({ ...prev, [id]: { ...prev[id], journalPhrase: dedupePhrase(journalPart), numerical_value: numPart } }));
  };


const updateItemFields = (itemOrId, updates) => {
  if (!journalSections) return;
  // determine stable target id (accept either item object or id string)
  const targetId = (typeof itemOrId === 'string') ? itemOrId : idForItem(itemOrId);

  // minimally deep-clone themes -> subgroups -> items arrays to avoid mutating original nested objects
  const newSections = {};
  try {
    Object.entries(journalSections).forEach(([theme, subgroups]) => {
      newSections[theme] = { ...subgroups };
      Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
        newSections[theme][subgroup] = Array.isArray(items) ? items.slice() : items;
      });
    });

    const clean = (v) => {
      if (v === undefined || v === null) return v;
      return removeAnswerIdTokens(String(v));
    };

    let didUpdate = false;

    Object.entries(newSections).forEach(([theme, subgroups]) => {
      Object.entries(subgroups || {}).forEach(([subgroup, items]) => {
        for (let idx = 0; idx < (items || []).length; idx++) {
          const it = items[idx];
          if (idForItem(it) === targetId) {
            // clone the item object only, do NOT mutate nested selected_answer/selected_answer_meta
            const newItem = { ...it };

            if (updates.metaJournalPhrase !== undefined) {
              newItem.metaJournalPhrase = clean(updates.metaJournalPhrase);
            }
            if (updates.journalPhrase !== undefined) {
              const cleaned = clean(updates.journalPhrase);
              const ext = extractTrailingNumber(cleaned);
              newItem.journalPhrase = ext.text;
              newItem.journal_text = ext.text;
              if (ext.num) {
                const nVal = parseNumericSafe(ext.num);
                newItem.numeric_value = nVal;
                newItem.numericValue = nVal;
              }
            }
            if (updates.answer_text !== undefined) {
              newItem.answer_text = clean(updates.answer_text);
              newItem.answerText = clean(updates.answer_text);
            }
            if (updates.numerical_value !== undefined) {
              const n = parseNumericSafe(updates.numerical_value);
              newItem.numeric_value = n;
              newItem.numericValue = n;
            }

            // Also update cloned selected_answer_meta / selected_answer for UI consumption
            try {
              const existingAnsMeta = it.selected_answer_meta || it.selected_answer || null;
              const clonedAns = Object.assign({}, existingAnsMeta || {});
              if (updates.metaJournalPhrase !== undefined) clonedAns.metaJournalPhrase = clean(updates.metaJournalPhrase);
                if (updates.journalPhrase !== undefined) {
                  const cleaned = clean(updates.journalPhrase);
                  const ext = extractTrailingNumber(cleaned);
                  clonedAns.journalPhrase = ext.text;
                  if (ext.num) {
                    const nn = parseNumericSafe(ext.num);
                    clonedAns.numeric_value = nn;
                    clonedAns.numericValue = nn;
                  }
                }
              if (updates.answer_text !== undefined) { clonedAns.answerText = clean(updates.answer_text); clonedAns.text = clean(updates.answer_text); }
              if (updates.numerical_value !== undefined) { const nn = parseNumericSafe(updates.numerical_value); clonedAns.numeric_value = nn; clonedAns.numericValue = nn; }
              newItem.selected_answer_meta = clonedAns;
              newItem.selected_answer = Object.assign({}, clonedAns);
            } catch (e) {}

            // Remove duplicate leading meta/answer_text from journal fields to avoid duplication in display
            try {
              const prefixes = [];
              if (newItem.metaJournalPhrase) prefixes.push(newItem.metaJournalPhrase);
              if (newItem.answer_text) prefixes.push(newItem.answer_text);
              const jp = (newItem.journalPhrase || '').toString().trim();
              const cleanedJP = stripLeadingPrefixes(jp, prefixes);
              newItem.journalPhrase = cleanedJP;
              newItem.journal_text = cleanedJP;
              if (newItem.selected_answer_meta && newItem.selected_answer_meta.journalPhrase) newItem.selected_answer_meta.journalPhrase = cleanedJP;
              if (newItem.selected_answer && newItem.selected_answer.journalPhrase) newItem.selected_answer.journalPhrase = cleanedJP;
            } catch (e) {}

            items[idx] = newItem;
            didUpdate = true;
            break;
          }
        }
      });
    });

    if (didUpdate) {
      console.log('updateItemFields applied for', targetId, updates);
      setJournalSections(newSections);
    } else {
      console.warn('updateItemFields: no matching item found for', targetId, updates);
    }
  } catch (e) {
    // swallow
    console.error('updateItemFields error', e);
  }
};
// ...existing code...

  // ✅ FIXED: Sync prognosis edits when entering/exiting global edit
  // Only depend on globalEditMode to reduce reruns (diagnosis object reference changes)
  React.useEffect(() => {
    if (!globalEditMode) {
      setPrognosisEdits(null);
      return;
    }
    try {
      // Prefer previously applied prognosis edits (client-side) so re-entering edit mode
      // doesn't overwrite saved edits with the original diagnosis prop.
      if (appliedPrognosis) {
        setPrognosisEdits(Object.assign({}, appliedPrognosis));
        return;
      }
      let diagObj = null;
      if (Array.isArray(diagnosis) && diagnosis.length > 0) diagObj = diagnosis[0];
      else if (diagnosis && diagnosis.diagnosis) diagObj = diagnosis;
      else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length > 0) diagObj = selectedPatient.diagnosis[0];
      const prog = diagObj && (diagObj.prognosis || (diagObj.diagnosis && diagObj.diagnosis.prognosis)) || null;
      if (prog) setPrognosisEdits(Object.assign({}, prog));
      else setPrognosisEdits(null);
    } catch (e) { setPrognosisEdits(null); }
    // ✅ FIXED: Only globalEditMode in deps; we read appliedPrognosis/diagnosis from closure which is fine
  }, [globalEditMode]);


  // ✅ FIXED: Fetch latest prognosis only when patient changes (not on every journal version change)
  React.useEffect(() => {
    let cancelled = false;
    // Abort controller to cancel stale requests on dependency change
    const controller = new AbortController();
    const fetchLatestPrognosis = async () => {
      try {
        // resolve diagnosis id candidates
        let diagId = null;
        if (appliedDiagnosis && appliedDiagnosis.id) diagId = appliedDiagnosis.id;
        else if (diagnosis && diagnosis.id) diagId = diagnosis.id;
        else if (Array.isArray(diagnosis) && diagnosis.length && diagnosis[0].id) diagId = diagnosis[0].id;
        else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length && selectedPatient.diagnosis[0].id) diagId = selectedPatient.diagnosis[0].id;
        if (!diagId) return;
        // Skip if diagnosis does not belong to current patient (prevents 401/404 with mismatched user_id)
        if (!diagBelongsToPatient(diagId)) return;
        if (!userToken || (typeof userToken === 'string' && userToken.trim() === '')) return;

        // Build a stable key for deduping fetches even across remounts in a session
        const key = `${diagId}|${selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : ''}|${userToken || ''}`;
        try {
          const seen = sessionStorage.getItem(`vp_prognosis_fetched_${key}`);
          if (seen === '1') {
            // Skip network call; derive prognosis from available local objects
            try {
              let diagObj = null;
              if (appliedDiagnosis && appliedDiagnosis.id) diagObj = appliedDiagnosis;
              else if (diagnosis && diagnosis.id) diagObj = diagnosis;
              else if (Array.isArray(diagnosis) && diagnosis.length && diagnosis[0]) diagObj = diagnosis[0];
              else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length && selectedPatient.diagnosis[0]) diagObj = selectedPatient.diagnosis[0];
              const progLocal = diagObj && (diagObj.prognosis || (diagObj.diagnosis && diagObj.diagnosis.prognosis)) ? (diagObj.prognosis || (diagObj.diagnosis && diagObj.diagnosis.prognosis)) : null;
              setAppliedPrognosis(progLocal || null);
              setAppliedDiagnosisIfChanged(diagObj || null);
            } catch (e) { /* ignore local derivation errors */ }
            return;
          }
        } catch (e) { /* sessionStorage not available, continue */ }

        const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
        const diagUrl = `${apiBaseUrl}/treatment/diagnoses/${diagId}/` + (selectedPatient && selectedPatient.user_id ? (`?user_id=${selectedPatient.user_id}`) : '');
        const res = await fetch(diagUrl, { method: 'GET', headers, signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const prog = data && (data.prognosis || (data.diagnosis && data.diagnosis.prognosis)) ? (data.prognosis || (data.diagnosis && data.diagnosis.prognosis)) : null;
        setAppliedPrognosis(prog);
        // also keep appliedDiagnosis up-to-date for future changes
        setAppliedDiagnosisIfChanged(data);
        try { sessionStorage.setItem(`vp_prognosis_fetched_${key}`, '1'); } catch (e) { /* ignore */ }
      } catch (e) {
        // ignore
      }
    };
    fetchLatestPrognosis();
    return () => { cancelled = true; try { controller.abort(); } catch (e) {} };
    // ✅ FIXED: Removed journalVersions from deps to prevent refetch on every journal save
    // Only refetch when patient or diagnosis ID explicitly changes (use stable ID strings)
  }, [selectedPatient?.user_id, (Array.isArray(diagnosis) ? diagnosis[0]?.id : diagnosis?.id), userToken]);

  const handlePrognosisChange = (key, value) => {
    setPrognosisEdits(prev => ({ ...(prev || {}), [key]: value }));
  };
  // Approve/Change diagnosis state
  const [approveState, setApproveState] = React.useState(null);
  const [diagnosisSelect, setDiagnosisSelect] = React.useState("");
  const [diagnosisInput, setDiagnosisInput] = React.useState("");
  const [diagnosisSearchInput, setDiagnosisSearchInput] = React.useState("");
  const [editedDiagnosis, setEditedDiagnosis] = React.useState("");
  const [diagnosisReason, setDiagnosisReason] = React.useState("");
  const [diagnosisIndication, setDiagnosisIndication] = React.useState("");
  const [diagnosesList, setDiagnosesList] = React.useState([]);
  const [prognosisExpectedOutcome, setPrognosisExpectedOutcome] = React.useState("");
  const [prognosisTypicalSymptoms, setPrognosisTypicalSymptoms] = React.useState("");
  const [prognosisAverageDuration, setPrognosisAverageDuration] = React.useState("");
  const [prognosisDevelopment, setPrognosisDevelopment] = React.useState("");
  const [prognosisIfWorsens, setPrognosisIfWorsens] = React.useState("");
  const [appliedDiagnosis, setAppliedDiagnosis] = React.useState(null);
  
  // Template selector state
  const [showTemplateSelector, setShowTemplateSelector] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState("");
  const [showTemplateAutoGenDialog, setShowTemplateAutoGenDialog] = React.useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = React.useState(null);

  // Notify parent when journal sections change (for epicrisis generation)
  React.useEffect(() => {
    if (props.onJournalSectionsUpdate && journalSections) {
      props.onJournalSectionsUpdate(journalSections);
    }
  }, [journalSections, props.onJournalSectionsUpdate]);

  // Notify parent when applied diagnosis changes (for epicrisis generation)
  React.useEffect(() => {
    if (props.onDiagnosisUpdate && appliedDiagnosis) {
      props.onDiagnosisUpdate(appliedDiagnosis);
    }
  }, [appliedDiagnosis, props.onDiagnosisUpdate]);

  // Helper: verify a diagnosis id belongs to the currently selected patient
  const diagBelongsToPatient = React.useCallback((diagId) => {
    if (!diagId || !selectedPatient || !Array.isArray(selectedPatient.diagnosis)) return true; // be permissive if unknown
    return selectedPatient.diagnosis.some(d => {
      const did = d?.id || d?.diagnosis?.id || d?.identifier || d?.diagnosis;
      return did === diagId;
    });
  }, [selectedPatient]);

  // Reset applied diagnosis/prognosis when switching patients to avoid stale diagId/user_id mismatches
  React.useEffect(() => {
    setAppliedDiagnosis(null);
    setAppliedPrognosis(null);
    setPrognosisEdits(null);
  }, [selectedPatient?.user_id]);

  // Also reset local diagnosis edit state when patient changes so single-treatment patients show correct suggestion
  React.useEffect(() => {
    setApproveState(null);
    setDiagnosisSelect("");
    setDiagnosisInput("");
    setDiagnosisSearchInput("");
    setEditedDiagnosis("");
    setDiagnosisReason("");
    setDiagnosisIndication("");
  }, [selectedPatient?.user_id]);

  const diagnosisAutocompleteOptions = React.useMemo(() => {
    const fallbackDiagnoses = [
      { id: 'Non-specific neck pain', title: 'Non-specific neck pain' },
      { id: 'Cervical Radiculopathy', title: 'Cervical Radiculopathy' },
      { id: 'Cervicogenic Headache', title: 'Cervicogenic Headache' },
    ];

    const sourceDiagnoses = Array.isArray(diagnosesList) && diagnosesList.length ? diagnosesList : fallbackDiagnoses;
    return [...sourceDiagnoses, { id: 'Other', title: 'Other (type below)' }];
  }, [diagnosesList]);

  const getDiagnosisOptionLabel = React.useCallback((option) => {
    if (!option) return '';
    return String(option.title || option.diagnosis || option.label || option.id || '').trim();
  }, []);

  const selectedDiagnosisOption = React.useMemo(() => {
    if (!diagnosisSelect || diagnosisSelect === 'Other') return null;
    const matched = (Array.isArray(diagnosesList) ? diagnosesList : []).find((item) => String(item.id) === String(diagnosisSelect));
    if (matched) return matched;
    return (diagnosisAutocompleteOptions || []).find((item) => String(item.id) === String(diagnosisSelect)) || null;
  }, [diagnosisAutocompleteOptions, diagnosesList, diagnosisSelect]);

  const diagnosisSearchHasMatches = React.useMemo(() => {
    const query = diagnosisSearchInput.trim().toLowerCase();
    if (!query || diagnosisSelect === 'Other') return true;

    return diagnosisAutocompleteOptions.some((option) => {
      const optionId = String(option && option.id ? option.id : '').trim().toLowerCase();
      const optionLabel = getDiagnosisOptionLabel(option).toLowerCase();
      if (!optionLabel || optionId === 'other' || optionLabel === 'other (type below)') return false;
      return optionLabel.includes(query) || optionId.includes(query);
    });
  }, [diagnosisAutocompleteOptions, diagnosisSearchInput, diagnosisSelect, getDiagnosisOptionLabel]);

  const setAppliedDiagnosisIfChanged = React.useCallback((next) => {
    setAppliedDiagnosis((prev) => {
      if (!prev && !next) return prev;
      if (!prev || !next) return next || null;
      const getId = (d) => (d && (d.id || (d.diagnosis && d.diagnosis.id) || d.identifier || d.diagnosis)) || null;
      const prevId = getId(prev);
      const nextId = getId(next);
      const prevTitle = (prev && (prev.title || prev.diagnosis)) || '';
      const nextTitle = (next && (next.title || next.diagnosis)) || '';
      const prevInd = (prev && prev.indication_for_physiotherapy) || '';
      const nextInd = (next && next.indication_for_physiotherapy) || '';
      if (prevId === nextId && prevTitle === nextTitle && prevInd === nextInd) return prev;
      return next;
    });
  }, []);

  // State for auto-generate treatment confirmation
  const [showAutoGenDialog, setShowAutoGenDialog] = React.useState(false);
  const [pendingDiagnosisChange, setPendingDiagnosisChange] = React.useState(null);
  const [diagnosisPreviewData, setDiagnosisPreviewData] = React.useState(null);
  const [diagnosisPreviewDraft, setDiagnosisPreviewDraft] = React.useState(null);
  const [diagnosisPreviewLoading, setDiagnosisPreviewLoading] = React.useState(false);
  const [diagnosisPreviewError, setDiagnosisPreviewError] = React.useState('');
  const [previewExerciseLibraryOpen, setPreviewExerciseLibraryOpen] = React.useState(false);
  const [previewAdviceLibraryOpen, setPreviewAdviceLibraryOpen] = React.useState(false);
  const [previewLibraryMode, setPreviewLibraryMode] = React.useState('replace');
  const [previewLibraryContext, setPreviewLibraryContext] = React.useState(null);
  const [previewLibraryExercises, setPreviewLibraryExercises] = React.useState([]);
  const [previewLibraryExercisesLoading, setPreviewLibraryExercisesLoading] = React.useState(false);
  const [previewLibraryExercisesError, setPreviewLibraryExercisesError] = React.useState('');
  const [previewExerciseExpandedCards, setPreviewExerciseExpandedCards] = React.useState({});
  const [previewExerciseDialogOpen, setPreviewExerciseDialogOpen] = React.useState(false);
  const [previewExerciseEditContext, setPreviewExerciseEditContext] = React.useState(null);
  const [previewEditedExerciseParams, setPreviewEditedExerciseParams] = React.useState({});
  const [previewAdviceDialogOpen, setPreviewAdviceDialogOpen] = React.useState(false);
  const [previewAdviceEditContext, setPreviewAdviceEditContext] = React.useState(null);
  const [previewPhaseTabByIndex, setPreviewPhaseTabByIndex] = React.useState({});
  const [previewPhaseProgressionDialogOpen, setPreviewPhaseProgressionDialogOpen] = React.useState(false);
  const [previewPhaseProgressionEditContext, setPreviewPhaseProgressionEditContext] = React.useState(null);
  const [previewPhaseProgressionDraftCombos, setPreviewPhaseProgressionDraftCombos] = React.useState([{
    localId: `preview-phase-combo-${Date.now()}`,
    combo_join_operator: 'and',
    conditions: [
      { metric_key: 'minimum_duration_weeks', operator: 'gte', threshold_value: 2 },
    ],
  }]);
  const [previewPhaseProgressionSaving, setPreviewPhaseProgressionSaving] = React.useState(false);
  const [previewEditedAdviceParams, setPreviewEditedAdviceParams] = React.useState({
    title: '',
    advice: '',
    importance: 1,
  });

  const formatPreviewExerciseDetails = React.useCallback((exercise) => {
    if (!exercise) return '';
    const parts = [];
    if (exercise.sets !== undefined && exercise.sets !== null && String(exercise.sets).trim() !== '') parts.push(`Sets: ${exercise.sets}`);
    if (exercise.repetitions !== undefined && exercise.repetitions !== null && String(exercise.repetitions).trim() !== '') parts.push(`Reps: ${exercise.repetitions}`);
    if (exercise.frequency !== undefined && exercise.frequency !== null && String(exercise.frequency).trim() !== '') parts.push(`Frequency: ${exercise.frequency}`);
    if (exercise.frequencyType) parts.push(String(exercise.frequencyType));
    if (exercise.time !== undefined && exercise.time !== null && String(exercise.time).trim() !== '') parts.push(`Time: ${exercise.time}`);
    return parts.join(' · ');
  }, []);

  const formatPreviewCondition = React.useCallback((condition, metricLabels = {}) => {
    if (!condition) return '';
    const metricLabel = metricLabels[condition.metric_key] || condition.metric_key || 'Progress criterion';
    const operatorLabel = {
      gte: 'at least',
      lte: 'at most',
      eq: 'exactly',
    }[condition.operator] || condition.operator || 'is';
    const threshold = condition.threshold_value !== undefined && condition.threshold_value !== null ? condition.threshold_value : '';
    const suffix = Array.isArray(condition.exercise_identifiers) && condition.exercise_identifiers.length
      ? ` for ${condition.exercise_identifiers.join(', ')}`
      : '';
    return `${metricLabel} ${operatorLabel} ${threshold}${suffix}`.trim();
  }, []);

  const formatPreviewRuleLines = React.useCallback((transition, metricLabels = {}) => {
    if (!transition) return [];
    const rule = transition.effective_rule || transition.template_rule || transition.override_rule || null;
    const sourceLines = [];
    const fallbackSummary = transition.fallback_rule_preset && transition.fallback_rule_preset.summary;
    if (rule && Array.isArray(rule.conditions) && rule.conditions.length) {
      const grouped = rule.conditions.reduce((acc, condition) => {
        const key = String(condition.combo_index ?? 0);
        if (!acc[key]) acc[key] = [];
        acc[key].push(condition);
        return acc;
      }, {});
      Object.keys(grouped).sort((a, b) => Number(a) - Number(b)).forEach((key, index) => {
        grouped[key].forEach((condition, conditionIndex) => {
          const formatted = formatPreviewCondition(condition, metricLabels);
          if (formatted) sourceLines.push(formatted);
        });
      });
    } else if (fallbackSummary) {
      sourceLines.push(fallbackSummary);
    }

    const extraNotes = [];
    if (transition.fallback_rule_preset && Array.isArray(transition.fallback_rule_preset.unrepresented_checks)) {
      extraNotes.push(...transition.fallback_rule_preset.unrepresented_checks);
    }
    if (transition.fallback_rule_preset && Array.isArray(transition.fallback_rule_preset.notes)) {
      extraNotes.push(...transition.fallback_rule_preset.notes);
    }

    return [...sourceLines, ...extraNotes];
  }, [formatPreviewCondition]);

  const cloneDiagnosisPreviewPayload = React.useCallback((payload) => {
    if (!payload) return null;
    try {
      return JSON.parse(JSON.stringify(payload));
    } catch (error) {
      const clonedPhases = Array.isArray(payload.phases)
        ? payload.phases.map((phase) => ({
          ...phase,
          exercises: Array.isArray(phase?.exercises) ? phase.exercises.map((exercise) => ({ ...exercise })) : [],
          advices: Array.isArray(phase?.advices) ? phase.advices.map((advice) => ({ ...advice })) : [],
        }))
        : [];
      return {
        ...payload,
        phases: clonedPhases,
      };
    }
  }, []);

  const clearDiagnosisPreviewState = React.useCallback(() => {
    setShowAutoGenDialog(false);
    setShowTemplateAutoGenDialog(false);
    setPendingDiagnosisChange(null);
    setDiagnosisPreviewData(null);
    setDiagnosisPreviewDraft(null);
    setDiagnosisPreviewError('');
    setDiagnosisPreviewLoading(false);
    setPreviewExerciseLibraryOpen(false);
    setPreviewAdviceLibraryOpen(false);
    setPreviewLibraryMode('replace');
    setPreviewLibraryContext(null);
    setPreviewLibraryExercises([]);
    setPreviewLibraryExercisesLoading(false);
    setPreviewLibraryExercisesError('');
    setPreviewExerciseExpandedCards({});
    setPreviewExerciseDialogOpen(false);
    setPreviewExerciseEditContext(null);
    setPreviewEditedExerciseParams({});
    setPreviewAdviceDialogOpen(false);
    setPreviewAdviceEditContext(null);
    setPreviewPhaseTabByIndex({});
    setPreviewPhaseProgressionDialogOpen(false);
    setPreviewPhaseProgressionEditContext(null);
    setPreviewPhaseProgressionDraftCombos([{
      localId: `preview-phase-combo-${Date.now()}`,
      combo_join_operator: 'and',
      conditions: [
        { metric_key: 'minimum_duration_weeks', operator: 'gte', threshold_value: 2 },
      ],
    }]);
    setPreviewEditedAdviceParams({
      title: '',
      advice: '',
      importance: 1,
    });
  }, []);

  const buildPreviewExerciseParams = React.useCallback((exercise) => ({
    sets: exercise?.sets ?? 3,
    repetitions: exercise?.repetitions ?? 10,
    time: exercise?.time ?? 30,
    pause: exercise?.pause ?? 15,
    frequencyType: exercise?.frequencyType || exercise?.frequency?.frequencyType || 'daily',
    frequency: exercise?.frequency?.frequency ?? exercise?.frequency ?? 1,
    daysInbetween: exercise?.daysInbetween ?? exercise?.daysInBetween ?? '',
    index: exercise?.index ?? 1,
    description: exercise?.description || '',
    title: exercise?.title || '',
    img: exercise?.picture || exercise?.img || '',
    video: exercise?.video || exercise?.video_url || '',
    doseType: exercise?.doseType || ((exercise?.time && exercise.time !== 0) ? 'time' : 'repetitions'),
  }), []);

  const buildPreviewAdviceParams = React.useCallback((advice) => ({
    title: advice?.title || '',
    advice: advice?.advice || '',
    importance: advice?.importance ?? 1,
  }), []);

  const updatePreviewDraft = React.useCallback((updater) => {
    setDiagnosisPreviewDraft((prev) => {
      if (!prev || !Array.isArray(prev.phases)) return prev;
      const nextDraft = typeof updater === 'function' ? updater(prev) : updater;
      return nextDraft;
    });
  }, []);

  const PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS = React.useMemo(() => ([
    { key: 'pain_reduction_percent', label: 'Pain reduction (%)', default_operator: 'gte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'current' },
    { key: 'pain_frequency_reduction_percent', label: 'Pain frequency reduction (%)', default_operator: 'gte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'frequency' },
    { key: 'custom_pain_baseline', label: 'Current pain threshold', default_operator: 'lte', metric_source: 'kpi_template', kpi_type: 'pain_baseline', kpi_subtype: 'current' },
    { key: 'psfs_score', label: 'PSFS / self-rated function', default_operator: 'gte', metric_source: 'kpi_template', kpi_type: 'function_goal', kpi_subtype: 'main_goal' },
    { key: 'custom_functional_goal', label: 'Custom functional goal threshold', default_operator: 'gte', metric_source: 'kpi_template', kpi_type: 'function_goal', kpi_subtype: 'main_goal' },
    { key: 'average_exercise_pain', label: 'Average exercise pain', default_operator: 'lte' },
    { key: 'exercise_level_progressions', label: 'Exercise level progressions', default_operator: 'gte' },
    { key: 'minimum_compliance', label: 'Compliance (%)', default_operator: 'gte' },
    { key: 'sessions_completed', label: 'Sessions completed', default_operator: 'gte' },
    { key: 'minimum_duration_weeks', label: 'Duration in phase (weeks)', default_operator: 'gte' },
  ]), []);

  const PREVIEW_PHASE_PROGRESSION_TIME_KEYS = React.useMemo(() => new Set(['minimum_duration_weeks', 'sessions_completed']), []);

  const getPreviewPhaseProgressionMetricType = React.useCallback((condition = {}) => {
    const metricKey = String(condition.metric_key || '').trim();
    if (metricKey === 'average_exercise_pain' || metricKey === 'minimum_compliance' || metricKey === 'exercise_level_progressions' || metricKey === 'sessions_completed' || metricKey === 'minimum_duration_weeks') {
      return 'time';
    }
    if (metricKey.includes('pain') || condition.kpi_type === 'pain_baseline') return 'pain';
    if (metricKey.includes('function') || condition.kpi_type === 'function_goal') return 'function';
    return 'generic';
  }, []);

  const createPreviewPhaseProgressionComboId = React.useCallback(() => `preview-phase-combo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);

  const getPreviewPhaseProgressionMetricOptions = React.useCallback((conditions = [], conditionIndex = 0) => {
    const available = conditionIndex === 0
      ? PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS.filter((option) => PREVIEW_PHASE_PROGRESSION_TIME_KEYS.has(option.key))
      : PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS;
    if (!Array.isArray(conditions) || !conditions.length) {
      return available;
    }
    return available;
  }, [PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS, PREVIEW_PHASE_PROGRESSION_TIME_KEYS]);

  const normalizePreviewPhaseProgressionConditionMetricKey = React.useCallback((condition = {}) => {
    const directKey = String(condition.metric_key || '').trim();
    if (directKey) return directKey;
    const kpiType = String(condition.kpi_type || '').trim();
    const kpiSubtype = String(condition.kpi_subtype || '').trim();
    if (kpiType && kpiSubtype) {
      return `kpi/${kpiType}/${kpiSubtype}`;
    }
    return '';
  }, []);

  const buildPreviewPhaseProgressionConditionDraft = React.useCallback((metricKey = 'minimum_duration_weeks', source = {}) => {
    const matched = PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS.find((option) => option.key === metricKey)
      || PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS[0];
    return {
      metric_key: matched.key,
      operator: source.operator || matched.default_operator || 'gte',
      threshold_value: source.threshold_value ?? '',
      combo_index: source.combo_index ?? 0,
      combo_join_operator: source.combo_join_operator || 'and',
      exercise_identifiers_text: Array.isArray(source.exercise_identifiers) ? source.exercise_identifiers.join(', ') : '',
    };
  }, [PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS]);

  const orderPreviewPhaseProgressionConditionDrafts = React.useCallback((conditions = []) => {
    const drafts = Array.isArray(conditions) && conditions.length
      ? conditions.map((condition, index) => buildPreviewPhaseProgressionConditionDraft(
        normalizePreviewPhaseProgressionConditionMetricKey(condition) || condition.metric_key || (index === 0 ? 'minimum_duration_weeks' : 'sessions_completed'),
        condition,
      ))
      : [buildPreviewPhaseProgressionConditionDraft('minimum_duration_weeks', {}), buildPreviewPhaseProgressionConditionDraft('sessions_completed', { combo_join_operator: 'or' })];
    const firstIndex = drafts.findIndex((condition) => PREVIEW_PHASE_PROGRESSION_TIME_KEYS.has(condition.metric_key));
    if (firstIndex > 0) {
      const [timeCondition] = drafts.splice(firstIndex, 1);
      drafts.unshift(timeCondition);
    }
    return drafts;
  }, [buildPreviewPhaseProgressionConditionDraft, normalizePreviewPhaseProgressionConditionMetricKey, PREVIEW_PHASE_PROGRESSION_TIME_KEYS]);

  const orderPreviewPhaseProgressionConditionCombos = React.useCallback((conditions = []) => {
    if (!Array.isArray(conditions) || conditions.length === 0) {
      return [{ localId: createPreviewPhaseProgressionComboId(), combo_join_operator: 'and', conditions: orderPreviewPhaseProgressionConditionDrafts([]) }];
    }
    const comboMap = new Map();
    conditions.forEach((condition) => {
      const comboIndex = Number(condition.combo_index || 0);
      if (!comboMap.has(comboIndex)) {
        comboMap.set(comboIndex, []);
      }
      comboMap.get(comboIndex).push(condition);
    });
    return Array.from(comboMap.entries()).sort((a, b) => a[0] - b[0]).map(([comboIndex, comboConditions], index) => ({
      localId: createPreviewPhaseProgressionComboId(),
      combo_join_operator: index === 0 ? 'and' : ((comboConditions[0] && comboConditions[0].combo_join_operator) || 'or'),
      conditions: orderPreviewPhaseProgressionConditionDrafts(comboConditions).map((condition, conditionIndex) => ({
        ...condition,
        combo_index: index,
        combo_join_operator: index === 0 ? 'and' : (conditionIndex === 0 ? (comboConditions[0]?.combo_join_operator || 'or') : 'and'),
      })),
    }));
  }, [createPreviewPhaseProgressionComboId, orderPreviewPhaseProgressionConditionDrafts]);

  const getPreviewPhaseProgressionThresholdLabel = React.useCallback((condition = {}) => {
    const metricType = getPreviewPhaseProgressionMetricType(condition);
    if (metricType === 'pain') return 'Threshold';
    if (metricType === 'function') return 'Threshold';
    return 'Value';
  }, [getPreviewPhaseProgressionMetricType]);

  const seedPreviewPhaseProgressionDraft = React.useCallback((transition) => {
    const sourceRule = transition?.override_rule || transition?.effective_rule || transition?.template_rule || null;
    const seededConditions = Array.isArray(sourceRule?.conditions) ? sourceRule.conditions : [];
    setPreviewPhaseProgressionDraftCombos(orderPreviewPhaseProgressionConditionCombos(seededConditions));
    setPreviewPhaseProgressionEditContext(transition ? {
      sourcePhaseIndex: transition.source_phase_index,
      targetPhaseIndex: transition.target_phase_index,
      transitionKey: `${transition.source_phase_index}->${transition.target_phase_index}`,
    } : null);
  }, [orderPreviewPhaseProgressionConditionCombos]);

  const updatePreviewPhaseProgressionDraftCondition = React.useCallback((comboLocalId, conditionIndex, field, value) => {
    setPreviewPhaseProgressionDraftCombos((previousCombos) => previousCombos.map((combo) => {
      if (combo.localId !== comboLocalId) return combo;
      const nextConditions = combo.conditions.map((condition, index) => {
        if (index !== conditionIndex) return condition;
        if (field === 'metric_key') {
          const nextMetric = PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS.find((option) => option.key === value) || PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS[0];
          return {
            ...buildPreviewPhaseProgressionConditionDraft(nextMetric.key, condition),
            metric_key: nextMetric.key,
            operator: nextMetric.default_operator || 'gte',
            combo_index: condition.combo_index ?? 0,
            combo_join_operator: condition.combo_join_operator || 'and',
          };
        }
        if (field === 'threshold_value') {
          return { ...condition, threshold_value: value };
        }
        if (field === 'operator') {
          return { ...condition, operator: value };
        }
        if (field === 'exercise_identifiers_text') {
          return { ...condition, exercise_identifiers_text: value };
        }
        return { ...condition, [field]: value };
      });
      return { ...combo, conditions: nextConditions };
    }));
  }, [PREVIEW_PHASE_PROGRESSION_METRIC_OPTIONS, buildPreviewPhaseProgressionConditionDraft]);

  const addPreviewPhaseProgressionConditionCombo = React.useCallback(() => {
    setPreviewPhaseProgressionDraftCombos((previousCombos) => ([
      ...previousCombos,
      {
        localId: createPreviewPhaseProgressionComboId(),
        combo_join_operator: 'or',
        conditions: [buildPreviewPhaseProgressionConditionDraft('sessions_completed', { combo_join_operator: 'or' })],
      },
    ]));
  }, [buildPreviewPhaseProgressionConditionDraft, createPreviewPhaseProgressionComboId]);

  const addPreviewPhaseProgressionCondition = React.useCallback((comboLocalId) => {
    setPreviewPhaseProgressionDraftCombos((previousCombos) => previousCombos.map((combo) => {
      if (combo.localId !== comboLocalId) return combo;
      return {
        ...combo,
        conditions: [
          ...combo.conditions,
          buildPreviewPhaseProgressionConditionDraft('psfs_score', { combo_join_operator: combo.conditions.length ? 'and' : 'and' }),
        ],
      };
    }));
  }, [buildPreviewPhaseProgressionConditionDraft]);

  const removePreviewPhaseProgressionCondition = React.useCallback((comboLocalId, conditionIndex) => {
    setPreviewPhaseProgressionDraftCombos((previousCombos) => previousCombos.map((combo) => {
      if (combo.localId !== comboLocalId) return combo;
      const nextConditions = combo.conditions.filter((_, index) => index !== conditionIndex);
      return {
        ...combo,
        conditions: nextConditions.length ? nextConditions : [buildPreviewPhaseProgressionConditionDraft('minimum_duration_weeks', {})],
      };
    }));
  }, [buildPreviewPhaseProgressionConditionDraft]);

  const removePreviewPhaseProgressionCombo = React.useCallback((comboLocalId) => {
    setPreviewPhaseProgressionDraftCombos((previousCombos) => {
      const nextCombos = previousCombos.filter((combo) => combo.localId !== comboLocalId);
      return nextCombos.length ? nextCombos : [{
        localId: createPreviewPhaseProgressionComboId(),
        combo_join_operator: 'and',
        conditions: [buildPreviewPhaseProgressionConditionDraft('minimum_duration_weeks', {})],
      }];
    });
  }, [buildPreviewPhaseProgressionConditionDraft, createPreviewPhaseProgressionComboId]);

  const applyPreviewPhaseProgressionDraft = React.useCallback(() => {
    if (!previewPhaseProgressionEditContext) return;
    const flattenedConditions = [];
    previewPhaseProgressionDraftCombos.forEach((combo, comboIndex) => {
      combo.conditions.forEach((condition, conditionIndex) => {
        flattenedConditions.push({
          metric_key: condition.metric_key,
          operator: condition.operator || 'gte',
          threshold_value: condition.threshold_value === '' ? 0 : Number(condition.threshold_value),
          combo_index: comboIndex,
          combo_join_operator: comboIndex === 0 ? 'and' : (combo.combo_join_operator || 'or'),
          exercise_identifiers: condition.exercise_identifiers_text ? String(condition.exercise_identifiers_text).split(',').map((entry) => entry.trim()).filter(Boolean) : undefined,
          kpi_type: condition.kpi_type || '',
          kpi_subtype: condition.kpi_subtype || '',
          question_identifier: condition.question_identifier || '',
        });
      });
    });

    updatePreviewDraft((prev) => {
      const nextProgressionRules = { ...(prev.progression_rules || {}) };
      const nextTransitions = Array.isArray(nextProgressionRules.transitions) ? nextProgressionRules.transitions.map((transition) => {
        if (String(transition.source_phase_index) !== String(previewPhaseProgressionEditContext.sourcePhaseIndex) || String(transition.target_phase_index) !== String(previewPhaseProgressionEditContext.targetPhaseIndex)) {
          return transition;
        }
        return {
          ...transition,
          override_rule: {
            ...(transition.override_rule || {}),
            source_phase_index: previewPhaseProgressionEditContext.sourcePhaseIndex,
            target_phase_index: previewPhaseProgressionEditContext.targetPhaseIndex,
            conditions: flattenedConditions,
          },
          effective_rule: {
            ...(transition.effective_rule || transition.override_rule || {}),
            source_phase_index: previewPhaseProgressionEditContext.sourcePhaseIndex,
            target_phase_index: previewPhaseProgressionEditContext.targetPhaseIndex,
            conditions: flattenedConditions,
          },
        };
      }) : [];
      return {
        ...prev,
        progression_rules: {
          ...nextProgressionRules,
          transitions: nextTransitions,
        },
      };
    });

    setPreviewPhaseProgressionDialogOpen(false);
    setPreviewPhaseProgressionEditContext(null);
  }, [previewPhaseProgressionDraftCombos, previewPhaseProgressionEditContext, updatePreviewDraft]);

  const buildPreviewExerciseFromLibrary = React.useCallback((exercise, fallbackIndex = 1) => ({
    id: exercise?.id || null,
    identifier: exercise?.identifier || null,
    title: exercise?.title || '',
    description: exercise?.description || '',
    sets: exercise?.sets ?? 3,
    repetitions: exercise?.repetitions ?? 10,
    time: exercise?.time ?? 30,
    pause: exercise?.pause ?? 15,
    frequencyType: exercise?.frequencyType || exercise?.frequency?.frequencyType || 'daily',
    frequency: exercise?.frequency?.frequency ?? exercise?.frequency ?? 1,
    index: exercise?.index ?? fallbackIndex,
    exerciseType: exercise?.exerciseType || 'custom',
    exerciseLevel: exercise?.exerciseLevel ?? 1,
    picture: exercise?.picture || exercise?.img || null,
    video: exercise?.video || exercise?.video_url || null,
    equipment: exercise?.equipment || '',
    bodyRegion: exercise?.bodyRegion || [],
    doseType: exercise?.doseType || ((exercise?.time && exercise.time !== 0) ? 'time' : 'repetitions'),
  }), []);

  const openPreviewExerciseLibraryDrawer = React.useCallback(async (context = {}) => {
    setPreviewLibraryMode(context.mode || 'replace');
    setPreviewLibraryContext(context);
    setPreviewExerciseLibraryOpen(true);
    setPreviewLibraryExercisesError('');
    if (previewLibraryExercises.length > 0) {
      return;
    }
    setPreviewLibraryExercisesLoading(true);
    try {
      const headers = {
        Authorization: `Token ${userToken}`,
        Accept: 'application/json',
      };
      const response = await fetch(`${apiBaseUrl}/treatment/exercise/`, { headers });
      const payload = await response.json().catch(() => ([]));
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.exercises)
          ? payload.exercises
          : Array.isArray(payload?.all_exercises)
            ? payload.all_exercises
            : [];
      setPreviewLibraryExercises(items);
    } catch (error) {
      setPreviewLibraryExercisesError(error.message || 'Failed to load exercises.');
    } finally {
      setPreviewLibraryExercisesLoading(false);
    }
  }, [previewLibraryExercises.length, userToken]);

  const closePreviewExerciseLibraryDrawer = React.useCallback(() => {
    setPreviewExerciseLibraryOpen(false);
    setPreviewLibraryContext(null);
  }, []);

  const openPreviewAdviceLibraryDrawer = React.useCallback((context = {}) => {
    setPreviewLibraryMode(context.mode || 'replace');
    setPreviewLibraryContext(context);
    setPreviewAdviceLibraryOpen(true);
  }, []);

  const closePreviewAdviceLibraryDrawer = React.useCallback(() => {
    setPreviewAdviceLibraryOpen(false);
    setPreviewLibraryContext(null);
  }, []);

  const applyPreviewExerciseSelection = React.useCallback((selectedExercise) => {
    if (!selectedExercise) return;
    const nextExercise = buildPreviewExerciseFromLibrary(selectedExercise);
    updatePreviewDraft((prev) => {
      const nextPhases = prev.phases.map((phase, phaseIndex) => {
        if (phaseIndex !== previewLibraryContext?.phaseIndex) return phase;
        const currentExercises = Array.isArray(phase.exercises) ? phase.exercises.slice() : [];
        const targetIndex = Number.isInteger(previewLibraryContext?.exerciseIndex) ? previewLibraryContext.exerciseIndex : currentExercises.length;
        if (previewLibraryMode === 'replace' && currentExercises[targetIndex]) {
          currentExercises[targetIndex] = {
            ...currentExercises[targetIndex],
            ...nextExercise,
            index: currentExercises[targetIndex].index ?? targetIndex + 1,
          };
        } else {
          currentExercises.splice(Math.max(0, targetIndex + (previewLibraryMode === 'replace' ? 0 : 1)), 0, {
            ...nextExercise,
            index: currentExercises.length + 1,
          });
        }
        return {
          ...phase,
          exercises: currentExercises.map((exercise, index) => ({ ...exercise, index: index + 1 })),
        };
      });
      return { ...prev, phases: nextPhases };
    });
    closePreviewExerciseLibraryDrawer();
  }, [buildPreviewExerciseFromLibrary, closePreviewExerciseLibraryDrawer, previewLibraryContext, previewLibraryMode, updatePreviewDraft]);

  const applyPreviewAdviceSelection = React.useCallback((selectedAdvice) => {
    if (!selectedAdvice) return;
    const nextAdvice = buildPreviewAdviceParams(selectedAdvice);
    updatePreviewDraft((prev) => {
      const nextPhases = prev.phases.map((phase, phaseIndex) => {
        if (phaseIndex !== previewLibraryContext?.phaseIndex) return phase;
        const currentAdvices = Array.isArray(phase.advices) ? phase.advices.slice() : [];
        const targetIndex = Number.isInteger(previewLibraryContext?.adviceIndex) ? previewLibraryContext.adviceIndex : currentAdvices.length;
        if (previewLibraryMode === 'replace' && currentAdvices[targetIndex]) {
          currentAdvices[targetIndex] = {
            ...currentAdvices[targetIndex],
            ...nextAdvice,
          };
        } else {
          currentAdvices.splice(Math.max(0, targetIndex + (previewLibraryMode === 'replace' ? 0 : 1)), 0, {
            ...nextAdvice,
            importance: currentAdvices.length + 1,
          });
        }
        return {
          ...phase,
          advices: currentAdvices.map((advice, index) => ({ ...advice, importance: index + 1 })),
        };
      });
      return { ...prev, phases: nextPhases };
    });
    closePreviewAdviceLibraryDrawer();
  }, [buildPreviewAdviceParams, closePreviewAdviceLibraryDrawer, previewLibraryContext, previewLibraryMode, updatePreviewDraft]);

  const removePreviewExercise = React.useCallback((phaseIndex, exerciseIndex) => {
    updatePreviewDraft((prev) => {
      const nextPhases = prev.phases.map((phase, currentPhaseIndex) => {
        if (currentPhaseIndex !== phaseIndex) return phase;
        return {
          ...phase,
          exercises: (phase.exercises || []).filter((_, index) => index !== exerciseIndex),
        };
      });
      return { ...prev, phases: nextPhases };
    });
  }, [updatePreviewDraft]);

  const removePreviewAdvice = React.useCallback((phaseIndex, adviceIndex) => {
    updatePreviewDraft((prev) => {
      const nextPhases = prev.phases.map((phase, currentPhaseIndex) => {
        if (currentPhaseIndex !== phaseIndex) return phase;
        return {
          ...phase,
          advices: (phase.advices || []).filter((_, index) => index !== adviceIndex),
        };
      });
      return { ...prev, phases: nextPhases };
    });
  }, [updatePreviewDraft]);

  const openPreviewExerciseEditDialog = React.useCallback((phaseIndex, exerciseIndex, exercise) => {
    setPreviewExerciseEditContext({ phaseIndex, exerciseIndex });
    setPreviewEditedExerciseParams(buildPreviewExerciseParams(exercise));
    setPreviewExerciseDialogOpen(true);
  }, [buildPreviewExerciseParams]);

  const closePreviewExerciseDialog = React.useCallback(() => {
    setPreviewExerciseDialogOpen(false);
    setPreviewExerciseEditContext(null);
    setPreviewEditedExerciseParams({});
  }, []);

  const handlePreviewExerciseParamChange = React.useCallback((field, value) => {
    setPreviewEditedExerciseParams((prev) => {
      if (field === 'time' || field === 'pause' || field === 'frequency' || field === 'index') {
        return { ...prev, [field]: parseInt(value, 10) || 0 };
      }
      if (field === 'doseType') {
        return value === 'repetitions'
          ? { ...prev, doseType: value, time: 0 }
          : { ...prev, doseType: value, repetitions: 0 };
      }
      if (field === 'frequencyType') {
        return { ...prev, frequencyType: value };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const handleConfirmPreviewExerciseEdit = React.useCallback(() => {
    if (!previewExerciseEditContext) return;
    const { phaseIndex, exerciseIndex } = previewExerciseEditContext;
    setDiagnosisPreviewDraft((prev) => {
      if (!prev || !Array.isArray(prev.phases)) return prev;
      const nextPhases = prev.phases.map((phase, currentPhaseIndex) => {
        if (currentPhaseIndex !== phaseIndex) return phase;
        const nextExercises = Array.isArray(phase.exercises)
          ? phase.exercises.map((exercise, currentExerciseIndex) => {
            if (currentExerciseIndex !== exerciseIndex) return exercise;
            return {
              ...exercise,
              title: previewEditedExerciseParams.title ?? exercise.title,
              description: previewEditedExerciseParams.description ?? exercise.description,
              sets: previewEditedExerciseParams.sets,
              repetitions: previewEditedExerciseParams.repetitions,
              time: previewEditedExerciseParams.time,
              pause: previewEditedExerciseParams.pause,
              frequencyType: previewEditedExerciseParams.frequencyType,
              frequency: previewEditedExerciseParams.frequency,
              picture: previewEditedExerciseParams.img ?? exercise.picture,
              img: previewEditedExerciseParams.img ?? exercise.img,
              video: previewEditedExerciseParams.video ?? exercise.video,
              video_url: previewEditedExerciseParams.video ?? exercise.video_url,
              doseType: previewEditedExerciseParams.doseType ?? exercise.doseType,
            };
          })
          : [];
        return { ...phase, exercises: nextExercises };
      });
      return { ...prev, phases: nextPhases };
    });
    closePreviewExerciseDialog();
  }, [closePreviewExerciseDialog, previewEditedExerciseParams, previewExerciseEditContext]);

  const openPreviewAdviceEditDialog = React.useCallback((phaseIndex, adviceIndex, advice) => {
    setPreviewAdviceEditContext({ phaseIndex, adviceIndex });
    setPreviewEditedAdviceParams(buildPreviewAdviceParams(advice));
    setPreviewAdviceDialogOpen(true);
  }, [buildPreviewAdviceParams]);

  const openPreviewPhaseProgressionEditDialog = React.useCallback((phaseIndex, transition) => {
    seedPreviewPhaseProgressionDraft(transition);
    setPreviewPhaseProgressionEditContext({
      phaseIndex,
      sourcePhaseIndex: transition?.source_phase_index,
      targetPhaseIndex: transition?.target_phase_index,
      transitionKey: transition ? `${transition.source_phase_index}->${transition.target_phase_index}` : '',
    });
    setPreviewPhaseProgressionDialogOpen(true);
  }, [seedPreviewPhaseProgressionDraft]);

  const closePreviewPhaseProgressionEditDialog = React.useCallback(() => {
    setPreviewPhaseProgressionDialogOpen(false);
    setPreviewPhaseProgressionEditContext(null);
  }, []);

  const closePreviewAdviceDialog = React.useCallback(() => {
    setPreviewAdviceDialogOpen(false);
    setPreviewAdviceEditContext(null);
    setPreviewEditedAdviceParams({
      title: '',
      advice: '',
      importance: 1,
    });
  }, []);

  const handlePreviewAdviceParamChange = React.useCallback((field, value) => {
    setPreviewEditedAdviceParams((prev) => ({
      ...prev,
      [field]: field === 'importance' ? (parseInt(value, 10) || 1) : value,
    }));
  }, []);

  const handleConfirmPreviewAdviceEdit = React.useCallback(() => {
    if (!previewAdviceEditContext) return;
    const { phaseIndex, adviceIndex } = previewAdviceEditContext;
    setDiagnosisPreviewDraft((prev) => {
      if (!prev || !Array.isArray(prev.phases)) return prev;
      const nextPhases = prev.phases.map((phase, currentPhaseIndex) => {
        if (currentPhaseIndex !== phaseIndex) return phase;
        const nextAdvices = Array.isArray(phase.advices)
          ? phase.advices.map((advice, currentAdviceIndex) => {
            if (currentAdviceIndex !== adviceIndex) return advice;
            return {
              ...advice,
              title: previewEditedAdviceParams.title ?? advice.title,
              advice: previewEditedAdviceParams.advice ?? advice.advice,
              importance: previewEditedAdviceParams.importance,
            };
          })
          : [];
        return { ...phase, advices: nextAdvices };
      });
      return { ...prev, phases: nextPhases };
    });
    closePreviewAdviceDialog();
  }, [closePreviewAdviceDialog, previewAdviceEditContext, previewEditedAdviceParams]);

  const activeDiagnosisPreview = diagnosisPreviewDraft || diagnosisPreviewData;
  const previewExerciseToEdit = previewExerciseEditContext && activeDiagnosisPreview && Array.isArray(activeDiagnosisPreview.phases)
    ? (activeDiagnosisPreview.phases[previewExerciseEditContext.phaseIndex] && Array.isArray(activeDiagnosisPreview.phases[previewExerciseEditContext.phaseIndex].exercises)
      ? activeDiagnosisPreview.phases[previewExerciseEditContext.phaseIndex].exercises[previewExerciseEditContext.exerciseIndex] || null
      : null)
    : null;
  const previewAdviceToEdit = previewAdviceEditContext && activeDiagnosisPreview && Array.isArray(activeDiagnosisPreview.phases)
    ? (activeDiagnosisPreview.phases[previewAdviceEditContext.phaseIndex] && Array.isArray(activeDiagnosisPreview.phases[previewAdviceEditContext.phaseIndex].advices)
      ? activeDiagnosisPreview.phases[previewAdviceEditContext.phaseIndex].advices[previewAdviceEditContext.adviceIndex] || null
      : null)
    : null;
  const previewPhaseProgressionToEdit = previewPhaseProgressionEditContext && activeDiagnosisPreview && Array.isArray(activeDiagnosisPreview.phases) && activeDiagnosisPreview.progression_rules && Array.isArray(activeDiagnosisPreview.progression_rules.transitions)
    ? activeDiagnosisPreview.progression_rules.transitions.find((transition) => String(transition.source_phase_index) === String(previewPhaseProgressionEditContext.sourcePhaseIndex) && String(transition.target_phase_index) === String(previewPhaseProgressionEditContext.targetPhaseIndex)) || null
    : null;

  async function loadDiagnosisProgramPreview(diagId) {
    if (!diagId || !selectedPatient || !userToken) return;
    setDiagnosisPreviewLoading(true);
    setDiagnosisPreviewError('');
    setDiagnosisPreviewData(null);
    setDiagnosisPreviewDraft(null);
    try {
      const params = selectedPatient && selectedPatient.user_id ? `?user_id=${selectedPatient.user_id}` : '';
      const response = await fetch(`${apiBaseUrl}/treatment/diagnoses/${diagId}/treatment-preview/${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${userToken}`,
          'Accept': 'application/json',
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load treatment preview.');
      }
      setDiagnosisPreviewData(payload || null);
      setDiagnosisPreviewDraft(cloneDiagnosisPreviewPayload(payload || null));
    } catch (error) {
      setDiagnosisPreviewError(error.message || 'Failed to load treatment preview.');
    } finally {
      setDiagnosisPreviewLoading(false);
    }
  }

  const openTemplatePreviewDialog = React.useCallback(async () => {
    if (!selectedTemplate) return;
    setShowTemplateAutoGenDialog(true);
    await loadDiagnosisProgramPreview(selectedTemplate);
  }, [selectedTemplate]);

  // Handler to confirm diagnosis change and optionally auto-generate treatment
  const handleConfirmDiagnosisChange = async (autoGenerate) => {
    try {
      if (!pendingDiagnosisChange) return;
      
      const change = pendingDiagnosisChange;
      setShowAutoGenDialog(false);
      setPendingDiagnosisChange(null);
      setDiagnosisPreviewData(null);
      setDiagnosisPreviewDraft(null);
      setDiagnosisPreviewError('');
      setDiagnosisPreviewLoading(false);

      if (change.type === 'existing') {
        // Existing diagnosis selected
        const headers = { 'Authorization': `Token ${userToken}`, 'Content-Type': 'application/json', 'Accept': 'application/json' };
        const treatmentId = props.treatmentId || (selectedPatient && selectedPatient.treatment_id) || null;
        const body = JSON.stringify({ 
          user_id: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null, 
          ...(treatmentId ? { treatment_id: treatmentId } : {}),
          diagnosis_id: change.diagId, 
          reason: change.reason || '',
          auto_generate_treatment: autoGenerate,
          ...(autoGenerate && diagnosisPreviewDraft ? { program_preview: diagnosisPreviewDraft } : {}),
        });
        const res = await fetch(`${apiBaseUrl}/treatment/treatments/change_diagnosis/`, { method: 'POST', headers, body });
        if (!res.ok) {
          const errText = await res.text();
          console.error('Failed to persist diagnosis change', res.status, errText);
          alert('Failed to save diagnosis change.');
        } else {
          const jr = await res.json();
          // fetch diagnosis detail to display
          const headers2 = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
          const diagUrl2 = `${apiBaseUrl}/treatment/diagnoses/${change.diagId}/` + (selectedPatient && selectedPatient.user_id ? (`?user_id=${selectedPatient.user_id}`) : '');
          const r2 = await fetch(diagUrl2, { headers: headers2, method: 'GET' });
          if (r2.ok) {
            const data = await r2.json();
            setAppliedDiagnosisIfChanged(data);
            const _diagStr = data.title || (typeof data.diagnosis === 'string' ? data.diagnosis : (data.diagnosis && data.diagnosis.title) || '') || '';
            setEditedDiagnosis(_diagStr);
          }
          setApproveState('approved');
          setDiagnosisSelect('');
          setDiagnosisInput('');
          setDiagnosisSearchInput('');
          setDiagnosisReason('');
          
          if (autoGenerate) {
            alert('Diagnosis changed and default treatment program generated successfully.');
          } else {
            alert('Diagnosis changed successfully.');
          }
          
          // Refetch all patient data to show the changes immediately
          // Add small delay to ensure backend has finished creating phases/advices
          await new Promise(resolve => setTimeout(resolve, 500));
          
          try {
            await fetchJournal();
            // Refetch phases
            if (selectedPatient && selectedPatient.user_id) {
              const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
              const phasesParams = new URLSearchParams();
              if (selectedPatient.treatment_id) {
                phasesParams.append('treatment_id', selectedPatient.treatment_id);
              }
              const phasesUrl = `${apiBaseUrl}/treatment/phases/patient/${selectedPatient.user_id}/?${phasesParams.toString()}`;
              const phasesRes = await fetch(phasesUrl, { headers });
              if (phasesRes.ok) {
                const phasesData = await phasesRes.json();
                setPatientPhases(phasesData.phases || []);
              } else {
                const errText = await phasesRes.text();
                console.error('Failed to fetch phases:', phasesRes.status, errText);
              }
              // Refetch advices
              const advicesParams = new URLSearchParams();
              if (selectedPatient.treatment_id) {
                advicesParams.append('treatment_id', selectedPatient.treatment_id);
              }
              const advicesUrl = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/?${advicesParams.toString()}`;
              const advicesRes = await fetch(advicesUrl, { headers });
              if (advicesRes.ok) {
                const advicesData = await advicesRes.json();
                setPatientAdvices(advicesData.advices || advicesData.all_advices || []);
              } else {
                const errText = await advicesRes.text();
                console.error('Failed to fetch advices:', advicesRes.status, errText);
              }
            }
            
            // Dispatch event to notify other components (e.g., selectedPatientProgram) to refresh
            window.dispatchEvent(new CustomEvent('diagnosisChanged', { 
              detail: { 
                userId: selectedPatient?.user_id,
                diagnosisId: change.diagId,
                autoGenerated: autoGenerate
              } 
            }));
            
            // Call parent callback to trigger data refetch
            if (props.onDiagnosisChanged) {
              props.onDiagnosisChanged({
                userId: selectedPatient?.user_id,
                diagnosisId: change.diagId,
                autoGenerated: autoGenerate
              });
            }
          } catch (e) {
            console.error('Error refetching data after diagnosis change:', e);
          }
        }
      } else if (change.type === 'new') {
        // New diagnosis creation
        const headers = { 'Authorization': `Token ${userToken}`, 'Content-Type': 'application/json', 'Accept': 'application/json' };
        const treatmentId = props.treatmentId || (selectedPatient && selectedPatient.treatment_id) || null;
        const body = JSON.stringify({ 
          user_id: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null, 
          ...(treatmentId ? { treatment_id: treatmentId } : {}),
          diagnosis_title: change.diagnosisTitle, 
          indication_for_physiotherapy: change.indication, 
          prognosis: change.prognosis, 
          reason: change.reason || '',
          auto_generate_treatment: autoGenerate
        });
        const res = await fetch(`${apiBaseUrl}/treatment/treatments/change_diagnosis/`, { method: 'POST', headers, body });
        if (!res.ok) {
          const errText = await res.text();
          console.error('Failed to create and persist diagnosis', res.status, errText);
          alert('Failed to save new diagnosis.');
        } else {
          const jr = await res.json();
          // fetch diagnosis detail to display
          const newDiagId = jr && jr.new_diagnosis_id ? jr.new_diagnosis_id : null;
          if (newDiagId) {
            const headers2 = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
            const diagUrl3 = `${apiBaseUrl}/treatment/diagnoses/${newDiagId}/` + (selectedPatient && selectedPatient.user_id ? (`?user_id=${selectedPatient.user_id}`) : '');
            const r2 = await fetch(diagUrl3, { headers: headers2, method: 'GET' });
            if (r2.ok) {
              const data = await r2.json();
              setAppliedDiagnosisIfChanged(data);
              const _diagStr2 = data.title || (typeof data.diagnosis === 'string' ? data.diagnosis : (data.diagnosis && data.diagnosis.title) || '') || '';
              setEditedDiagnosis(_diagStr2);
            }
          } else {
            // fallback: show typed text
            setEditedDiagnosis(change.diagnosisTitle);
          }
          setApproveState('approved');
          setDiagnosisSelect('');
          setDiagnosisInput('');
          setDiagnosisSearchInput('');
          setDiagnosisReason('');
          setDiagnosisIndication('');
          setPrognosisExpectedOutcome('');
          setPrognosisTypicalSymptoms('');
          setPrognosisAverageDuration('');
          setPrognosisDevelopment('');
          setPrognosisIfWorsens('');
          
          if (autoGenerate) {
            alert('New diagnosis created and default treatment program generated successfully.');
          } else {
            alert('New diagnosis created successfully.');
          }
          
          // Refetch all patient data to show the changes immediately
          // Add small delay to ensure backend has finished creating phases/advices
          await new Promise(resolve => setTimeout(resolve, 500));
          
          try {
            await fetchJournal();
            // Refetch phases
            if (selectedPatient && selectedPatient.user_id) {
              const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
              const phasesParams = new URLSearchParams();
              if (selectedPatient.treatment_id) {
                phasesParams.append('treatment_id', selectedPatient.treatment_id);
              }
              const phasesUrl = `${apiBaseUrl}/treatment/phases/patient/${selectedPatient.user_id}/?${phasesParams.toString()}`;
              const phasesRes = await fetch(phasesUrl, { headers });
              if (phasesRes.ok) {
                const phasesData = await phasesRes.json();
                setPatientPhases(phasesData.phases || []);
              } else {
                const errText = await phasesRes.text();
                console.error('Failed to fetch phases:', phasesRes.status, errText);
              }
              // Refetch advices
              const advicesParams = new URLSearchParams();
              if (selectedPatient.treatment_id) {
                advicesParams.append('treatment_id', selectedPatient.treatment_id);
              }
              const advicesUrl = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/?${advicesParams.toString()}`;
              const advicesRes = await fetch(advicesUrl, { headers });
              if (advicesRes.ok) {
                const advicesData = await advicesRes.json();
                setPatientAdvices(advicesData.advices || advicesData.all_advices || []);
              } else {
                const errText = await advicesRes.text();
                console.error('Failed to fetch advices:', advicesRes.status, errText);
              }
            }
            
            // Dispatch event to notify other components (e.g., selectedPatientProgram) to refresh
            window.dispatchEvent(new CustomEvent('diagnosisChanged', { 
              detail: { 
                userId: selectedPatient?.user_id,
                diagnosisId: newDiagId,
                autoGenerated: autoGenerate
              } 
            }));
            
            // Call parent callback to trigger data refetch
            if (props.onDiagnosisChanged) {
              props.onDiagnosisChanged({
                userId: selectedPatient?.user_id,
                diagnosisId: newDiagId,
                autoGenerated: autoGenerate
              });
            }
          } catch (e) {
            console.error('Error refetching data after diagnosis change:', e);
          }
        }
      }
    } catch (e) {
      console.error('Error confirming diagnosis change:', e);
      alert('An error occurred while saving the diagnosis change.');
    }
  };

  const applySelectedTemplate = React.useCallback(async () => {
    try {
      setShowTemplateAutoGenDialog(false);
      const templateDiagId = selectedTemplate;
      const previewPayload = diagnosisPreviewDraft || diagnosisPreviewData || null;
      setSelectedTemplate("");
      setShowTemplateSelector(false);

      if (!templateDiagId) return;

      const headers = { 'Authorization': `Token ${userToken}`, 'Content-Type': 'application/json', 'Accept': 'application/json' };
      const treatmentId = props.treatmentId || (selectedPatient && selectedPatient.treatment_id) || null;

      const body = JSON.stringify({
        user_id: selectedPatient && selectedPatient.user_id ? selectedPatient.user_id : null,
        ...(treatmentId ? { treatment_id: treatmentId } : {}),
        template_diagnosis_id: templateDiagId,
        apply_template_only: true,
        ...(previewPayload ? { program_preview: previewPayload } : {}),
      });

      const res = await fetch(`${apiBaseUrl}/treatment/treatments/apply_template/`, { method: 'POST', headers, body });

      if (!res.ok) {
        const errText = await res.text();
        console.error('Failed to apply template', res.status, errText);
        alert('Failed to apply template.');
      } else {
        alert('Treatment template applied successfully.');

        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          await fetchJournal();

          window.dispatchEvent(new CustomEvent('diagnosisChanged', {
            detail: {
              userId: selectedPatient?.user_id,
              templateApplied: true
            }
          }));

          if (props.onDiagnosisChanged) {
            props.onDiagnosisChanged({
              userId: selectedPatient?.user_id,
              templateApplied: true
            });
          }
        } catch (e) {
          console.error('Error refetching data after template apply:', e);
        }
      }
    } catch (err) {
      console.error('Template application error:', err);
      alert('Error applying template.');
    }
  }, [diagnosisPreviewData, diagnosisPreviewDraft, selectedTemplate, userToken, props.treatmentId, selectedPatient, fetchJournal, props.onDiagnosisChanged]);

  const requestTemplateApplication = React.useCallback(() => {
    requestUnsavedJournalAction('applying the treatment template', async () => {
      await applySelectedTemplate();
    });
  }, [requestUnsavedJournalAction, applySelectedTemplate]);

  // Fetch diagnosis list when entering change mode or opening template selector
  React.useEffect(() => {
    const fetchDiagnoses = async () => {
      try {
        if (!userToken || (typeof userToken === 'string' && userToken.trim() === '')) return;
        const headers = { 'Authorization': `Token ${userToken}`, 'Accept': 'application/json' };
        const res = await fetch(`${apiBaseUrl}/treatment/diagnoses/`, { headers, method: 'GET' });
        if (!res.ok) return;
        const data = await res.json();
        if (data && Array.isArray(data.diagnoses)) setDiagnosesList(data.diagnoses);
      } catch (e) { console.error('Failed to load diagnoses', e); }
    };
    if ((approveState === 'change' || showTemplateSelector) && (!diagnosesList || diagnosesList.length === 0)) {
      fetchDiagnoses();
    }
  }, [approveState, showTemplateSelector, userToken, diagnosesList]);

  const [subGoals, setSubGoals] = React.useState([
    "Limited rotation the left",
    "Difficulty looking upwards and backwards",
    "Neck discomfort and pain when laying on the side",
    
  ]);
  const [subGoalValues, setSubGoalValues] = React.useState([0, 0, 0]);
  const [symptoms, setSymptoms] = React.useState([
    "Stiffness or tension in neck",
    "Retraction exercises, both alone and combined with extension, result in symptom improvement."
    
  ]);
  const [flags, setFlags] = React.useState([
    "No red flags identified.",
    
  ]);

  const [Rx, setRx] = React.useState([
    "Virtual physiotherapy self-management trial for 2 weeks. ",
    "Refer for in-person assessment if no improvement or worsening of symptoms."
    
  ]);

  const [symptomValues, setSymptomValues] = React.useState([0, 0, 0]);
  const [newSubGoal, setNewSubGoal] = React.useState("");
  const [newSymptom, setNewSymptom] = React.useState("");
  const [celebrateOpen, setCelebrateOpen] = React.useState(false);
  const [celebrateMsg, setCelebrateMsg] = React.useState("");
    
  const handleSubGoalChange = (idx, val) => {
    const newVals = [...subGoalValues];
    newVals[idx] = val;
    setSubGoalValues(newVals);
    if (val === 100) {
      setCelebrateMsg(`Sub-goal achieved: ${subGoals[idx]}! 🎉`);
      setCelebrateOpen(true);
    }
  };

  const handleSymptomChange = (idx, val) => {
    const newVals = [...symptomValues];
    newVals[idx] = val;
    setSymptomValues(newVals);
    if (val === 100) {
      setCelebrateMsg(`Symptom improvement: ${symptoms[idx]}! 🎉`);
      setCelebrateOpen(true);
    }
  };

  const handleAddSubGoal = () => {
    if (newSubGoal.trim()) {
      setSubGoals([...subGoals, newSubGoal.trim()]);
      setSubGoalValues([...subGoalValues, 0]);
      setNewSubGoal("");
    }
  };

  const handleAddSymptom = () => {
    if (newSymptom.trim()) {
      setSymptoms([...symptoms, newSymptom.trim()]);
      setSymptomValues([...symptomValues, 0]);
      setNewSymptom("");
    }
  };

  React.useEffect(() => {
      // do not load sub-goal or symptom persisted values from localStorage; rely on backend
    }, []);

  // Per-group draft inputs for empty groups (so user can type a note below group name)
  const [groupDrafts, setGroupDrafts] = React.useState({}); // { [groupName]: draftText }
  const excludedGroupsForDraft = new Set([
    'Evaluation and indication for physiotherapy:',
    'Treatmentplan',
    'Initial Advice',
    'Information to the patient'
  ]);

  // Reset groupDrafts when entering edit mode
  React.useEffect(() => {
    if (globalEditMode) {
      setGroupDrafts({});
    }
  }, [globalEditMode]);

  const handleGroupDraftChange = (groupName, value) => {
    setGroupDrafts(prev => ({ ...prev, [groupName]: value }));
  };

  const handleGroupDraftBlur = async (groupName, groupKeys) => {
    try {
      const draft = (groupDrafts && groupDrafts[groupName]) ? String(groupDrafts[groupName]).trim() : '';
      if (!draft) return;
      // Create one manual item under the first mapped theme key for this group
      let themeKey = Array.isArray(groupKeys) && groupKeys.length ? groupKeys[0] : null;
      // Fallback: try high-level mapping if GROUPING_MAP didn't include this display group
      if (!themeKey) {
        const highLevelFallback = {
          'Primary complaint and cause of contact:': ['keySymptom','painAnamnesis','painAnamnesis_cause_how_issue_occured','painAnamnesis_duration','painAnamnesis_doctor','painAnamnesis_doctorDiagnosis','secondary_multimorbid_doctorDiagnosis'],
          'Location and character of symptoms:': ['painAnamnesis_location','painAnamnesis_knownCondition','painAnamnesis_painCharacteristics'],
          'Affected activities, pattern and development of symptoms:': ['painAnamnesis_pattern','painAnamnesis_affectedActivities','painAnamnesis_episodes','painAnamnesis_development'],
          'Pain baseline levels:': ['painAnamnesis_frequency','painAnamnesis_painAtBest','painAnamnesis_painCurrent','painAnamnesis_painAtWorst','painAnamnesis_painAtRest','painAnamnesis_painWithActivity','painAnamnesis_difficulty','painAnamnesis_custom','painAnamnesis_function'],
          'Patient goal for treatment:': ['painAnamnesis_SelfRatedFunction_MainGoal'],
          'Redflags:': ['redflags'],
          'Movement tests': ['movement_test'],
          'Symptom response on clinical tests': ['clinicalTest','SymptomProvoking_clinicalTest','SymptomImproving_clinicalTest','keyFunctionalLimitations']
        };
        if (highLevelFallback[groupName] && highLevelFallback[groupName].length) themeKey = highLevelFallback[groupName][0];
      }
      if (!themeKey) return;

      // derive theme from mapping key strings like 'painAnamnesis_painAtBest'
      // but use subgroup 'manual' for user-created group drafts so they render last
      let theme = themeKey;
      let subgroup = 'manual';
      if (typeof themeKey === 'string' && themeKey.includes('_')) {
        const idx = themeKey.indexOf('_');
        theme = themeKey.substring(0, idx) || themeKey;
        // record original subgroup mapping via mapped_key but keep subgroup as 'manual'
      }

      // create a stable manual id and immediately show local item
      const manualId = `manual-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      setJournalSections(prev => {
        const clone = prev ? JSON.parse(JSON.stringify(prev)) : {};
        if (!clone[theme]) clone[theme] = {};
        if (!Array.isArray(clone[theme][subgroup])) clone[theme][subgroup] = [];
        const item = makeManualItem(draft, theme, subgroup, themeKey);
        item.id = manualId;
        clone[theme][subgroup].push(item);
        return clone;
      });
      // prepare local editable state so user can continue editing immediately
      setEditingItems(prev => ({ ...(prev || {}), [manualId]: true }));
      setEditableValues(prev => ({ ...(prev || {}), [manualId]: { metaJournalPhrase: draft, journalPhrase: '', numerical_value: '', theme, subgroup } }));
      setGlobalEditMode(true);

      // persist to backend as an edit (create immutable journal version)
      if (selectedPatient && selectedPatient.user_id) {
        const editPayload = [{ id: manualId, theme: theme, subgroup: subgroup, metaJournalPhrase: draft, journalPhrase: '', numerical_value: '', mapped_key: themeKey }];
        try {
          const body = { user_id: selectedPatient.user_id, payload: { edits: editPayload }, note: 'Added manual note from UI' };
          const res = await postJournalVersion(body);
          if (res && res.id) {
            // refresh canonical data after small delay to ensure backend applied changes
            try { await fetchJournalDelayed(1000); } catch (e) { /* ignore */ }
          } else {
            // if persist failed, leave the local item but inform console
            console.error('Persisting manual group note failed', res);
          }
        } catch (e) {
          console.error('Error persisting manual group note', e);
        }
      }

      // clear draft input after attempting persist
      setGroupDrafts(prev => { const n = { ...(prev || {}) }; delete n[groupName]; return n; });
    } catch (e) {
      console.error('handleGroupDraftBlur', e);
    }
  };

  // Per-group extra editable fields (shown in edit mode at end of each group)
  // structure: { [groupName]: [manualId, ...] }
  const [groupExtraFields, setGroupExtraFields] = React.useState({});
  const [tooltipOpen, setTooltipOpen] = React.useState({});
  const tooltipTimeoutsRef = React.useRef({});

  const ensureGroupHasField = (groupName, groupKeys) => {
    try {
      const existing = groupExtraFields[groupName] || [];
      if (existing.length) return existing[existing.length - 1];
      // create one field
      const nid = `manual-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      // derive theme/subgroup from first mapping key
      let themeKey = Array.isArray(groupKeys) && groupKeys.length ? groupKeys[0] : null;
      // fallback to high-level grouping if groupKeys missing
      if (!themeKey) {
        const highLevelFallback = {
          'Primary complaint and cause of contact:': ['keySymptom','painAnamnesis','painAnamnesis_cause_how_issue_occured','painAnamnesis_duration','painAnamnesis_doctor','painAnamnesis_doctorDiagnosis','secondary_multimorbid_doctorDiagnosis'],
          'Location and character of symptoms:': ['painAnamnesis_location','painAnamnesis_knownCondition','painAnamnesis_painCharacteristics'],
          'Affected activities, pattern and development of symptoms:': ['painAnamnesis_pattern','painAnamnesis_affectedActivities','painAnamnesis_episodes','painAnamnesis_development'],
          'Pain baseline levels:': ['painAnamnesis_frequency','painAnamnesis_painAtBest','painAnamnesis_painCurrent','painAnamnesis_painAtWorst','painAnamnesis_function', 'painAnamnesis_painAtRest','painAnamnesis_difficulty','painAnamnesis_custom','painAnamnesis_function'],
          'Patient goal for treatment:': ['painAnamnesis_SelfRatedFunction_MainGoal'],
          'Redflags:': ['redflags'],
          'Movement tests': ['movement_test'],
          'Symptom response on clinical tests': ['clinicalTest','SymptomProvoking_clinicalTest','SymptomImproving_clinicalTest','keyFunctionalLimitations']
        };
        if (highLevelFallback[groupName] && highLevelFallback[groupName].length) themeKey = highLevelFallback[groupName][0];
      }
      let theme = themeKey || '';
      let subgroup = 'default';
      if (typeof themeKey === 'string' && themeKey.includes('_')) {
        const idx = themeKey.indexOf('_');
        theme = themeKey.substring(0, idx) || themeKey;
        subgroup = themeKey.substring(idx + 1) || 'default';
      }
     // ensure item is present in journalSections so findThemeSubgroupForId can find it
    setJournalSections(prev => {
      const clone = prev ? JSON.parse(JSON.stringify(prev)) : {};
      if (!clone[theme]) clone[theme] = {};
      if (!Array.isArray(clone[theme][subgroup])) clone[theme][subgroup] = [];
      const item = makeManualItem('', theme, subgroup, themeKey || '');
      item.id = nid;
      clone[theme][subgroup].push(item);
      return clone;
    });
      // pre-create editableValues entry so the TextField is controlled and saved on Save
      setEditableValues(prev => ({ ...(prev || {}), [nid]: { metaJournalPhrase: '', journalPhrase: '', numerical_value: '', theme, subgroup } }));
      setEditingItems(prev => ({ ...(prev || {}), [nid]: true }));
      setGroupExtraFields(prev => ({ ...(prev || {}), [groupName]: [ ...(prev[groupName] || []), nid ] }));
      return nid;
    } catch (e) { return null; }
  };

  const addGroupField = (groupName, groupKeys, initialValue = '') => {
    const nid = ensureGroupHasField(groupName, groupKeys);
    if (!nid) return null;
    if (initialValue && initialValue.trim()) {
      setEditableValues(prev => ({ ...(prev || {}), [nid]: { ...(prev[nid] || {}), journalPhrase: initialValue } }));
    }
    return nid;
  };

  const appendGroupFieldAfter = (groupName, groupKeys, afterId) => {
    try {
      const nid = `manual-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const themeKey = Array.isArray(groupKeys) && groupKeys.length ? groupKeys[0] : null;
      let theme = themeKey || '';
      let subgroup = 'default';
      if (typeof themeKey === 'string' && themeKey.includes('_')) {
        const idx = themeKey.indexOf('_');
        theme = themeKey.substring(0, idx) || themeKey;
        subgroup = themeKey.substring(idx + 1) || 'default';
      }
      // ensure item is present in journalSections for theme/subgroup inference and persistence
      setJournalSections(prev => {
        const clone = prev ? JSON.parse(JSON.stringify(prev)) : {};
        if (!clone[theme]) clone[theme] = {};
        if (!Array.isArray(clone[theme][subgroup])) clone[theme][subgroup] = [];
        const item = makeManualItem('', theme, subgroup, themeKey || '');
        item.id = nid;
        clone[theme][subgroup].push(item);
        return clone;
      });
      setEditableValues(prev => ({ ...(prev || {}), [nid]: { metaJournalPhrase: '', journalPhrase: '', numerical_value: '', theme, subgroup } }));
      setEditingItems(prev => ({ ...(prev || {}), [nid]: true }));
      setGroupExtraFields(prev => {
        const arr = prev[groupName] ? [...prev[groupName]] : [];
        const idx = arr.indexOf(afterId);
        if (idx === -1) arr.push(nid); else arr.splice(idx + 1, 0, nid);
        return { ...(prev || {}), [groupName]: arr };
      });
      return nid;
    } catch (e) { return null; }
  };

  const removeGroupField = (groupName, id) => {
    try {
      setGroupExtraFields(prev => {
        const arr = prev[groupName] ? prev[groupName].filter(x => x !== id) : [];
        const out = { ...(prev || {}), [groupName]: arr };
        return out;
      });
      setEditingItems(prev => { const n = { ...(prev || {}) }; delete n[id]; return n; });
      setEditableValues(prev => { const n = { ...(prev || {}) }; delete n[id]; return n; });
    } catch (e) {}
  };

  // Helper to extract indication_for_physiotherapy from possible payload shapes
  const getIndicationText = () => {
    // Prefer any locally applied diagnosis (from change) so edits persist in UI
    if (appliedDiagnosis && appliedDiagnosis.indication_for_physiotherapy) return appliedDiagnosis.indication_for_physiotherapy;
    
    // 1) prefer the diagnosis prop passed from parent (may be the serialized object)
    if (diagnosis) {
      // shape: { diagnosis: { ... , indication_for_physiotherapy }, prognosis: {...} }
      if (typeof diagnosis === 'object' && !Array.isArray(diagnosis)) {
        if (diagnosis.indication_for_physiotherapy) return diagnosis.indication_for_physiotherapy;
        if (diagnosis.diagnosis && diagnosis.diagnosis.indication_for_physiotherapy) return diagnosis.diagnosis.indication_for_physiotherapy;
        if (diagnosis.indication) return diagnosis.indication;
        if (diagnosis.indication_for_physio) return diagnosis.indication_for_physio; // alternate field name
      }
      // shape: array of diagnoses
      if (Array.isArray(diagnosis) && diagnosis.length > 0) {
        const d0 = diagnosis[0];
        if (d0.indication_for_physiotherapy) return d0.indication_for_physiotherapy;
        if (d0.diagnosis && d0.diagnosis.indication_for_physiotherapy) return d0.diagnosis.indication_for_physiotherapy;
        if (d0.indication) return d0.indication;
      }
    }

    // 2) fallback to selectedPatient top-level fields
    if (selectedPatient) {
      if (selectedPatient.indication_for_physiotherapy) return selectedPatient.indication_for_physiotherapy;
      // selectedPatient.diagnosis may be an array of serialized diagnoses
      if (Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length > 0) {
        const d0 = selectedPatient.diagnosis[0];
        if (d0.indication_for_physiotherapy) return d0.indication_for_physiotherapy;
        if (d0.diagnosis && d0.diagnosis.indication_for_physiotherapy) return d0.diagnosis.indication_for_physiotherapy;
        if (d0.indication) return d0.indication;
      }
    }

    return '';
  };

  // Format ISO timestamp to readable date/time string
  const formatDate = (iso) => {
    try {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-GB', { 
        year: '2-digit', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (e) { return ''; }
  };

  // Determine initial assessment date from available sources
  const getInitialAssessmentDate = () => {
    // prefer server-provided answered assessment date when available
    if (initialAssessmentDate) return initialAssessmentDate;
    // preferred: selectedPatient fields populated by backend
    if (selectedPatient) {
      if (selectedPatient.assessment_date) return selectedPatient.assessment_date;
      if (selectedPatient.initial_assessment_date) return selectedPatient.initial_assessment_date;
      if (selectedPatient.answered_assessment_date) return selectedPatient.answered_assessment_date;
      if (selectedPatient.answeredAssessmentDate) return selectedPatient.answeredAssessmentDate;
    }
    // fallback: use the earliest journalVersions ts (assumed initial version at index 0)
    if (journalVersions && journalVersions.length > 0 && journalVersions[0] && journalVersions[0].ts) return journalVersions[0].ts;
    return null;
  };
  
    return (
    <>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert elevation={6} variant="filled" severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    <Card
      className={clsx(classes.root, className)}
    >
      <CardHeader
        className={classes.cardHeaderRoot}
        title={(
          <div className={classes.headerTitleRow}>
            <Select
              value={headerView}
              onChange={handleHeaderViewChange}
              variant="outlined"
              className={classes.headerViewSelect}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: { style: { borderRadius: 14, marginTop: 8 } },
              }}
            >
              <MenuItem value="Assessment" className={classes.headerViewMenuItem}>Assessment</MenuItem>
              <MenuItem value="Epicrisis" className={classes.headerViewMenuItem}>Epicrisis</MenuItem>
              <MenuItem value="Status Rapport" className={classes.headerViewMenuItem}>Status Rapport</MenuItem>
              <MenuItem value="Development" className={classes.headerViewMenuItem}>Patient Reported Outcomes</MenuItem>
              <MenuItem value="Questionnaires" className={classes.headerViewMenuItem}>Questionnaires</MenuItem>
            </Select>
          </div>
        )}
        action={(
          <div className={classes.headerActionRow}>
            {headerView === 'Assessment' && (
              <>
                {globalEditMode ? (
                  <>
                    <Button size="small" className={classes.headerPrimaryButton} variant={'contained'} onClick={toggleGlobalEdit} classes={{ root: classes.headerControlButton }}>
                      Save
                    </Button>
                    <Button size="small" className={classes.headerSecondaryButton} variant="contained" onClick={() => { if (generatedDraftActive) { cancelGeneratedDraft(); } else { cancelEditing(); } }} classes={{ root: classes.headerControlButton }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    size="small"
                    className={classes.headerPrimaryButton}
                    variant="contained"
                    onClick={toggleGlobalEdit}
                    classes={{ root: classes.headerControlButton }}
                  >
                    Edit
                  </Button>
                )}
                {journalVersions && journalVersions.length ? (
                  <div className={classes.headerActionRow}>
                    <Button size="small" className={classes.headerNavButton} variant="contained" onClick={goPrevVersion}>{'<'}</Button>
                    <div style={{ fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)', color: '#333', margin: '0 4px' }}>
                      {(() => {
                        const total = journalVersions ? journalVersions.length : 0;
                        if (!total) return null;
                        const idx = currentVersionIndex === -1 ? (total - 1) : currentVersionIndex;
                        const v = journalVersions[idx];
                        const verLabel = currentVersionIndex === -1
                          ? 'Live'
                          : (isSmallScreen ? `${currentVersionIndex + 1}/${total}` : `Version ${currentVersionIndex + 1}/${total}`);
                        const ts = v && v.ts ? formatDate(v.ts) : null;
                        return (<span>{verLabel}{ts ? ` — ${ts}` : ''}</span>);
                      })()}
                    </div>
                    <Button size="small" className={classes.headerNavButton} variant="contained" onClick={goNextVersion}>{'>'}</Button>
                  </div>
                ) : null}
              </>
            )}
            {onOpenReportDialog && (
              <Button
                size="small"
                className={classes.headerPrimaryButton}
                variant="contained"
                onClick={() => onOpenReportDialog('create')}
                classes={{ root: classes.headerControlButton }}
                style={{ minWidth: 'clamp(74px, 8.2vw, 100px)' }}
              >
                + Create
              </Button>
            )}
          </div>
        )}
      />
      <CardContent>
        {headerView === 'Assessment' && (
        <Grid container spacing={1} style={{ width: '100%' }}>
        <Grid item xs={12} md={6} lg={6} direction='column'>
          <Grid item style={{marginLeft: 0, paddingLeft: 0}}>
                {/* Journal for initial assessment */}
                <Box mt={0} mb={2} style={{border: '1px solid #ddd', backgroundColor: '#fff', borderRadius: 14, padding: 12, height: '100%'}}>
                  {journalLoading && <Typography>Loading journal...</Typography>}
                  {journalError && <Typography color="error">Error: {journalError}</Typography>}
                  {journalSections && Object.keys(journalSections).length === 0 && (
                    <Typography>No journal data available.</Typography>
                  )}
                  {journalSections && (() => {
                    // Ordered section keys user requested
                    const orderedKeys = [
                      'keySymptom',
                      'painAnamnesis',
                      'painAnamnesis_location',
                      'painAnamnesis_painCharacteristics',
                      'redflags',
                      'painAnamnesis_pattern',
                      'painAnamnesis_affectedActivities',
                      'painAnamnesis_cause_how_issue_occured',
                      'painAnamnesis_duration',
                      'painAnamnesis_frequency',
                      'painAnamnesis_painAtBest',
                      'painAnamnesis_painCurrent',
                      'painAnamnesis_painAtWorst',
                      'painAnamnesis_painWithActivity',
                      'painAnamnesis_painAtRest',
                      'painAnamnesis_difficulty',
                      'painAnamnesis_custom',
                      'painAnamnesis_aggravatingFactors',
                      'painAnamnesis_relievingFactors',
                      'painAnamnesis_doctor',
                      'painAnamnesis_doctorDiagnosis',
                      'secondary_multimorbid_doctorDiagnosis',
                      'painAnamnesis_development',
                      'painAnamnesis_episodes',
                      'painAnamnesis_function',
                      'painAnamnesis_knownCondition',
                      'movement_test',
                      'clinicalTest',
                      'SymptomProvoking_clinicalTest',
                      'SymptomImproving_clinicalTest',
                      'keyFunctionalLimitations',
                      'painAnamnesis_SelfRatedFunction_MainGoal',
                      'riskFactors',
                      'riskFactors_advice',
                      'diagnosis',
                    ];

                    // Tag order for sorting priority (first match wins)
                    const tagOrder = [...orderedKeys];

                    // Collect all items and mark consumed to avoid duplicates
                    const consumed = new Set();
                    const raw = journalSections;

                    // resolve the matched answer object for an item (prefers selected_answer, then selected_answer_meta, then legacy fields)
                    const findMatchedAnswer = (item) => {
                      if (!item) return null;
                      const sel = item.selected_answer || item.selected_answer_meta || {};
                      // Collect many possible candidate values from sel and item (handle different key names)
                      const candidates = [];
                      const addIf = (v) => { if (v !== undefined && v !== null && v !== '') candidates.push(String(v)); };
                      addIf(sel.answer_id);
                      addIf(sel.answerId);
                      addIf(sel.value);
                      addIf(sel.label);
                      addIf(sel.text);
                      addIf(sel.answerText);
                      addIf(sel.answer_text);
                      addIf(sel.canonical);
                      addIf(sel.text_value);
                      addIf(sel.numeric_value);
                      addIf(item.answer_id);
                      addIf(item.answerId);
                      addIf(item.answer_text);
                      addIf(item.answerText);
                      // also include selected_answer_meta.value if present
                      if (item.selected_answer_meta && item.selected_answer_meta.value) addIf(item.selected_answer_meta.value);

                      const answers = (item.question && Array.isArray(item.question.answers)) ? item.question.answers : [];
                      if (!answers.length) return null;
                      for (const a of answers) {
                        if (!a) continue;
                        const aVals = [];
                        const pushA = (v) => { if (v !== undefined && v !== null) aVals.push(String(v)); };
                        pushA(a.value);
                        pushA(a.id);
                        pushA(a.answerId);
                        pushA(a.label);
                        pushA(a.text);
                        pushA(a.answerText);
                        pushA(a.answer_text);
                        // compare intersection
                        for (const c of candidates) {
                          if (aVals.includes(String(c))) return a;
                        }
                      }
                      return null;
                    };

                    const getItemTags = (item) => {
                      const ans = findMatchedAnswer(item);
                      if (ans && Array.isArray(ans.tags) && ans.tags.length) return ans.tags;
                      // fallback to selected_answer_meta tags (legacy server payload)
                      if (item.selected_answer_meta && Array.isArray(item.selected_answer_meta.tags) && item.selected_answer_meta.tags.length) return item.selected_answer_meta.tags;
                      if (item.question && Array.isArray(item.question.tags) && item.question.tags.length) return item.question.tags;
                      if (Array.isArray(item.tags) && item.tags.length) return item.tags;
                      return [];
                    };

                    const pickItemsForKey = (key) => {
                      const out = [];
                      Object.entries(raw).forEach(([theme, subgroups]) => {
                        Object.entries(subgroups).forEach(([subgroup, items]) => {
                          items.forEach(i => {
                            const id = idForItem(i);
                            if (consumed.has(id)) return;
                            const qidentifier = (i.identifier || (i.question && i.question.identifier)) || '';
                            const identifierMatch = qidentifier === key;
                            const ans = findMatchedAnswer(i);
                            const answerSubgroup = (ans && (ans.subgroup || ans.type)) || null;
                            // Use canonical theme (without _manual) for matching
                            const canonicalTheme = typeof theme === 'string' ? theme.replace(/_manual$/, '') : theme;
                            const combinedKey = `${canonicalTheme}_${subgroup}`;
                            const subgroupMatch = (i.subgroup === key) || (answerSubgroup === key) || ((i.question && i.question.subgroup) === key) || (combinedKey === key) || (i.subgroup && `${canonicalTheme}_${i.subgroup}` === key);
                            const combinedKeyForItem = (i.subgroup ? `${canonicalTheme}_${i.subgroup}` : null);
                            // Match both canonical theme and theme_manual variants to the same key
                            const themeMatch = (theme === key || canonicalTheme === key) && !(i.subgroup && i.subgroup !== 'default' && Array.isArray(orderedKeys) && combinedKeyForItem && orderedKeys.includes(combinedKeyForItem));
                            const tags = getItemTags(i);
                            const tagMatch = Array.isArray(tags) && tags.includes(key);
                            // derived matches: some questions use generic tags like 'normal' but their identifier or question text
                            // indicates doctor-related content. Map those heuristics to the doctor keys.
                            let derivedMatch = false;
                            const qtext = (i.question && (i.question.question || '') || '').toString().toLowerCase();
                            const qid = (i.identifier || (i.question && i.question.identifier) || '').toString().toLowerCase();
                            if (key === 'painAnamnesis_doctor') {
                              if (qid.includes('doctor') || qtext.includes('doctor') || qtext.includes('examined')) derivedMatch = true;
                            }
                            if (key === 'painAnamnesis_doctorDiagnosis') {
                              if (qid.includes('diagnos') || qtext.includes('diagnos') || qtext.includes('diagnose')) derivedMatch = true;
                            }
                            if (identifierMatch || subgroupMatch || themeMatch || tagMatch || derivedMatch) {
                              consumed.add(id);
                              out.push(i);
                            }
                          });
                        });
                      });
                      return out;
                    };

                    const orderedSections = {};
                    // helper: determine tag priority for an item (prefers answer-level tags)
                    const getTagPriority = (item) => {
                      const tags = getItemTags(item) || [];
                      if (!Array.isArray(tags) || tags.length === 0) return tagOrder.length;
                      for (let i = 0; i < tagOrder.length; i++) {
                        if (tags.includes(tagOrder[i])) return i;
                      }
                      return tagOrder.length;
                    };

                    // helper: sort items by tag priority then by subgroup
                    // Ensure manual/default subgroup items render last by using a subgroup weight
                    const sortItems = (items) => {
                      return items.sort((a, b) => {
                        const pa = getTagPriority(a);
                        const pb = getTagPriority(b);
                        if (pa !== pb) return pa - pb;
                        // Check if item is manually-created (by theme suffix or id)
                        const isManualA = (a.manual === true) || (a.id && String(a.id).startsWith('manual-')) || (a.theme && String(a.theme).endsWith('_manual'));
                        const isManualB = (b.manual === true) || (b.id && String(b.id).startsWith('manual-')) || (b.theme && String(b.theme).endsWith('_manual'));
                        // Manual items should render last
                        if (isManualA && !isManualB) return 1;
                        if (!isManualA && isManualB) return -1;
                        const ansA = findMatchedAnswer(a);
                        const ansB = findMatchedAnswer(b);
                        const sa = ( (ansA && (ansA.subgroup || ansA.type)) || a.subgroup || (a.question && a.question.subgroup) || '' ).toString();
                        const sb = ( (ansB && (ansB.subgroup || ansB.type)) || b.subgroup || (b.question && b.question.subgroup) || '' ).toString();
                        // subgroup weight: normal (0), manual (2), default (3) => higher weights sorted later
                        const weight = (s) => { if (!s) return 0; if (s === 'manual') return 2; if (s === 'default') return 3; return 0; };
                        const wa = weight(sa);
                        const wb = weight(sb);
                        if (wa !== wb) return wa - wb;
                        return sa.localeCompare(sb);
                      });
                    };

                    orderedKeys.forEach(k => {
                      const items = pickItemsForKey(k);
                      if (items && items.length) orderedSections[k] = sortItems(items);
                    });

                    // Any remaining items go into 'other'
                    const remaining = [];
                    Object.entries(raw).forEach(([theme, subgroups]) => {
                      Object.entries(subgroups).forEach(([subgroup, items]) => {
                        items.forEach(i => {
                          const id = i.question_id || JSON.stringify(i);
                          if (!consumed.has(id)) {
                            consumed.add(id);
                            remaining.push(i);
                          }
                        });
                      });
                    });
                    if (remaining.length) orderedSections.other = sortItems(remaining);

                    // Group orderedSections into more user-friendly frontend sections
                    // Add explicit *_manual keys so manually-created items (subgroup 'manual')
                    // can be matched and rendered in a predictable order (typically last).
                    const grouping = {
                      'Primary complaint and cause of contact:': [
                        'keySymptom',
                        'painAnamnesis',
                        'painAnamnesis_cause_how_issue_occured',
                        'painAnamnesis_duration',
                        'painAnamnesis_doctor',
                        'painAnamnesis_doctorDiagnosis',
                        'secondary_multimorbid_doctorDiagnosis',
                        'painAnamnesis_episodes',
                        'painAnamnesis_episodes_manual',
                        'keySymptom_manual',
                        'painAnamnesis_manual',
                        'painAnamnesis_cause_how_issue_occured_manual',
                        'painAnamnesis_duration_manual',
                        'painAnamnesis_doctor_manual',
                        'painAnamnesis_doctorDiagnosis_manual',
                        'secondary_multimorbid_doctorDiagnosis_manual'
                      ],
                      'Location and character of symptoms:': [
                        'painAnamnesis_location',
                        'painAnamnesis_knownCondition',
                        'painAnamnesis_painCharacteristics',
                        'painAnamnesis_location_manual',
                        'painAnamnesis_painCharacteristics_manual'
                      ],
                      'Affected activities, pattern and development of symptoms:': [
                        'painAnamnesis_pattern',
                        'painAnamnesis_affectedActivities',
                        
                        'painAnamnesis_development',
                        'painAnamnesis_pattern_manual',
                        'painAnamnesis_affectedActivities_manual',
                        
                        'painAnamnesis_development_manual'
                      ],
                      'Pain baseline levels:': [
                        'painAnamnesis_frequency',
                        'painAnamnesis_painAtBest',
                        'painAnamnesis_painCurrent',
                        'painAnamnesis_painAtWorst',
                        'painAnamnesis_painAtRest',
                        'painAnamnesis_painWithActivity',
                        'painAnamnesis_difficulty',
                        'painAnamnesis_function',
                        'painAnamnesis_frequency_manual',
                        'painAnamnesis_painAtBest_manual',
                        'painAnamnesis_painCurrent_manual',
                        'painAnamnesis_painAtWorst_manual',
                        'painAnamnesis_painAtRest_manual',
                        'painAnamnesis_painWithActivity_manual',
                        'painAnamnesis_difficulty_manual',
                        'painAnamnesis_custom',
                        'painAnamnesis_custom_manual',
                        'painAnamnesis_function_manual',
                        'painAnamnesis_knownCondition_manual'
                      ],
                      'Patient goal for treatment:': [
                        'painAnamnesis_SelfRatedFunction_MainGoal',
                        'painAnamnesis_SelfRatedFunction_MainGoal_manual'
                      ],
                      'Redflags:': [
                        'redflags',
                        'redflags_manual'
                      ],
                      'Movement tests': [
                        'movement_test',
                        'movement_test_manual'
                      ],
                      'Symptom response on clinical tests': [
                        'clinicalTest',
                        'SymptomProvoking_clinicalTest',
                        'SymptomImproving_clinicalTest',
                        'keyFunctionalLimitations',
                        'clinicalTest_manual',
                        'SymptomProvoking_clinicalTest_manual',
                        'SymptomImproving_clinicalTest_manual',
                        'keyFunctionalLimitations_manual'
                      ]
                    };

                    const grouped = {};
                    // Collect grouped items
                    Object.entries(grouping).forEach(([groupName, keys]) => {
                      grouped[groupName] = [];
                      keys.forEach(k => {
                        if (orderedSections[k]) {
                          grouped[groupName] = grouped[groupName].concat(orderedSections[k]);
                        }
                        // also append any manual/default variants for this key so manual-created
                        // items render after canonical items in the same display group
                        const manualKey = `${k}_manual`;
                        const defaultKey = `${k}_default`;
                        if (orderedSections[manualKey]) grouped[groupName] = grouped[groupName].concat(orderedSections[manualKey]);
                        if (orderedSections[defaultKey]) grouped[groupName] = grouped[groupName].concat(orderedSections[defaultKey]);
                      });
                      // Re-sort after concatenation to ensure manual items appear last
                      if (grouped[groupName] && grouped[groupName].length) {
                        grouped[groupName] = sortItems(grouped[groupName]);
                      }
                    });

                    // Also include items that were saved with the display-group title as their theme
                    // (some manual entries use the display title as theme; ensure they render)
                    try {
                      Object.entries(raw || {}).forEach(([themeName, subgroups]) => {
                        if (Object.prototype.hasOwnProperty.call(grouping, themeName)) {
                          const flat = [];
                          Object.entries(subgroups || {}).forEach(([sg, items]) => {
                            if (Array.isArray(items)) flat.push(...items);
                          });
                          if (flat.length) {
                            grouped[themeName] = (grouped[themeName] || []).concat(flat);
                            // Re-sort after adding legacy items
                            grouped[themeName] = sortItems(grouped[themeName]);
                          }
                        }
                      });
                    } catch (e) {}

                    // Any remaining orderedSections not in grouping are intentionally omitted
                    // (The 'Other' group has been removed per UX request)

                    // helper to determine if an item is a pain-scale item (use subgroup/tags/identifier)
                    const painKeys = new Set([
                      'painAnamnesis_painCurrent', 'painAnamnesis_painAtBest', 'painAnamnesis_painAtWorst', 'painAnamnesis_frequency', 'painAnamnesis_painAtRest', 'painAnamnesis_painWithActivity', 'painAnamnesis_difficulty', 'painAnamnesis_custom',
                      // manual subgroups (no prefix)
                      'painCurrent', 'painAtBest', 'painAtWorst', 'painAtRest', 'painWithActivity', 'frequency', 'custom'
                    ]);
                    const sliderKeys = new Set(['painAnamnesis_SelfRatedFunction_MainGoal', 'painAnamnesis_SelfRatedFunction_MainGoal_manual']);
                    const sliderTypes = new Set(['selfRatedFunction', 'selfRated_NPS', 'selfRatedPain']);
                    const isPainItem = (it) => {
                      if (!it) return false;
                      const keyBlob = [it.mapped_key, it.identifier, it.subgroup, it.theme]
                        .filter(Boolean)
                        .map(v => String(v).toLowerCase())
                        .join(' ');
                      // direct pain keys (cover missing subgroup by checking identifier and theme too)
                      if (painKeys.has(it.subgroup)) return true;
                      if (it.identifier && painKeys.has(it.identifier)) return true;
                      if (painKeys.has(it.theme)) return true;
                      // explicit slider group (PSFS / self-rated function) should also be shown as numeric
                      if (it.subgroup === 'painAnamnesis_SelfRatedFunction_MainGoal' || it.subgroup === 'painAnamnesis_SelfRatedFunction_MainGoal_manual' || it.identifier === 'painAnamnesis_SelfRatedFunction_MainGoal') return true;
                      if (keyBlob.includes('selfratedfunction_maingoal') || keyBlob.includes('function_goal')) return true;
                      // question type based: slider-backed types
                      if (it.question && sliderTypes.has(it.question.type)) return true;
                      // tags can indicate grouping
                      const t = Array.isArray(it.tags) ? it.tags : (it.question && it.question.tags) || [];
                      for (const pk of painKeys) if (Array.isArray(t) && t.includes(pk)) return true;
                      for (const sk of sliderKeys) if (Array.isArray(t) && t.includes(sk)) return true;
                      return false;
                    };

                    // helper to detect if an item would render no visible content in non-edit mode
                    const isItemEmptyForDisplay = (item) => {
                      if (!item) return true;
                      // Pain items: require a numeric value to display anything
                      if (isPainItem(item)) {
                        const val = (item.numeric_value !== undefined && item.numeric_value !== null)
                          ? item.numeric_value
                          : item.numerical_value;
                        if (val === null || val === undefined) return true;
                        // numeric present ⇒ will render at least the number
                        return false;
                      }
                      const matched = findMatchedAnswer(item) || item.selected_answer_meta || null;
                      const getField = (obj, ...keys) => {
                        if (!obj) return null;
                        for (const k of keys) {
                          if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
                        }
                        return null;
                      };
                      const answerMetaJP = getField(matched, 'metaJournalPhrase', 'meta_journalPhrase');
                      const answerJP = getField(matched, 'journalPhrase', 'journal_phrase');
                      const questionMetaJP = getField(item.question, 'metaJournalPhrase', 'meta_journalPhrase');
                      const questionJP = getField(item.question, 'journalPhrase', 'journal_phrase');
                      const itemMetaJP = getField(item, 'metaJournalPhrase', 'meta_journalPhrase', 'meta_journal_phrase');
                      const itemJP = getField(item, 'journalPhrase', 'journal_phrase');

                      let mainText = null;
                      if (answerMetaJP !== undefined && answerMetaJP !== null && answerMetaJP !== '') {
                        mainText = String(answerMetaJP) + (answerJP ? (' ' + String(answerJP)) : '');
                      } else if (answerJP !== undefined && answerJP !== null && answerJP !== '') {
                        mainText = String(answerJP);
                      } else if (itemMetaJP !== undefined && itemMetaJP !== null && itemMetaJP !== '') {
                        mainText = String(itemMetaJP) + (itemJP ? (' ' + String(itemJP)) : '');
                      } else if (itemJP !== undefined && itemJP !== null && itemJP !== '') {
                        mainText = String(itemJP);
                      } else if (questionMetaJP !== undefined && questionMetaJP !== null && questionMetaJP !== '') {
                        mainText = String(questionMetaJP) + (questionJP ? (' ' + String(questionJP)) : '');
                      } else if (questionJP !== undefined && questionJP !== null && questionJP !== '') {
                        mainText = String(questionJP);
                      }
                      return !mainText;
                    };

                    // Render grouped sections
                    return Object.entries(grouped).map(([groupName, items]) => (
                      <Box key={groupName} mb={0} >
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" style={{ color: '#FF7F27', fontWeight: 600, marginTop: 6 }}>
                            {groupName}
                          </Typography>
                          {/* Special + button for Pain baseline levels and Patient goal for treatment */}
                          {/*{(groupName === 'Pain baseline levels:' || groupName === 'Patient goal for treatment:') && (
                            <Button
                              size="small"
                              onClick={() => setFollowupDialogOpen(true)}
                              style={{ marginLeft: 4, textTransform: 'none', fontSize: 11, color: '#002D55', fontWeight: 700, padding: '2px 6px', minWidth: 0, lineHeight: 1.4 }}
                            >
                              📊 Follow-up
                            </Button>
                          )}*/}
                          {globalEditMode && groupName === 'Pain baseline levels:' && (
                            <Button
                              size="small"
                              onClick={() => {
                                const creatorId = `creator-${Date.now()}-${Math.floor(Math.random()*10000)}`;
                                setPainBaselineCreators(prev => ({ ...prev, [groupName]: [...(prev[groupName] || []), creatorId] }));
                              }}
                              startIcon={<ControlPointIcon style={{ fontSize: 13 }} />}
                              style={{ marginLeft: 4, textTransform: 'none', fontSize: 11, color: '#fb741a', fontWeight: 700, padding: '2px 6px', minWidth: 0, lineHeight: 1.4 }}
                            >
                              Add baseline
                            </Button>
                          )}
                          {globalEditMode && groupName === 'Patient goal for treatment:' && (
                            <Button
                              size="small"
                              onClick={() => {
                                const creatorId = `creator-${Date.now()}-${Math.floor(Math.random()*10000)}`;
                                setFunctionGoalCreators(prev => ({ ...prev, [groupName]: [...(prev[groupName] || []), creatorId] }));
                              }}
                              startIcon={<ControlPointIcon style={{ fontSize: 13 }} />}
                              style={{ marginLeft: 4, textTransform: 'none', fontSize: 11, color: '#fb741a', fontWeight: 700, padding: '2px 6px', minWidth: 0, lineHeight: 1.4 }}
                            >
                              Add goal
                            </Button>
                          )}
                          {/* Regular add button for other sections */}
                          {(() => { const hasVisibleContent = Array.isArray(items) && items.some(it => !isItemEmptyForDisplay(it)); return ((!items || items.length === 0 || !hasVisibleContent) && (GROUPING_MAP[groupName] && GROUPING_MAP[groupName].length) && groupName !== 'Pain baseline levels:' && groupName !== 'Patient goal for treatment:'); })() ? (
                            <IconButton size="small" onClick={() => addManualItem(GROUPING_MAP[groupName][0], 'manual', 'New note') }>
                              <ControlPointIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                          {/* show add button for empty groups (maps display group to a known theme key) */}
                          {(() => { const hasVisibleContent = Array.isArray(items) && items.some(it => !isItemEmptyForDisplay(it)); return ((!items || items.length === 0 || !hasVisibleContent) && (GROUPING_MAP[groupName] && GROUPING_MAP[groupName].length) && groupName !== 'Pain baseline levels:' && groupName !== 'Patient goal for treatment:'); })() ? (
                            <IconButton size="small" onClick={() => addManualItem(GROUPING_MAP[groupName][0], 'manual', 'New note') }>
                              <ControlPointIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                            {(props.setActiveTab && (groupName === 'Treatmentplan' || groupName === 'Initial Advice')) && (
                            <Button
                              size="small"
                              className={classes.Button}
                              variant="outlined"
                              onClick={() => {
                                openProgramBuilder(null, groupName === 'Initial Advice');
                              }}
                              style={{ marginLeft: 8, textTransform: 'none', fontSize: 12, minWidth: 64 }}
                            >
                              {groupName === 'Treatmentplan' ? 'Edit exercises' : 'Edit advice'}
                            </Button>
                          )}
                        </Box>
                        {groupName === 'Patient consent' && (() => {
                          const consentSectionItems = journalSections && journalSections.patientConsent && Array.isArray(journalSections.patientConsent.default)
                            ? journalSections.patientConsent.default
                            : [];
                          const consentItem = (Array.isArray(items) && items.length > 0 ? items[0] : null) || consentSectionItems[0] || null;
                          const consentId = consentItem ? idForItem(consentItem) : null;
                          const consentVals = consentId ? (editableValues[consentId] || {}) : {};
                          const isEditingConsent = !!(consentId && (globalEditMode || !!editingItems[consentId]));
                          const defaultConsentText = hasStaffAuthoredConsentNote
                            ? PATIENT_CONSENT_CLINICIAN_DEFAULT_TEXT
                            : PATIENT_CONSENT_B2C_DEFAULT_TEXT;
                          const storedConsentText = consentItem
                            ? [consentItem.journalPhrase, consentItem.journal_text, consentItem.journal_phrase]
                              .find(value => value !== undefined && value !== null && String(value).trim() !== '')
                            : '';
                          const localConsentText = Object.prototype.hasOwnProperty.call(consentVals, 'journalPhrase')
                            ? String(consentVals.journalPhrase || '')
                            : '';
                          const visibleConsentText = (isEditingConsent ? localConsentText : '') || storedConsentText || defaultConsentText;

                          return (
                            <Box mt={0.5} mb={1} style={{ marginLeft: 0, border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
                              {isEditingConsent && consentId ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  margin="dense"
                                  value={visibleConsentText}
                                  onChange={(e) => setEditableValues(prev => ({ ...(prev || {}), [consentId]: { ...(prev && prev[consentId] ? prev[consentId] : {}), journalPhrase: e.target.value, metaJournalPhrase: '', theme: consentItem?.theme || 'patientConsent', subgroup: consentItem?.subgroup || 'default' } }))}
                                  InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158' } }}
                                  inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                />
                              ) : (
                                <Typography variant="body2" component="div" style={{ color: '#002D55', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {String(visibleConsentText || defaultConsentText)}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}

                        <Table size="small" style={{ marginTop: 6, marginBottom: 8 }}>
                          <TableBody>
                            <TableRow>
                              <TableCell style={{ padding: 0, borderBottom: 'none' }}>
                                <Box ml={0} mb={0} style={{ padding: 4, border: '1px solid #ccc', borderRadius: 12, width: '100%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {(() => { const hasVisibleContent = Array.isArray(items) && items.some(it => !isItemEmptyForDisplay(it)); return groupName !== 'Patient consent' && ((!items || items.length === 0 || !hasVisibleContent) && !excludedGroupsForDraft.has(groupName)); })() ? (
                            <Box style={{ marginLeft: 18, marginBottom: 0, width: '80%' }}>
                               {globalEditMode ? (
                                 null
                               ) : (
                                <Typography variant="body2" style={{ fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)', lineHeight: '1.5', minHeight: 45, color: (groupDrafts[groupName] ? '#324158' : '#9E9E9E') }}>
                                  {groupDrafts[groupName] || "Click 'Edit' to add note..."}
                                </Typography>
                              )}
                            </Box>
                          ) : null}
                          {items.map((item, idx) => {
                            if (groupName === 'Patient consent') return null;
                            // Special-case pain-scale items: show only meta/journalPhrase + numeric_value
                            if (isPainItem(item)) {
                              const val = (item.numeric_value !== undefined && item.numeric_value !== null)
                                ? item.numeric_value
                                : item.numerical_value;
                              if (val === null || val === undefined) return null;
                              const matchedAns = findMatchedAnswer(item) || item.selected_answer_meta || null;

                              const id = idForItem(item);
                              const isEditing = (globalEditMode || !!editingItems[id]);
                              const editVals = editableValues[id] || {};
                              const kpiState = getKpiItemState(item, groupName);
                              if (kpiState.isKpi && kpiState.isDeleted) return null;

                              // Build ordered parts using the minimal snippet: meta, answer, journal
                              const meta = sanitizeDisplay((matchedAns && (matchedAns.metaJournalPhrase || matchedAns.meta_journalPhrase)) ? (matchedAns.metaJournalPhrase || matchedAns.meta_journalPhrase) : (item.selected_answer_meta && (item.selected_answer_meta.metaJournalPhrase || item.selected_answer_meta.meta_journalPhrase) ? (item.selected_answer_meta.metaJournalPhrase || item.selected_answer_meta.meta_journalPhrase) : (item.metaJournalPhrase || item.meta_journalPhrase || '')));
                              const ans = sanitizeDisplay((matchedAns && (matchedAns.answerText || matchedAns.text || matchedAns.label)) ? (matchedAns.answerText || matchedAns.text || matchedAns.label) : (item.answer_text || ''));
                              const journal = sanitizeDisplay(item.journalPhrase || item.journal_text || item.journal_phrase || '');
                              const num = val != null ? String(val) : '';

                              // new order: metaJournalPhrase, answer_text, journalPhrase
                              const parts = [meta, ans, journal].map(p => removeAnswerIdTokens(String(p || '')).trim()).filter(p => p && p !== num);

                              if (isEditing) {
                                // In edit mode show three inline fields: description (meta+journal), answer_text, numeric value
                                // Use journalPhrase from editVals (meta is merged into journal on save)
                                const freqKeys = new Set(['painAnamnesis_frequency', 'frequency']);
                                const isFrequency = freqKeys.has(item.subgroup) || freqKeys.has(item.identifier) || freqKeys.has(item.theme);
                                const isGoal = groupName === 'Patient goal for treatment:'
                                  || [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
                                    .filter(Boolean)
                                    .some(value => {
                                      const lowered = String(value).toLowerCase();
                                      return lowered.includes('painanamnesis_selfratedfunction_maingoal') || lowered === 'function_goal';
                                    });
                                // Custom pain baselines (e.g. "Patient reported: pain
                                // with benchpress") need the same editable activity
                                // textbox the goal items use, so the clinician can
                                // rename the activity in place. Detect via the
                                // canonical kpi_subtype, the legacy subgroup
                                // (`painAnamnesis_custom`), or any identifier marked
                                // custom — mirror the heuristics used in
                                // inferKpiTypeFromItem so we catch both new and
                                // historical rows.
                                const isCustomPain = !isGoal && !isFrequency && (
                                  String(item.kpi_subtype || '').toLowerCase() === 'custom'
                                  || [item.subgroup, item.identifier, item.theme, item.mapped_key]
                                    .filter(Boolean)
                                    .some(v => String(v).toLowerCase().includes('custom'))
                                );
                                const sanitizeDesc = (s) => (s || '').replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ').replace(/\s{2,}/g, ' ').trim();
                                const descVal = (editVals && editVals.journalPhrase !== undefined) 
                                  ? sanitizeDesc(editVals.journalPhrase)
                                  : sanitizeDesc(((meta || '') + ' ' + (journal || '')).trim());
                                const descValTrimmed = String(descVal || '').trim();
                                const descColonIdx = descValTrimmed.indexOf(':');
                                // Some legacy custom rows persist without a colon
                                // ("Patient reported pain with benchpress 66"). Split
                                // them on the boilerplate prefix so the activity
                                // textbox still gets populated on edit.
                                const colonlessCustomPrefix = /^patient reported\s+/i;
                                const colonlessCustomMatch = isCustomPain && descColonIdx < 0
                                  ? descValTrimmed.match(colonlessCustomPrefix)
                                  : null;
                                const labelText = isGoal
                                  ? 'Patient described functional goal (PSFS):'
                                  : isFrequency
                                  ? 'Patient reported frequency of symptoms:'
                                  : (descColonIdx >= 0
                                      ? descValTrimmed.slice(0, descColonIdx + 1).trim()
                                      : (colonlessCustomMatch
                                          ? 'Patient reported:'
                                          : descValTrimmed));
                                const inlineActivityFromDesc = isGoal
                                  ? descValTrimmed
                                    .replace(/^patient described functional goal \(psfs\):\s*/i, '')
                                    .replace(/^the patient described their main functional goal \(psfs\) as:\s*/i, '')
                                    .trim()
                                  : (descColonIdx >= 0
                                      ? descValTrimmed.slice(descColonIdx + 1).trim()
                                      : (colonlessCustomMatch
                                          ? descValTrimmed.slice(colonlessCustomMatch[0].length).trim()
                                          : ''));
                                const numericVal = (editVals && (editVals.numerical_value !== undefined ? editVals.numerical_value : num)) || '';
                                const answerVal = (editVals && (editVals.answer_text !== undefined ? editVals.answer_text : ans)) || '';
                                const activityVal = isFrequency
                                  ? ''
                                  : ((answerVal || '').toString().trim() !== '' ? answerVal : inlineActivityFromDesc);
                                return (
                                  <Box key={item.question_id || idx} style={{ marginLeft: 18, marginBottom: 6, display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                                    <TextField
                                      value={(editVals && editVals.custom_label !== undefined) ? editVals.custom_label : labelText}
                                      onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), custom_label: e.target.value } }))}
                                      margin="dense"
                                      multiline
                                      rowsMax={2}
                                      InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)', color: '#324158', fontWeight: 500, padding: '2px 0' } }}
                                      style={{ flex: '0 1 500px', minWidth: 110, maxWidth: 500 }}
                                    />
                                    {(isGoal || isCustomPain) ? (
                                      <TextField
                                        value={activityVal}
                                        onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), answer_text: e.target.value } }))}
                                        placeholder="description"
                                        margin="dense"
                                        multiline
                                        rowsMax={2}
                                        style={{ flex: '1 1 0', minWidth: 120 }}
                                        disabled={isFrequency}
                                        InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.4', color: '#324158' } }}
                                      />
                                    ) : (
                                      <Box style={{ flex: '1 1 0', minWidth: 60 }} />
                                    )}
                                    <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, paddingRight: 18 }}>
                                      <TextField
                                        value={numericVal != null ? String(numericVal) : ''}
                                        onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), numerical_value: parseNumericSafe(e.target.value) } }))}
                                        placeholder="Score"
                                        margin="dense"
                                        InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.4', color: '#324158', maxWidth: 25 } }}
                                      />
                                      <Typography variant="body2" style={{ color: '#666', minWidth: 35 }}>{isFrequency ? ' / 7d' : ' / 100'}</Typography>
                                      {kpiState.isKpi && (
                                        <>
                                          <Tooltip title={kpiState.isPaused ? 'Resume in follow-up' : 'Pause from follow-up'}>
                                            <IconButton size="small" onClick={() => toggleExistingKpiPaused(item, groupName)}>
                                              {kpiState.isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete KPI">
                                            <IconButton size="small" onClick={() => markExistingKpiDeleted(item, groupName)}>
                                              <RemoveIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              }

                              // Non-edit display: combine without duplicating answer_text if already present
                              const isGoalItem = groupName === 'Patient goal for treatment:'
                                || [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
                                  .filter(Boolean)
                                  .some(value => {
                                    const lowered = String(value).toLowerCase();
                                    return lowered.includes('painanamnesis_selfratedfunction_maingoal') || lowered === 'function_goal';
                                  });
                              const isFrequencyItem = [item.subgroup, item.identifier, item.theme, item.mapped_key]
                                .filter(Boolean)
                                .some(value => String(value).toLowerCase().includes('frequency'));
                              if (isFrequencyItem) {
                                const frequencyParts = ['Patient reported frequency of symptoms:'];
                                if (num) frequencyParts.push(`${num} / 7 days`);
                                return (
                                  <Typography key={item.question_id || idx} variant="body2" className={classes.compactText}>
                                    {frequencyParts.join(' — ')}
                                  </Typography>
                                );
                              }
                              if (isGoalItem) {
                                const goalLabel = 'Patient described functional goal (PSFS):';
                                const rawActivity = (ans || journal || '').toString().trim();
                                const activityText = rawActivity
                                  .replace(/^patient described functional goal \(psfs\):\s*/i, '')
                                  .replace(/^the patient described their main functional goal \(psfs\) as:\s*/i, '')
                                  .trim();
                                const goalParts = [goalLabel];
                                if (activityText) goalParts.push(activityText);
                                if (num) goalParts.push(`${num} / 100`);
                                return (
                                  <Typography key={item.question_id || idx} variant="body2" className={classes.compactText}>
                                    {goalParts.join(' — ').replace(': —', ':')}
                                  </Typography>
                                );
                              }

                              const desc = ([meta, journal].map(s => (s || '').toString().trim()).filter(Boolean).join(' ')).trim()
                                .replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ')
                                .replace(/\s{2,}/g, ' ')
                                .trim();
                              const answerText = (ans || '').toString().trim();
                              const numberText = num ? String(num) : '';
                              const displayParts = [];
                              if (desc) displayParts.push(desc);
                              if (answerText && (!desc || !desc.toLowerCase().includes(answerText.toLowerCase()))) displayParts.push(answerText);
                              const freqKeys = new Set(['painAnamnesis_frequency', 'frequency']);
                              const isFrequency = freqKeys.has(item.subgroup) || freqKeys.has(item.identifier) || freqKeys.has(item.theme);
                              if (numberText) displayParts.push(numberText + (isFrequency ? ' / 7 days' : ' / 100'));
                              return (
                                <Box key={item.question_id || idx} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6, marginBottom: 2 }}>
                                  <Typography variant="body2" className={classes.compactText} style={{ flex: 1 }}>
                                    {displayParts.join(' — ')}
                                  </Typography>
                                  {(() => {
                                    const qId = kpiSubtypeToQId(item.kpi_type, item.kpi_subtype || item.subgroup);
                                    const histEntry = qId && kpiHistory[qId];
                                    if (!histEntry) return null;
                                    const baseline = histEntry.effective_baseline;
                                    const series = histEntry.series || [];
                                    const latest = series.length > 1 ? series[series.length - 1] : null;
                                    if (!latest || baseline == null || baseline.numeric_value == null || latest.numeric_value == null) return null;
                                    const pct = baseline.numeric_value !== 0
                                      ? Math.round(((latest.numeric_value - baseline.numeric_value) / Math.abs(baseline.numeric_value)) * 100)
                                      : null;
                                    if (pct == null) return null;
                                    const improved = item.kpi_type === 'function_goal' ? pct > 0 : pct < 0;
                                    const chipColor = improved ? '#00a152' : pct === 0 ? '#64748B' : '#d84315';
                                    return (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 10, background: chipColor, color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 36, justifyContent: 'center' }}>
                                        {pct > 0 ? '+' : ''}{pct}%
                                      </span>
                                    );
                                  })()}
                                </Box>
                              );
                            }

                            const genericItemId = idForItem(item);
                            const genericEditVals = editableValues[genericItemId] || {};
                            const genericIsDeleted = genericEditVals.is_deleted !== undefined
                              ? parseBooleanSafe(genericEditVals.is_deleted)
                              : parseBooleanSafe(item && item.is_deleted);
                            if (genericIsDeleted) return null;

                            const matched = findMatchedAnswer(item) || item.selected_answer_meta || null;
                            const getField = (obj, ...keys) => {
                              if (!obj) return null;
                              for (const k of keys) {
                                if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
                              }
                              return null;
                            };
                            const answerMetaJP = getField(matched, 'metaJournalPhrase', 'meta_journalPhrase');
                            const answerJP = getField(matched, 'journalPhrase', 'journal_phrase');
                            const questionMetaJP = getField(item.question, 'metaJournalPhrase', 'meta_journalPhrase');
                            const questionJP = getField(item.question, 'journalPhrase', 'journal_phrase');
                            const itemMetaJP = getField(item, 'metaJournalPhrase', 'meta_journalPhrase', 'meta_journal_phrase');
                            const itemJP = getField(item, 'journalPhrase', 'journal_phrase');
                            const answerDisplay = getHumanAnswerText(
                              matched,
                              (answerJP !== undefined && answerJP !== null && String(answerJP).trim() !== '')
                                ? answerJP
                                : getField(item, 'answer_text', 'answerText')
                            );
                            const answerMetaDisplay = sanitizeDisplay(answerMetaJP);
                            const itemMetaDisplay = sanitizeDisplay(itemMetaJP);
                            const itemJournalDisplay = sanitizeDisplay(itemJP);
                            const questionMetaDisplay = sanitizeDisplay(questionMetaJP);
                            const questionJournalDisplay = sanitizeDisplay(questionJP);
                            const identifierBlob = [
                              item && item.identifier,
                              item && item.subgroup,
                              item && item.theme,
                              item && item.mapped_key,
                              item && item.question_id,
                              item && item.question && item.question.identifier,
                            ]
                              .filter(Boolean)
                              .map(v => String(v).toLowerCase())
                              .join(' ');
                            const redflagLabel = identifierBlob.includes('accompaniedbysystemicsymptoms')
                              ? 'Accompanying systemic symptoms:'
                              : ((identifierBlob.includes('redflags_1') || identifierBlob.includes('neurological redflag'))
                                ? 'Patient reported neurological redflag symptoms:'
                                : null);

                            let mainText = null;
                            if (redflagLabel) {
                              const redflagValue = [
                                answerDisplay,
                                itemJournalDisplay,
                                questionJournalDisplay,
                              ]
                                .find(v => v !== undefined && v !== null && String(v).trim() !== '');
                              if (redflagValue) {
                                mainText = `${redflagLabel} ${String(redflagValue).trim()}`;
                              } else if (questionMetaDisplay) {
                                mainText = questionMetaDisplay;
                              }
                            } else if (answerMetaDisplay) {
                              mainText = answerMetaDisplay + (answerDisplay ? (' ' + answerDisplay) : '');
                            } else if (answerDisplay) {
                              mainText = answerDisplay;
                            } else if (itemMetaDisplay) {
                              mainText = itemMetaDisplay + (itemJournalDisplay ? (' ' + itemJournalDisplay) : '');
                            } else if (itemJournalDisplay) {
                              mainText = itemJournalDisplay;
                            } else if (questionMetaDisplay) {
                              mainText = questionMetaDisplay + (questionJournalDisplay ? (' ' + questionJournalDisplay) : '');
                            } else if (questionJournalDisplay) {
                              mainText = questionJournalDisplay;
                            }

                            if (mainText === null || mainText === undefined || mainText === '') return null;
                            mainText = mainText
                              .replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ')
                              .replace(/(\d)\s*\/(\d)/g, '$1 / $2')
                              .replace(/\s{2,}/g, ' ')
                              .trim();
                            if (!mainText) return null;
                            const gid = idForItem(item);
                            const gIsEditing = (globalEditMode || !!editingItems[gid]);
                            const gVals = editableValues[gid] || {};
                            if (gIsEditing) {
                              const hasLocalJournal = !!(gVals && Object.prototype.hasOwnProperty.call(gVals, 'journalPhrase'));
                              const localJournal = hasLocalJournal ? String(gVals.journalPhrase || '') : '';
                              const localLower = localJournal.trim().toLowerCase();
                              const labelLower = (redflagLabel || '').toLowerCase();
                              const shouldUseMainForRedflag = !!(redflagLabel && localJournal.trim() && !localLower.startsWith(labelLower));
                              const editValue = shouldUseMainForRedflag
                                ? mainText
                                : (hasLocalJournal ? localJournal : mainText);
                              return (
                                <Box key={item.question_id || idx} style={{ marginLeft: 18, marginBottom: 0, padding: 0 }}>
                                  <TextField
                                    value={editValue}
                                    onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [gid]: { ...(prev && prev[gid] ? prev[gid] : {}), journalPhrase: e.target.value, metaJournalPhrase: '' } }))}
                                    fullWidth
                                    margin="dense"
                                    multiline
                                    InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                    inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                  />
                                  {/* global save on finish; inline Save/Cancel removed */}
                                </Box>
                              );
                            }
                            return (
                              <Typography key={item.question_id || idx} variant="body2" className={classes.compactText}>
                                {mainText}
                                {/* single global edit toggles all fields */}
                              </Typography>
                            );
                          })}

                          {/* Render per-group extra editable fields in edit mode */}
                          {globalEditMode && (() => {
                            // Special rendering for Pain baseline levels - render multiple creators
                            if (groupName === 'Pain baseline levels:' && painBaselineCreators[groupName] && painBaselineCreators[groupName].length > 0) {
                              return painBaselineCreators[groupName].map((creatorId) => {
                                const baselineKey = `pain-baseline-${creatorId}`;
                                const inputs = painBaselineInputs[baselineKey] || { baselineType: 'best', activity: '', score: '', customLabel: '', customMetric: '' };
                              
                              const baselineLabels = {
                                frequency: 'Patient reported symptom:',
                                best: 'Patient reported pain when at:',
                                worst: 'Patient reported pain when at:',
                                current: 'Patient reported pain as it is:',
                                rest: 'Patient reported pain when at:',
                                activity: 'Patient reported pain with:',
                                difficulty: 'Patient reported:',
                                custom: 'Patient reported:'
                              };

                              const baselineOptionText = {
                                best: 'best/lowest',
                                worst: 'worst/highest',
                                current: 'current',
                                rest: 'rest',
                                activity: 'activity',
                                frequency: 'frequency',
                                difficulty: 'difficulty',
                                custom: inputs.customMetric || 'custom metric'
                              };
                              
                              // Helper to update editableValues when inputs change
                              const updateBaselineEditableValues = (baselineType, activity, score, customMetricVal, customLabelVal = '') => {
                                if (!score || score === '') {
                                  // Clear if no score
                                  setEditingItems(prev => {
                                    const next = { ...prev };
                                    delete next[baselineKey];
                                    return next;
                                  });
                                  setEditableValues(prev => {
                                    const next = { ...prev };
                                    delete next[baselineKey];
                                    return next;
                                  });
                                  return;
                                }
                                
                                const typeLabels = {
                                  'best': 'pain level when at best/lowest',
                                  'worst': 'pain level when at worst/highest', 
                                  'current': 'current pain level',
                                  'frequency': 'pain frequency (days per week)',
                                  'rest': 'pain at rest',
                                  'activity': 'pain with activity',
                                  'custom': customMetricVal || 'custom metric'
                                };
                                
                                const themeMap = {
                                  'best': 'painAnamnesis_painAtBest',
                                  'worst': 'painAnamnesis_painAtWorst',
                                  'current': 'painAnamnesis_painCurrent',
                                  'frequency': 'painAnamnesis_frequency',
                                  'rest': 'painAnamnesis_painAtRest',
                                  'activity': 'painAnamnesis_painWithActivity',
                                  'difficulty': 'painAnamnesis_difficulty',
                                  'custom': 'painAnamnesis_custom'
                                };
                                
                                const subgroupMap = {
                                  'best': 'painAtBest',
                                  'worst': 'painAtWorst',
                                  'current': 'painCurrent',
                                  'frequency': 'frequency',
                                  'rest': 'painAtRest',
                                  'activity': 'painWithActivity',
                                  'custom': 'custom'
                                };
                                
                                const theme = themeMap[baselineType] || 'painAnamnesis_painCurrent';
                                const subgroup = subgroupMap[baselineType] || 'painCurrent';
                                const base = (customLabelVal && String(customLabelVal).trim())
                                  ? String(customLabelVal).trim()
                                  : (baselineLabels[baselineType] || 'Patient reported pain level');
                                const customMetricText = customMetricVal ? String(customMetricVal).trim() : '';
                                const suffix = baselineType === 'custom'
                                  ? (customMetricText ? ` ${customMetricText}` : '')
                                  : (baselineOptionText[baselineType] ? ` ${baselineOptionText[baselineType]}` : '');
                                // Insert a colon between the boilerplate base and the
                                // free-text suffix so the inline-edit path can split
                                // label/activity cleanly. Without it, "Patient reported"
                                // + " pain with benchpress" persisted as one phrase and
                                // the activity field rendered empty on re-edit.
                                const baseHasTerminator = /[:\u2014\u2013\-]\s*$/.test(base);
                                const joinedBase = (suffix && !baseHasTerminator) ? `${base}:` : base;
                                const label = `${joinedBase}${suffix}`.replace(/\s{2,}/g, ' ').trim();
                                
                                // Add to editableValues and groupExtraFields - will be saved when main Save is clicked
                                setEditingItems(prev => ({ ...(prev || {}), [baselineKey]: true }));
                                setEditableValues(prev => ({ 
                                  ...(prev || {}), 
                                  [baselineKey]: { 
                                    id: creatorId,
                                    metaJournalPhrase: '',
                                    journalPhrase: label,
                                    numerical_value: parseInt(score, 10) || 0,
                                    answer_text: dedupePhrase(activity || ''),
                                    theme: theme,
                                    subgroup: subgroup,
                                    mapped_key: theme,
                                    display_group: groupName,
                                    answer_text_role: 'unknown'
                                  } 
                                }));
                                
                                // Track this item in groupExtraFields so it gets saved properly
                                setGroupExtraFields(prev => {
                                  const existing = prev[groupName] || [];
                                  if (!existing.includes(baselineKey)) {
                                    return { ...prev, [groupName]: [...existing, baselineKey] };
                                  }
                                  return prev;
                                });
                              };
                              
                              return (
                                <Box key={`pain-baseline-creator-${creatorId}`} style={{ marginLeft: 18, marginTop: 4, marginBottom: 6, display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', width: 'calc(100% - 18px)', minWidth: 0, gap: 8 }}>
                                  <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                                    <TextField
                                      value={(painBaselineInputs[baselineKey] && painBaselineInputs[baselineKey].customLabel) ? painBaselineInputs[baselineKey].customLabel : (inputs.baselineType === 'custom' ? 'Patient reported' : (baselineLabels[inputs.baselineType] || 'Patient reported pain level when at'))}
                                      onChange={(e) => {
                                        const nextLabel = e.target.value;
                                        setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, customLabel: nextLabel } }));
                                        updateBaselineEditableValues(inputs.baselineType, inputs.activity, inputs.score, inputs.customMetric || '', nextLabel);
                                      }}
                                      margin="dense"
                                      multiline
                                      rowsMax={2}
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', fontWeight: 500, padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                      style={{ flex: '1 1 50px', minWidth: 120, maxWidth: 'clamp(120px, 30vw, 320px)', marginRight: 8 }}
                                    />
                                    {inputs.baselineType !== 'custom' ? (
                                      <Select
                                        value={inputs.baselineType}
                                        onChange={(e) => {
                                          const newType = e.target.value;
                                          setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, baselineType: newType } }));
                                          updateBaselineEditableValues(newType, inputs.activity, inputs.score, inputs.customMetric || '', inputs.customLabel || '');
                                        }}
                                        variant="outlined"
                                        className={classes.baselineKpiSelect}
                                        style={{ flex: '0 1 auto', minWidth: 'clamp(90px, 11vw, 150px)', maxWidth: 'clamp(90px, 11vw, 150px)', marginRight: 8 }}
                                        MenuProps={{
                                          variant: 'menu',
                                          getContentAnchorEl: null,
                                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                                          PaperProps: { style: { borderRadius: 14, marginTop: 8 } },
                                        }}
                                      >
                                        <MenuItem className={classes.baselineKpiMenuItem} value="best">best/lowest</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="worst">worst/highest</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="current">current</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="rest">rest</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="activity">activity</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="frequency">frequency</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="difficulty">difficulty</MenuItem>
                                        <MenuItem className={classes.baselineKpiMenuItem} value="custom"><em>custom…</em></MenuItem>
                                      </Select>
                                    ) : (
                                      <Box className={classes.customKpiInlineField} style={{ flex: '0 1 clamp(120px, 16vw, 220px)', width: 'clamp(120px, 16vw, 220px)', minWidth: 120, maxWidth: 220, marginRight: 8 }}>
                                        <TextField
                                          placeholder="metric name"
                                          value={inputs.customMetric || ''}
                                          onChange={(e) => {
                                            const newLabel = e.target.value;
                                            setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, customMetric: newLabel } }));
                                            updateBaselineEditableValues('custom', inputs.activity, inputs.score, newLabel, inputs.customLabel || '');
                                          }}
                                          margin="none"
                                          InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                          inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                          style={{ flex: 1, minWidth: 80 }}
                                        />
                                        <Select
                                          value={inputs.baselineType}
                                          onChange={(e) => {
                                            const newType = e.target.value;
                                            setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, baselineType: newType, customMetric: '' } }));
                                            updateBaselineEditableValues(newType, inputs.activity, inputs.score, '', inputs.customLabel || '');
                                          }}
                                          variant="outlined"
                                          className={classes.customKpiTypeToggle}
                                          renderValue={() => ''}
                                          MenuProps={{
                                            variant: 'menu',
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                                            transformOrigin: { vertical: 'top', horizontal: 'right' },
                                            PaperProps: { style: { borderRadius: 14, marginTop: 8 } },
                                          }}
                                        >
                                          <MenuItem className={classes.baselineKpiMenuItem} value="best">best/lowest</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="worst">worst/highest</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="current">current</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="rest">rest</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="activity">activity</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="frequency">frequency</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="difficulty">difficulty</MenuItem>
                                          <MenuItem className={classes.baselineKpiMenuItem} value="custom"><em>custom…</em></MenuItem>
                                        </Select>
                                      </Box>
                                    )}
                                    {/*
                                    <TextField
                                      placeholder="description"
                                      value={inputs.activity}
                                      onChange={(e) => {
                                        const newActivity = e.target.value;
                                        setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, activity: newActivity } }));
                                        updateBaselineEditableValues(inputs.baselineType, newActivity, inputs.score, inputs.customMetric || '', inputs.customLabel || '');
                                      }}
                                      margin="dense"
                                      multiline
                                      rowsMax={2}
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                      style={{ flex: '1 1 120px', minWidth: 90 }}
                                    />
                                    */}
                                    <Box style={{ flex: '1 1 120px', minWidth: 90 }} />
                                  </Box>
                                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <TextField
                                      placeholder="score"
                                      type="number"
                                      className={classes.noNumberSpinner}
                                      value={inputs.score}
                                      onChange={(e) => {
                                        const newScore = e.target.value;
                                        setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: { ...inputs, score: newScore } }));
                                        updateBaselineEditableValues(inputs.baselineType, inputs.activity, newScore, inputs.customMetric || '', inputs.customLabel || '');
                                      }}
                                      margin="dense"
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', width: 'clamp(35px, 2.2vw, 42px)', padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                    />
                                    <Typography variant="body2" style={{ color: '#666', minWidth: 34 }}>{inputs.baselineType === 'frequency' ? ' / 7d days' : ' / 100'}</Typography>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => {
                                        setPainBaselineCreators(prev => ({ ...prev, [groupName]: (prev[groupName] || []).filter(id => id !== creatorId) }));
                                        setPainBaselineInputs(prev => ({ ...prev, [baselineKey]: undefined }));
                                        setEditingItems(prev => {
                                          const next = { ...(prev || {}) };
                                          delete next[baselineKey];
                                          return next;
                                        });
                                        setEditableValues(prev => {
                                          const next = { ...(prev || {}) };
                                          delete next[baselineKey];
                                          return next;
                                        });
                                        setGroupExtraFields(prev => ({
                                          ...(prev || {}),
                                          [groupName]: (prev && prev[groupName] ? prev[groupName] : []).filter(id => id !== baselineKey)
                                        }));
                                      }} 
                                      style={{ marginLeft: 6 }}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              );
                              });
                            }
                            
                            // Special rendering for Patient goal for treatment - render multiple creators
                            if (groupName === 'Patient goal for treatment:' && functionGoalCreators[groupName] && functionGoalCreators[groupName].length > 0) {
                              return functionGoalCreators[groupName].map((creatorId) => {
                                const goalKey = `function-goal-${creatorId}`;
                                const inputs = functionGoalInputs[goalKey] || { activity: '', score: '' };
                              
                              // Helper to update editableValues when inputs change
                              const updateGoalEditableValues = (activity, score) => {
                                if (!score || score === '' || !activity || activity === '') {
                                  // Clear if incomplete
                                  setEditingItems(prev => {
                                    const next = { ...prev };
                                    delete next[goalKey];
                                    return next;
                                  });
                                  setEditableValues(prev => {
                                    const next = { ...prev };
                                    delete next[goalKey];
                                    return next;
                                  });
                                  return;
                                }
                                
                                const baseIdentifier = 'painAnamnesis_SelfRatedFunction_MainGoal';
                                
                                // Add to editableValues and groupExtraFields - will be saved when main Save is clicked
                                setEditingItems(prev => ({ ...(prev || {}), [goalKey]: true }));
                                setEditableValues(prev => ({ 
                                  ...(prev || {}), 
                                  [goalKey]: { 
                                    id: creatorId,
                                    metaJournalPhrase: "Patient described functional goal (PSFS):",
                                    journalPhrase: activity,
                                    numerical_value: parseInt(score, 10) || 0,
                                    numeric_value: parseInt(score, 10) || 0,
                                    answer_text: "",
                                    theme: 'painAnamnesis_SelfRatedFunction_MainGoal_manual',
                                    subgroup: 'painAnamnesis_SelfRatedFunction_MainGoal_manual',
                                    mapped_key: baseIdentifier,
                                    identifier: baseIdentifier,
                                    display_group: groupName,
                                    answer_text_role: 'goal_text'
                                  } 
                                }));
                                
                                // Track this item in groupExtraFields so it gets saved properly
                                setGroupExtraFields(prev => {
                                  const existing = prev[groupName] || [];
                                  if (!existing.includes(goalKey)) {
                                    return { ...prev, [groupName]: [...existing, goalKey] };
                                  }
                                  return prev;
                                });
                              };
                              
                              return (
                                <Box key={goalKey} style={{ marginLeft: 18, marginTop: 4, marginBottom: 6, display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', width: 'calc(100% - 18px)', minWidth: 0, gap: 8 }}>
                                  <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                                    <TextField
                                      value={(functionGoalInputs[goalKey] && functionGoalInputs[goalKey].customLabel) ? functionGoalInputs[goalKey].customLabel : 'Patient described functional goal (PSFS):'}
                                      onChange={(e) => setFunctionGoalInputs(prev => ({ ...prev, [goalKey]: { ...inputs, customLabel: e.target.value } }))}
                                      margin="dense"
                                      multiline
                                      rowsMax={2}
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', fontWeight: 500, padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                      style={{ flex: '0 1 clamp(140px, 26vw, 350px)', minWidth: 120, marginRight: 8 }}
                                    />
                                    <TextField
                                      placeholder="description"
                                      value={inputs.activity}
                                      onChange={(e) => {
                                        const newActivity = e.target.value;
                                        setFunctionGoalInputs(prev => ({ ...prev, [goalKey]: { ...inputs, activity: newActivity } }));
                                        updateGoalEditableValues(newActivity, inputs.score);
                                      }}
                                      margin="dense"
                                      multiline
                                      rowsMax={2}
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                      style={{ flex: '1 1 0', minWidth: 120 }}
                                    />
                                  </Box>
                                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <TextField
                                      placeholder="ability"
                                      type="number"
                                      className={classes.noNumberSpinner}
                                      value={inputs.score}
                                      onChange={(e) => {
                                        const newScore = e.target.value;
                                        setFunctionGoalInputs(prev => ({ ...prev, [goalKey]: { ...inputs, score: newScore } }));
                                        updateGoalEditableValues(inputs.activity, newScore);
                                      }}
                                      margin="dense"
                                      InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', width: 'clamp(35px, 3.8vw, 45px)', padding: 1 } }}
                                      inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                    />
                                    <Typography variant="body2" style={{ color: '#666', minWidth: 34 }}> / 100</Typography>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => {
                                        setFunctionGoalCreators(prev => ({ ...prev, [groupName]: (prev[groupName] || []).filter(id => id !== creatorId) }));
                                        setFunctionGoalInputs(prev => ({ ...prev, [goalKey]: undefined }));
                                        setEditingItems(prev => {
                                          const next = { ...(prev || {}) };
                                          delete next[goalKey];
                                          return next;
                                        });
                                        setEditableValues(prev => {
                                          const next = { ...(prev || {}) };
                                          delete next[goalKey];
                                          return next;
                                        });
                                        setGroupExtraFields(prev => ({
                                          ...(prev || {}),
                                          [groupName]: (prev && prev[groupName] ? prev[groupName] : []).filter(id => id !== goalKey)
                                        }));
                                      }} 
                                      style={{ marginLeft: 6 }}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              );
                              });
                            }
                            
                            // Standard rendering for other groups
                            try {
                              const gkeys = GROUPING_MAP[groupName] || [];
                              // ensure at least one field exists
                              if (!groupExtraFields[groupName] || !groupExtraFields[groupName].length) {
                                ensureGroupHasField(groupName, gkeys);
                              }
                              const ids = groupExtraFields[groupName] || [];
                              return ids.map((fid, fi) => {
                                const val = (editableValues && editableValues[fid] && (editableValues[fid].journalPhrase !== undefined)) ? String(editableValues[fid].journalPhrase) : '';
                                const handleTooltipOpen = () => {
                                  setTooltipOpen(prev => ({ ...prev, [fid]: true }));
                                  if (tooltipTimeoutsRef.current[fid]) clearTimeout(tooltipTimeoutsRef.current[fid]);
                                  tooltipTimeoutsRef.current[fid] = setTimeout(() => setTooltipOpen(prev => ({ ...prev, [fid]: false })), 9000);
                                };
                                const handleTooltipClose = () => {
                                  if (tooltipTimeoutsRef.current[fid]) clearTimeout(tooltipTimeoutsRef.current[fid]);
                                  setTooltipOpen(prev => ({ ...prev, [fid]: false }));
                                };
                                return (
                                  <Box key={`extra-${fid}`} style={{ marginLeft: 18, marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                                    {/*<Tooltip title="After clicking the text field, you can Press Enter to add another field" placement="top" arrow open={tooltipOpen[fid] || false} onOpen={handleTooltipOpen} onClose={handleTooltipClose}>*/}
                                      <TextField
                                        value={val}
                                        onChange={(e) => setEditableValues(prev => ({ ...(prev || {}), [fid]: { ...(prev && prev[fid] ? prev[fid] : {}), journalPhrase: e.target.value } }))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.ctrlKey) {
                                            e.preventDefault();
                                            appendGroupFieldAfter(groupName, gkeys, fid);
                                          }
                                        }}
                                        fullWidth
                                        margin="dense"
                                        multiline
                                        placeholder="Add note...    |    Press Ctrl+Enter to add another field"
                                        InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                        inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                      />
                                    {/*</Tooltip>*/}
                                    <IconButton size="small" onClick={() => removeGroupField(groupName, fid)} style={{ marginLeft: 6 }}>
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                );
                              });
                            } catch (e) { return null; }
                          })()}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    ));
                  })()}
                </Box>
              <Box
                mt={2}
                display="flex"
                alignItems="center"   
              >
            <Grid container direction="column" style={{marginRight: 40}}>
              <Grid item alignItems="left">

              </Grid>
            </Grid>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} lg={6} direction="column" style={{marginRight: 0, paddingLeft: 12}}>
          {/* SECOND COLUMN: Journal sections */}
              <Grid item style={{marginLeft: 0, backgroundColor: '#fff', borderRadius: 12}}>
                {/* Journal for initial assessment */}
                <Box mt={0} mb={1} style={{ marginLeft: 0, border: '1px solid #ddd', borderRadius: 12, padding: 12, height: '100%'}}>
                  {journalLoading && <Typography>Loading journal...</Typography>}
                  {journalError && <Typography color="error">Error: {journalError}</Typography>}
                  {journalSections && Object.keys(journalSections).length === 0 && (
                    <Typography>No journal data available.</Typography>
                  )}
                  {journalSections && (() => {
                    // Ordered section keys user requested
                    const orderedKeys = [
                      'keySymptom',
                      'painAnamnesis',
                      'painAnamnesis_location',
                      'painAnamnesis_painCharacteristics',
                      'redflags',
                      'painAnamnesis_pattern',
                      'painAnamnesis_affectedActivities',
                      'painAnamnesis_cause_how_issue_occured',
                      'painAnamnesis_duration',
                      'painAnamnesis_frequency',
                      'painAnamnesis_painAtBest',
                      'painAnamnesis_painCurrent',
                      'painAnamnesis_painAtWorst',
                      'painAnamnesis_painWithActivity',
                      'painAnamnesis_painAtRest',
                      'painAnamnesis_difficulty',
                      'painAnamnesis_custom',
                      'painAnamnesis_aggravatingFactors',
                      'painAnamnesis_relievingFactors',
                      'painAnamnesis_doctor',
                      'painAnamnesis_doctorDiagnosis',
                      'secondary_multimorbid_doctorDiagnosis',
                      'painAnamnesis_development',
                      'painAnamnesis_episodes',
                      'painAnamnesis_function',
                      'painAnamnesis_knownCondition',
                      'movement_test',
                      'clinicalTest',
                      'SymptomProvoking_clinicalTest',
                      'SymptomImproving_clinicalTest',
                      'keyFunctionalLimitations',
                      'painAnamnesis_SelfRatedFunction_MainGoal',
                      'riskFactors',
                      'riskFactors_advice',
                      'diagnosis',
                    ];

                   
                    const tagOrder = [...orderedKeys];

                    // Collect all items and mark consumed to avoid duplicates
                    const consumed = new Set();
                    const raw = journalSections;

                    // resolve the matched answer object for an item (prefers selected_answer, then selected_answer_meta, then legacy fields)
                    const findMatchedAnswer = (item) => {
                      if (!item) return null;
                      const sel = item.selected_answer || item.selected_answer_meta || {};
                      // Collect many possible candidate values from sel and item (handle different key names)
                      const candidates = [];
                      const addIf = (v) => { if (v !== undefined && v !== null && v !== '') candidates.push(String(v)); };
                      addIf(sel.answer_id);
                      addIf(sel.answerId);
                      addIf(sel.value);
                      addIf(sel.label);
                      addIf(sel.text);
                      addIf(sel.answerText);
                      addIf(sel.answer_text);
                      addIf(sel.canonical);
                      addIf(sel.text_value);
                      addIf(sel.numeric_value);
                      addIf(item.answer_id);
                      addIf(item.answerId);
                      addIf(item.answer_text);
                      addIf(item.answerText);
                      // also include selected_answer_meta.value if present
                      if (item.selected_answer_meta && item.selected_answer_meta.value) addIf(item.selected_answer_meta.value);

                      const answers = (item.question && Array.isArray(item.question.answers)) ? item.question.answers : [];
                      if (!answers.length) return null;
                      for (const a of answers) {
                        if (!a) continue;
                        const aVals = [];
                        const pushA = (v) => { if (v !== undefined && v !== null) aVals.push(String(v)); };
                        pushA(a.value);
                        pushA(a.id);
                        pushA(a.answerId);
                        pushA(a.label);
                        pushA(a.text);
                        pushA(a.answerText);
                        pushA(a.answer_text);
                        // compare intersection
                        for (const c of candidates) {
                          if (aVals.includes(String(c))) return a;
                        }
                      }
                      return null;
                    };

                    const getItemTags = (item) => {
                      const ans = findMatchedAnswer(item);
                      if (ans && Array.isArray(ans.tags) && ans.tags.length) return ans.tags;
                      // fallback to selected_answer_meta tags (legacy server payload)
                      if (item.selected_answer_meta && Array.isArray(item.selected_answer_meta.tags) && item.selected_answer_meta.tags.length) return item.selected_answer_meta.tags;
                      if (item.question && Array.isArray(item.question.tags) && item.question.tags.length) return item.question.tags;
                      if (Array.isArray(item.tags) && item.tags.length) return item.tags;
                      return [];
                    };

                    const pickItemsForKey = (key) => {
                      const out = [];
                      Object.entries(raw).forEach(([theme, subgroups]) => {
                        Object.entries(subgroups).forEach(([subgroup, items]) => {
                          items.forEach(i => {
                            const id = i.question_id || JSON.stringify(i);
                            if (consumed.has(id)) return;
                            const qidentifier = (i.identifier || (i.question && i.question.identifier)) || '';
                            const identifierMatch = qidentifier === key;
                            const ans = findMatchedAnswer(i);
                            const answerSubgroup = (ans && (ans.subgroup || ans.type)) || null;
                            // Use canonical theme (without _manual) for matching
                            const canonicalTheme = typeof theme === 'string' ? theme.replace(/_manual$/, '') : theme;
                            const combinedKey = `${canonicalTheme}_${subgroup}`;
                            const subgroupMatch = (i.subgroup === key) || (answerSubgroup === key) || ((i.question && i.question.subgroup) === key) || (combinedKey === key) || (i.subgroup && `${canonicalTheme}_${i.subgroup}` === key);
                            const combinedKeyForItem = (i.subgroup ? `${canonicalTheme}_${i.subgroup}` : null);
                            // Match both canonical theme and theme_manual variants to the same key
                            const themeMatch = (theme === key || canonicalTheme === key) && !(i.subgroup && i.subgroup !== 'default' && Array.isArray(orderedKeys) && combinedKeyForItem && orderedKeys.includes(combinedKeyForItem));
                            const tags = getItemTags(i);
                            const tagMatch = Array.isArray(tags) && tags.includes(key);
                            // derived matches...
                            let derivedMatch = false;
                            const qtext = (i.question && (i.question.question || '') || '').toString().toLowerCase();
                            const qid = (i.identifier || (i.question && i.question.identifier) || '').toString().toLowerCase();
                            if (key === 'painAnamnesis_doctor') {
                              if (qid.includes('doctor') || qtext.includes('doctor') || qtext.includes('examined')) derivedMatch = true;
                            }
                            if (key === 'painAnamnesis_doctorDiagnosis') {
                              if (qid.includes('diagnos') || qtext.includes('diagnos') || qtext.includes('diagnose')) derivedMatch = true;
                            }
                            if (identifierMatch || subgroupMatch || themeMatch || tagMatch || derivedMatch) {
                              consumed.add(id);
                              out.push(i);
                            }
                          });
                        });
                      });
                      return out;
                    };

                    const orderedSections = {};
                    // helper: determine tag priority for an item (prefers answer-level tags)
                    const getTagPriority = (item) => {
                      const tags = getItemTags(item) || [];
                      if (!Array.isArray(tags) || tags.length === 0) return tagOrder.length;
                      for (let i = 0; i < tagOrder.length; i++) {
                        if (tags.includes(tagOrder[i])) return i;
                      }
                      return tagOrder.length;
                    };

                    // helper: sort items by tag priority then by subgroup
                    const sortItems = (items) => {
                      return items.sort((a, b) => {
                        const pa = getTagPriority(a);
                        const pb = getTagPriority(b);
                        if (pa !== pb) return pa - pb;
                        // Check if item is manually-created (by theme suffix or id)
                        const isManualA = (a.manual === true) || (a.id && String(a.id).startsWith('manual-')) || (a.theme && String(a.theme).endsWith('_manual'));
                        const isManualB = (b.manual === true) || (b.id && String(b.id).startsWith('manual-')) || (b.theme && String(b.theme).endsWith('_manual'));
                        // Manual items should render last
                        if (isManualA && !isManualB) return 1;
                        if (!isManualA && isManualB) return -1;
                        const ansA = findMatchedAnswer(a);
                        const ansB = findMatchedAnswer(b);
                        const sa = ( (ansA && (ansA.subgroup || ansA.type)) || a.subgroup || (a.question && a.question.subgroup) || '' ).toString();
                        const sb = ( (ansB && (ansB.subgroup || ansB.type)) || b.subgroup || (b.question && b.question.subgroup) || '' ).toString();
                        return sa.localeCompare(sb);
                      });
                    };

                    orderedKeys.forEach(k => {
                      const items = pickItemsForKey(k);
                      if (items && items.length) orderedSections[k] = sortItems(items);
                    });

                    // Any remaining items go into 'other'
                    const remaining = [];
                    Object.entries(raw).forEach(([theme, subgroups]) => {
                      Object.entries(subgroups).forEach(([subgroup, items]) => {
                        items.forEach(i => {
                          const id = i.question_id || JSON.stringify(i);
                          if (!consumed.has(id)) {
                            consumed.add(id);
                            remaining.push(i);
                          }
                        });
                      });
                    });
                    if (remaining.length) orderedSections.other = sortItems(remaining);

                    // Group orderedSections into more user-friendly frontend sections
                    const grouping = {
                      'Evaluation and indication for physiotherapy:': [
                        'diagnosis',
                        'diagnosis_manual'
                      ],
                      'Treatmentplan': [
                        'treatmentPlan',
                        'treatmentPlan_manual'
                      ],
                      'Initial Advice': [
                        'riskFactors',
                        'riskFactors_advice',
                        'riskFactors_manual',
                        'riskFactors_advice_manual'
                      ],
                      'Information to the patient': [
                        
                      ],
                      'Patient consent': [
                        'patientConsent',
                        'patientConsent_manual'
                      ]
                    };

                    const grouped = {};
                    // Collect grouped items
                    Object.entries(grouping).forEach(([groupName, keys]) => {
                      grouped[groupName] = [];
                      keys.forEach(k => {
                        if (orderedSections[k]) {
                          grouped[groupName] = grouped[groupName].concat(orderedSections[k]);
                        }
                      });
                      // Re-sort after concatenation to ensure manual items appear last
                      if (grouped[groupName] && grouped[groupName].length) {
                        grouped[groupName] = sortItems(grouped[groupName]);
                      }
                    });

                    // Any remaining orderedSections not in grouping are intentionally omitted
                    // (The 'Other' group has been removed per UX request)

                    // helper to determine if an item is a pain-scale item (use subgroup/tags/identifier)
                    const painKeys = new Set([
                      'painAnamnesis_painCurrent', 'painAnamnesis_painAtBest', 'painAnamnesis_painAtWorst', 'painAnamnesis_frequency', 'painAnamnesis_painWithActivity', 'painAnamnesis_painAtRest', 'painAnamnesis_difficulty', 'painAnamnesis_custom',
                      // manual subgroups (no prefix)
                      'painCurrent', 'painAtBest', 'painAtWorst', 'painAtRest', 'painWithActivity', 'frequency', 'custom'
                    ]);
                    const sliderKeys = new Set(['painAnamnesis_SelfRatedFunction_MainGoal']);
                    const sliderTypes = new Set(['selfRatedFunction', 'selfRated_NPS', 'selfRatedPain']);
                    const isPainItem = (it) => {
                      if (!it) return false;
                      // direct pain keys (cover missing subgroup by checking identifier and theme too)
                      if (painKeys.has(it.subgroup)) return true;
                      if (it.identifier && painKeys.has(it.identifier)) return true;
                      if (painKeys.has(it.theme)) return true;
                      // explicit slider group (PSFS / self-rated function) should also be shown as numeric
                      if (it.subgroup === 'painAnamnesis_SelfRatedFunction_MainGoal' || it.identifier === 'painAnamnesis_SelfRatedFunction_MainGoal') return true;
                      // question type based: slider-backed types
                      if (it.question && sliderTypes.has(it.question.type)) return true;
                      // tags can indicate grouping
                      const t = Array.isArray(it.tags) ? it.tags : (it.question && it.question.tags) || [];
                      for (const pk of painKeys) if (Array.isArray(t) && t.includes(pk)) return true;
                      for (const sk of sliderKeys) if (Array.isArray(t) && t.includes(sk)) return true;
                      return false;
                    };

                    // Render grouped sections
                    return Object.entries(grouped).map(([groupName, items]) => (
                      <Box key={groupName} mb={0} >
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" style={{ color: '#FF7F27', fontWeight: 600, marginTop: 0 }}>
                            {groupName}
                          </Typography>
                            {(props.setActiveTab && (groupName === 'Treatmentplan' || groupName === 'Initial Advice')) && (
                            <>
                            {groupName === 'Treatmentplan' && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                                  style={{ marginRight: 8, color: '#002D55', padding: 4 }}
                                >
                                  <BuildIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                  anchorEl={actionMenuAnchor}
                                  open={Boolean(actionMenuAnchor)}
                                  onClose={() => setActionMenuAnchor(null)}
                                  keepMounted
                                >
                                  <MenuItem
                                    disabled={!canAdaptiveExercise}
                                    onClick={() => {
                                      setActionMenuAnchor(null);
                                      let targetPhaseId = expandedTreatmentPhase;
                                      if (!targetPhaseId && Array.isArray(patientPhases) && patientPhases.length > 0) {
                                        const first = patientPhases[0];
                                        targetPhaseId = first && (first.id || first.phase_id || first.phaseId || first.pk);
                                      }
                                      openProgramBuilder(targetPhaseId, false);
                                    }}
                                  >
                                    Edit in program builder
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      setActionMenuAnchor(null);
                                      setShowTemplateSelector(true);
                                    }}
                                  >
                                    Select template
                                  </MenuItem>
                                </Menu>
                                {showTemplateSelector ? (
                                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                                    <select
                                      style={{ padding: 'clamp(3px, 0.45vw, 4px) clamp(8px, 1vw, 10px)', borderRadius: 6, border: '1px solid #002D55', color: '#002D55', marginRight: 8, maxWidth: 'clamp(150px, 18vw, 200px)', fontSize: 'clamp(0.72rem, 0.9vw, 0.81rem)', height: 'clamp(24px, 3vw, 28px)' }}
                                      value={selectedTemplate}
                                      onChange={e => setSelectedTemplate(e.target.value)}
                                    >
                                      <option value="">-- Select Template --</option>
                                      {diagnosesList && diagnosesList.length ? (
                                        diagnosesList.map(d => (
                                          <option key={d.id} value={d.id}>{d.title || d.diagnosis}</option>
                                        ))
                                      ) : (
                                        <>
                                          <option value="Cervical Radiculopathy">Cervical Radiculopathy</option>
                                          <option value="Cervicogenic Headache">Cervicogenic Headache</option>
                                          <option value="Neck Strain">Neck Strain</option>
                                        </>
                                      )}
                                    </select>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setShowTemplateSelector(false);
                                        setSelectedTemplate("");
                                      }}
                                      style={{ marginRight: 4, color: '#002D55', padding: 4 }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <Tooltip title="Applying a template will overwrite the current exercises and advices.">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!selectedTemplate}
                                          onClick={() => {
                                            if (selectedTemplate) {
                                              openTemplatePreviewDialog();
                                            }
                                          }}
                                          style={{ marginRight: 8, color: selectedTemplate ? '#FF7F27' : '#ccc', padding: 4 }}
                                        >
                                          <CheckIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </div>
                                ) : null}
                              </>
                            )}
                            {groupName === 'Initial Advice' && (
                              <Button
                                size="small"
                                className={classes.Button}
                                variant="outlined"
                                disabled={!canAdaptiveExercise}
                                onClick={() => {
                                  // Keep advice view and select the current advice phase
                                  let targetPhaseId = expandedAdvicePhase;
                                  if (!targetPhaseId && Array.isArray(patientPhases) && patientPhases.length > 0) {
                                    const first = patientPhases[0];
                                    targetPhaseId = first && (first.id || first.phase_id || first.phaseId || first.pk);
                                  }
                                  openProgramBuilder(targetPhaseId, true);
                                  return;
                                }}
                                style={{ marginLeft: 8, textTransform: 'none', fontSize: 'clamp(0.72rem, 0.9vw, 0.81rem)', fontWeight: 600, minWidth: 'clamp(44px, 5vw, 50px)', padding: 'clamp(3px, 0.45vw, 4px) clamp(10px, 1.3vw, 14px)' }}
                              >
                                Edit in program builder
                              </Button>
                            )}
                            </>
                          )}
                        </Box>
                        {/* Insert diagnosis controls under the Evaluation group */}
                        {groupName === 'Evaluation and indication for physiotherapy:' && (
                          <Box container spacing={2} alignItems="center" style={{ marginTop: 11, border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
                            <Typography variant="body2" style={{ marginLeft: 0, color: "#002D55"}} >
                              <span style={{marginLeft: 0, marginBottom: 0, display: 'inline-block'}}>
                                Based on the self-reported answers, our evaluation suggests the best explanation of the patient reported symptoms are:
                              </span> 
                            </Typography>
                            <Grid item>
                              <Typography style={{ margin: 6, marginLeft: 0, fontWeight: 600, color: "#002D55"}} variant="body2">
                                {(() => {
                                  // Helper: safely extract a display string from a diagnosis value that may be a nested object
                                  const extractDiagTitle = (val) => {
                                    if (!val) return '';
                                    if (typeof val === 'string') return val;
                                    if (typeof val === 'object') return val.title || val.identifier || (typeof val.diagnosis === 'string' ? val.diagnosis : '') || '';
                                    return '';
                                  };

                                  if (editedDiagnosis) {
                                    const safe = typeof editedDiagnosis === 'string' ? editedDiagnosis : extractDiagTitle(editedDiagnosis);
                                    return <>Edited diagnosis: {safe}</>;
                                  }
                                  
                                  // Prefer the scoped diagnosis prop from parent (consolidated fetch by treatment_id)
                                  if (diagnosis && typeof diagnosis === 'object' && !Array.isArray(diagnosis)) {
                                    const title = extractDiagTitle(diagnosis.title || diagnosis.identifier) || extractDiagTitle(diagnosis.diagnosis) || '';
                                    if (title) return <>Suggested diagnosis: {title}</>;
                                  }
                                  
                                  // Fallback: search in selectedPatient.diagnosis array
                                  const diags = (selectedPatient && Array.isArray(selectedPatient.diagnosis)) ? selectedPatient.diagnosis : [];
                                  const chosenTid = selectedPatient && (selectedPatient.treatment_id || (selectedPatient.treatment && selectedPatient.treatment.id));
                                  const chosenDiagId = selectedPatient && (selectedPatient.selected_diagnosis_id || selectedPatient.diagnosis_id);
                                  let activeDiag = null;
                                  if (chosenTid && diags.length) {
                                    activeDiag = diags.find(d => d.treatment_id && d.treatment_id === chosenTid) || null;
                                  }
                                  if (!activeDiag && chosenDiagId && diags.length) {
                                    activeDiag = diags.find(d => (d.diagnosis_id || d.id) === chosenDiagId) || null;
                                  }
                                  if (!activeDiag && diags.length === 1) {
                                    activeDiag = diags[0];
                                  }
                                  if (activeDiag) {
                                    const title = extractDiagTitle(activeDiag.title || activeDiag.identifier) || extractDiagTitle(activeDiag.diagnosis) || '';
                                    return <>Suggested diagnosis: {title}</>;
                                  }
                                  if (diags && diags.length > 0) {
                                    const fallback = diags[diags.length - 1];
                                    const title = fallback && (extractDiagTitle(fallback.title || fallback.identifier) || extractDiagTitle(fallback.diagnosis));
                                    return <>Suggested diagnosis: {title || props.title}</>;
                                  }
                                  return <>Suggested diagnosis: {props.title}</>;
                                })()}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                size='small'
                                className={classes.diagnosisDecisionButton}
                                style={{
                                  background: approveState === 'approved' ? '#00b894' : 'rgba(0,0,0,0.54)',
                                  color: '#fff',
                                  margin: 8,
                                }}
                                onClick={() => setApproveState('approved')}
                              >
                                {approveState === 'approved' ? 'Approved' : 'Approve'}
                              </Button>
                              <Button
                                variant="outlined"
                                size='small'
                                className={clsx(classes.headerSecondaryButton, classes.diagnosisDecisionButton)}
                                onClick={() => setApproveState('change')}
                              >
                                Change
                              </Button>
                            </Grid>
                            {approveState === 'change' && (
                              <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item>
                                    <Typography variant='body2' style={{marginLeft: 0, marginRight: 10, color: '#002D55', fontWeight: 600 }}>
                                      Select or type a new diagnosis:
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    {diagnosisSelect !== 'Other' ? (
                                      <Autocomplete
                                        options={diagnosisAutocompleteOptions}
                                        value={selectedDiagnosisOption}
                                        inputValue={diagnosisSearchInput}
                                        onInputChange={(event, newInputValue, reason) => {
                                          if (reason === 'input' || reason === 'clear' || reason === 'reset') {
                                            setDiagnosisSearchInput(newInputValue);
                                          }
                                        }}
                                        onChange={(event, newValue) => {
                                          if (!newValue) {
                                            setDiagnosisSelect('');
                                            setDiagnosisSearchInput('');
                                            return;
                                          }

                                          const nextId = String(newValue.id || newValue.title || newValue.diagnosis || '');
                                          if (nextId === 'Other') {
                                            setDiagnosisSelect('Other');
                                            setDiagnosisInput('');
                                            setDiagnosisSearchInput('');
                                            return;
                                          }

                                          setDiagnosisSelect(nextId);
                                          setDiagnosisSearchInput(getDiagnosisOptionLabel(newValue));
                                        }}
                                        getOptionLabel={getDiagnosisOptionLabel}
                                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                        autoHighlight
                                        openOnFocus
                                        clearOnBlur={false}
                                        renderOption={(option) => (
                                          <div className={classes.diagnosisAutocompleteOption}>
                                            {getDiagnosisOptionLabel(option)}
                                          </div>
                                        )}
                                        classes={{ paper: classes.diagnosisAutocompletePaper }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Search diagnoses"
                                            className={classes.diagnosisAutocompleteRoot}
                                            InputProps={{
                                              ...params.InputProps,
                                              style: { minWidth: 'clamp(220px, 28vw, 320px)', backgroundColor: '#FFF', borderRadius: 14 },
                                            }}
                                          />
                                        )}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="Type diagnosis..."
                                        style={{ padding: 'clamp(4px, 0.7vw, 6px) clamp(9px, 1.2vw, 12px)', borderRadius: 6, border: '1px solid #002D55', color: '#002D55', minWidth: 'clamp(180px, 28vw, 260px)' }}
                                        value={diagnosisInput}
                                        onChange={e => setDiagnosisInput(e.target.value)}
                                      />
                                    )}
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      value={diagnosisReason}
                                      onChange={(e) => setDiagnosisReason(e.target.value)}
                                      placeholder="Reason for diagnosis change (required)"
                                      InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                    />
                                  </Grid>
                                  {diagnosisSelect === 'Other' && (
                                    <>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={diagnosisIndication}
                                          onChange={(e) => setDiagnosisIndication(e.target.value)}
                                          placeholder="Indication for physiotherapy"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={prognosisExpectedOutcome}
                                          onChange={(e) => setPrognosisExpectedOutcome(e.target.value)}
                                          placeholder="Expected outcome"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={prognosisTypicalSymptoms}
                                          onChange={(e) => setPrognosisTypicalSymptoms(e.target.value)}
                                          placeholder="Typical character symptoms"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={prognosisAverageDuration}
                                          onChange={(e) => setPrognosisAverageDuration(e.target.value)}
                                          placeholder="Expected average duration"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={prognosisDevelopment}
                                          onChange={(e) => setPrognosisDevelopment(e.target.value)}
                                          placeholder="Expected development"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          className={clsx(classes.diagnosisOtherTextField, classes.orangeFocused)}
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          value={prognosisIfWorsens}
                                          onChange={(e) => setPrognosisIfWorsens(e.target.value)}
                                          placeholder="If symptoms worsen"
                                          InputProps={{ style: { fontSize: 14, color: '#324158' } }}
                                        />
                                      </Grid>
                                    </>
                                  )}
                                  <Grid item>
                                    <Box className={classes.diagnosisActionRow}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        className={classes.headerSecondaryButton}
                                        classes={{ root: classes.headerControlButton }}
                                        onClick={() => {
                                          // Reset selection/input and hide change controls
                                          setDiagnosisSelect('');
                                          setDiagnosisInput('');
                                          setDiagnosisSearchInput('');
                                          setApproveState(null);
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        className={classes.headerPrimaryButton}
                                        classes={{ root: classes.headerControlButton }}
                                        disabled={
                                          !((diagnosisReason && diagnosisReason.trim()) && ((diagnosisSelect && diagnosisSelect !== 'Other') || (diagnosisSelect === 'Other' && diagnosisInput && diagnosisInput.trim() && diagnosisIndication && diagnosisIndication.trim() && prognosisExpectedOutcome && prognosisExpectedOutcome.trim() && prognosisTypicalSymptoms && prognosisTypicalSymptoms.trim() && prognosisAverageDuration && prognosisAverageDuration.trim() && prognosisDevelopment && prognosisDevelopment.trim() && prognosisIfWorsens && prognosisIfWorsens.trim())))
                                          || !diagnosisSearchHasMatches
                                        }
                                        onClick={async () => {
                                          try {
                                            if (!(diagnosisReason && diagnosisReason.trim())) {
                                              alert('Please provide a reason for the diagnosis change.');
                                              return;
                                            }

                                            if (diagnosisSelect && diagnosisSelect !== 'Other') {
                                              const diagId = diagnosisSelect;
                                              queueDiagnosisChangeConfirmation({
                                                type: 'existing',
                                                diagId,
                                                reason: diagnosisReason
                                              });
                                              return;
                                            }

                                            if (diagnosisSelect === 'Other') {
                                              if (diagnosisInput && diagnosisInput.trim() && diagnosisIndication && diagnosisIndication.trim()) {
                                                queueDiagnosisChangeConfirmation({
                                                  type: 'new',
                                                  diagnosisTitle: diagnosisInput.trim(),
                                                  indication: diagnosisIndication.trim(),
                                                  prognosis: {
                                                    Expected_outcome: prognosisExpectedOutcome.trim(),
                                                    Typical_character_Symptoms: prognosisTypicalSymptoms.trim(),
                                                    Expected_average_duration: prognosisAverageDuration.trim(),
                                                    Expected_development: prognosisDevelopment.trim(),
                                                    If_your_symptoms_worsens: prognosisIfWorsens.trim()
                                                  },
                                                  reason: diagnosisReason
                                                });
                                                return;
                                              }

                                              alert('Please provide the diagnosis, indication and prognosis details for the new diagnosis.');
                                            }
                                          } catch (err) {
                                            console.error('Error queuing diagnosis change confirmation:', err);
                                            alert('An error occurred while preparing the diagnosis change.');
                                          }
                                        }}
                                      >
                                        Confirm
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                            )}
                            {/* Separate indication row below diagnosis/buttons */}
                            {(() => {
                              const indication = getIndicationText();
                              return indication ? (
                                <Grid item xs={12} style={{ marginTop: 8 }}>
                                  <Typography style={{ marginLeft: 0, marginTop: 6, color: '#002D55' }} variant="body2">
                                    <span style={{ fontWeight: 600 }}>Indication for physiotherapy:</span>
                                  </Typography>
                                  <Typography style={{ marginLeft: 0, marginTop: 6, color: '#002D55' }} variant="body2">
                                    {indication}
                                  </Typography>
                                </Grid>
                              ) : null;
                            })()}
                          </Box>
                        )}
                        {/* Insert prognosis text under Information to the patient */}
                        {groupName === 'Information to the patient' && (() => {
                          try {
                            // Accept multiple possible shapes for diagnosis/prognosis coming from the API:
                            // - selectedPatient.diagnosis = [{ diagnosis: {...}, prognosis: {...} }, ...]
                            // - diagnosis prop = [{ diagnosis: {...}, prognosis: {...} }, ...]
                            // - legacy: diagnosis prop = { diagnosis: {...}, prognosis: {...} }
                            let diagObj = null;
                            if (Array.isArray(diagnosis) && diagnosis.length > 0) {
                              diagObj = diagnosis[0];
                            } else if (diagnosis && diagnosis.diagnosis) {
                              // diagnosis prop may be wrapper { diagnosis: {...}, prognosis: {...} }
                              diagObj = diagnosis;
                            } else if (selectedPatient && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length > 0) {
                              diagObj = selectedPatient.diagnosis[0];
                            }

                            if (!diagObj) return null;
                            // prognosis may be at diagObj.prognosis or diagObj.diagnosis.prognosis
                            const progRaw = diagObj.prognosis || (diagObj.diagnosis && diagObj.diagnosis.prognosis) || null;
                            const prog = appliedPrognosis || progRaw;
                            if (!prog) return null;

                            const rows = [];
                            if (prog.Expected_outcome) rows.push({key: 'Expected_outcome', label: 'Expected outcome', text: prog.Expected_outcome});
                            if (prog.Typical_character_Symptoms) rows.push({key: 'Typical_character_Symptoms', label: 'Typical symptoms', text: prog.Typical_character_Symptoms});
                            if (prog.Expected_average_duration) rows.push({key: 'Expected_average_duration', label: 'Expected duration', text: prog.Expected_average_duration});
                            if (prog.Expected_development) rows.push({key: 'Expected_development', label: 'Expected development', text: prog.Expected_development});
                            if (prog.If_your_symptoms_worsens) rows.push({key: 'If_your_symptoms_worsens', label: 'If symptoms worsen', text: prog.If_your_symptoms_worsens});
                            if (!rows.length) return null;

                            return (
                              <Box mt={0.5} mb={0.5} style={{ marginLeft: 0, border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
                                {rows.map((r) => {
                                  const isExpanded = globalEditMode || expandedInfoKey === r.key;
                                  return (
                                    <Box key={r.key} mb={0.5}>
                                      <Box onClick={() => setExpandedInfoKey(isExpanded ? null : r.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>{r.label}</Typography>
                                        <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>{isExpanded ? '▾' : '▸'}</Typography>
                                      </Box>
                                      <Collapse in={isExpanded}>
                                        <Box mt={0.5} mb={0.5} style={{ marginLeft: 6 }}>
                                              {globalEditMode ? (
                                                <TextField
                                                  fullWidth
                                                  multiline
                                                  margin="dense"
                                                  value={(prognosisEdits && Object.prototype.hasOwnProperty.call(prognosisEdits, r.key)) ? (prognosisEdits[r.key] || '') : String(r.text)}
                                                  onChange={(e) => handlePrognosisChange(r.key, e.target.value)}
                                                  InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158' } }}
                                                  inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                                />
                                              ) : (
                                                <Typography variant="body2" component="div" style={{ color: '#002D55', marginBottom: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                  {String(r.text)}
                                                </Typography>
                                              )}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          } catch (e) {
                            return null;
                          }
                        })()}

                        {/* Treatment plan: show exercises per phase (expandable per phase) */}
                        {groupName === 'Treatmentplan' && (() => {
                          try {
                            if (phasesLoading) return (
                              <Box mt={0.5} mb={0.5} style={{ marginLeft: 0 }}>
                                <Typography variant="body2">Loading treatment phases...</Typography>
                              </Box>
                            );
                            if (phasesError) return (
                              <Box mt={0.5} mb={0.5} style={{ marginLeft: 0 }}>
                                <Typography color="error" variant="body2">Error: {phasesError}</Typography>
                              </Box>
                            );

                            if (!patientPhases || !patientPhases.length) return null;

                            return (
                              <Box mt={1} mb={0.5} style={{ marginLeft: 0 }}>
                                {patientPhases.map((phase) => {
                                  const pid = phase.id || phase.phase_id || phase.phaseId || phase.pk;
                                  const pname = phase.name || phase.phase_name || phase.phaseName || `Phase`;
                                  const isExpanded = expandedTreatmentPhase === pid;
                                  const exData = phaseExercises[pid];

                                  return (
                                    <Box key={pid} mb={0.5} style={{border: '1px solid #ddd', borderRadius: 12, padding: 12}}>
                                      <Box onClick={() => {
                                        setExpandedTreatmentPhase(isExpanded ? null : pid);
                                        if (!isExpanded) fetchExercisesForPhase(pid);
                                      }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>{pname}</Typography>
                                        <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>{isExpanded ? '▾' : '▸'}</Typography>
                                      </Box>
                                      <Collapse in={isExpanded}>
                                        <Box mt={0.5} mb={0.5} style={{ marginLeft: 6 }}>
                                          {!exData && <Typography variant="body2">Loading exercises...</Typography>}
                                          {exData && exData.__error && (
                                            <Typography color="error" variant="body2">Error: {exData.__error}</Typography>
                                          )}
                                          {Array.isArray(exData) && exData.length === 0 && (
                                            <Typography variant="body2">No planned exercises for this phase.</Typography>
                                          )}
                                          {Array.isArray(exData) && exData.map((ex, xi) => {
                                            // Map common field names
                                            const title = ex.title || ex.name || ex.exercise_title || (ex.exercise && ex.exercise.title) || '';
                                            const repetitions = ex.repetitions || ex.reps || ex.sets_reps || (ex.exercise && ex.exercise.repetitions) || '';
                                            const sets = ex.sets || ex.set || ex.set_count || (ex.exercise && ex.exercise.sets) || '';
                                            const frequency = (ex.frequency && (ex.frequency.label || ex.frequency.name)) || ex.frequency || ex.freq || '';
                                            const time = ex.time || ex.duration || ex.session_time || '';
                                            return (
                                              <Box key={ex.id || `${pid}-${xi}`} mb={0.5}>
                                                <Typography variant="body2" className={classes.compactText} style={{ fontWeight: 700, marginBottom: 4 }}>{title}</Typography>
                                                <Typography variant="body2" className={classes.compactText}>
                                                  {repetitions ? `Repetitions: ${repetitions}` : null}{(repetitions && sets) ? ' · ' : ''}{sets ? `Sets: ${sets}` : null}{( (repetitions || sets) && frequency) ? ' · ' : ''}{frequency ? `Frequency: ${frequency}` : ''}{(frequency && time) ? ' · ' : ''}{time ? `Time: ${time}` : ''}
                                                </Typography>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          } catch (e) {
                            return null;
                          }
                        })()}

                        {/* Insert patient advices under the Advices group */}
                        {groupName === 'Initial Advice' && (() => {
                          try {
                            if (advicesLoading) return (
                              <Box mt={1} mb={1} style={{ marginLeft: 0 }}>
                                <Typography variant="body2">Loading advices...</Typography>
                              </Box>
                            );
                            if (advicesError) return (
                              <Box mt={1} mb={1} style={{ marginLeft: 0 }}>
                                <Typography color="error" variant="body2">Error: {advicesError}</Typography>
                              </Box>
                            );
                            if (!patientAdvices || !patientAdvices.length) return null;

                            return (
                              <Box mt={1} mb={1} style={{ marginLeft: 0, border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
                                {(() => {
                                  // Build a flat list of advice items for Phase 1
                                  const flat = [];
                                  patientAdvices.filter((phase) => {
                                    const phaseName = phase.phase_name || phase.phaseName || phase.name || '';
                                    return (/phase\s*1/i.test(String(phaseName)) || phase.phase === 1);
                                  }).forEach((phase, pi) => {
                                    const rawAdv = phase.advices || phase.advices_list || phase.advicesObject || phase.advices_object || phase.adv || null;
                                    let items = [];
                                    if (Array.isArray(rawAdv)) items = rawAdv;
                                    else if (rawAdv && typeof rawAdv === 'object') items = Object.values(rawAdv).flatMap(v => Array.isArray(v) ? v : [v]);
                                    if (!items.length && (phase.title || phase.text || phase.advice || phase.message)) items = [phase];
                                    items.forEach((adv, ai) => {
                                      flat.push({
                                        adv,
                                        key: adv.id || `${pi}-${ai}`,
                                      });
                                    });
                                  });

                                  if (!flat.length) return null;

                                  return flat.map(({adv, key}, idx) => {
                                    const title = adv.title || adv.name || adv.heading || adv.label || null;
                                    const text = adv.advice || adv.text || adv.advice_text || adv.message || adv.content || adv.description || null;
                                    if (!title && !text) return null;
                                    const isExpanded = expandedAdvicePhase === key;
                                    return (
                                      <Box key={key} mb={0.5}>
                                        <Box onClick={() => setExpandedAdvicePhase(isExpanded ? null : key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                                            {title}
                                          </Typography>
                                          <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                                            {isExpanded ? '▾' : '▸'}
                                          </Typography>
                                        </Box>
                                        <Collapse in={isExpanded}>
                                          <Box mt={0.5} mb={0.5} style={{ marginLeft: 6 }}>
                                            {text && (
                                              <Typography variant="body2" style={{ color: '#002D55', marginBottom: 4 }}>
                                                <span dangerouslySetInnerHTML={{ __html: String(text) }} />
                                              </Typography>
                                            )}
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                            );
                          } catch (e) {
                            return null;
                          }
                        })()}

                        {groupName === 'Patient consent' && (() => {
                          const consentSectionItems = journalSections && journalSections.patientConsent && Array.isArray(journalSections.patientConsent.default)
                            ? journalSections.patientConsent.default
                            : [];
                          const consentItem = (Array.isArray(items) && items.length > 0 ? items[0] : null) || consentSectionItems[0] || null;
                          const consentId = consentItem ? idForItem(consentItem) : null;
                          const consentVals = consentId ? (editableValues[consentId] || {}) : {};
                          const isEditingConsent = !!(consentId && (globalEditMode || !!editingItems[consentId]));
                          const defaultConsentText = hasStaffAuthoredConsentNote
                            ? PATIENT_CONSENT_CLINICIAN_DEFAULT_TEXT
                            : PATIENT_CONSENT_B2C_DEFAULT_TEXT;
                          const storedConsentText = consentItem
                            ? [consentItem.journalPhrase, consentItem.journal_text, consentItem.journal_phrase]
                              .find(value => value !== undefined && value !== null && String(value).trim() !== '')
                            : '';
                          const localConsentText = Object.prototype.hasOwnProperty.call(consentVals, 'journalPhrase')
                            ? String(consentVals.journalPhrase || '')
                            : '';
                          const visibleConsentText = (isEditingConsent ? localConsentText : '') || storedConsentText || defaultConsentText;

                          return (
                            <Box mt={0.5} mb={1} style={{ marginLeft: 0, border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
                              {isEditingConsent && consentId ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  margin="dense"
                                  value={visibleConsentText}
                                  onChange={(e) => setEditableValues(prev => ({ ...(prev || {}), [consentId]: { ...(prev && prev[consentId] ? prev[consentId] : {}), journalPhrase: e.target.value, metaJournalPhrase: '', theme: consentItem?.theme || 'patientConsent', subgroup: consentItem?.subgroup || 'default' } }))}
                                  InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                  inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                />
                              ) : (
                                <Typography variant="body2" component="div" style={{ color: '#002D55', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {String(visibleConsentText || defaultConsentText)}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}

                        <Table size="small" style={{ marginTop: 6, marginBottom: 8, tableLayout: 'fixed', width: '100%'}}>
                          <TableBody >
                            <TableRow >
                              <TableCell style={{ padding: 0, borderBottom: 'none' }}>
                                <Box ml={0} mb={0} style={{ padding: 4, width: '100%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                          
                          {items.map((item, idx) => {
                            if (groupName === 'Patient consent') return null;
                            
                            // Special-case pain-scale items: show only meta/journalPhrase + numeric_value
                            if (isPainItem(item)) {
                              const val = (item.numeric_value !== undefined && item.numeric_value !== null)
                                ? item.numeric_value
                                : item.numerical_value;
                              if (val === null || val === undefined) return null;
                              const matchedAns = findMatchedAnswer(item) || item.selected_answer_meta || null;

                              const id = idForItem(item);
                              const isEditing = (globalEditMode || !!editingItems[id]);
                              const editVals = editableValues[id] || {};
                              const kpiState = getKpiItemState(item, groupName);
                              if (kpiState.isKpi && kpiState.isDeleted) return null;

                              // Build ordered parts using the minimal snippet: meta, answer, journal
                              const meta = (matchedAns && (matchedAns.metaJournalPhrase || matchedAns.meta_journalPhrase)) ? (matchedAns.metaJournalPhrase || matchedAns.meta_journalPhrase) : (item.selected_answer_meta && (item.selected_answer_meta.metaJournalPhrase || item.selected_answer_meta.meta_journalPhrase) ? (item.selected_answer_meta.metaJournalPhrase || item.selected_answer_meta.meta_journalPhrase) : (item.metaJournalPhrase || item.meta_journalPhrase || ''));
                              const ans = (matchedAns && (matchedAns.answerText || matchedAns.text || matchedAns.label)) ? (matchedAns.answerText || matchedAns.text || matchedAns.label) : (item.answer_text || '');
                              const journal = item.journalPhrase || item.journal_text || item.journal_phrase || '';
                              const num = val != null ? String(val) : '';

                              // new order: metaJournalPhrase, answer_text, journalPhrase
                              const sanitizeP = (s) => (s || '').replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ').replace(/\s{2,}/g, ' ').trim();
                              const parts = [meta.toString().trim(), ans.toString().trim(), journal.toString().trim()].map(sanitizeP).filter(p => p && p !== num);

                              if (isEditing) {
                                  const freqKeys = new Set(['painAnamnesis_frequency', 'frequency']);
                                  const isFrequency = freqKeys.has(item.subgroup) || freqKeys.has(item.identifier) || freqKeys.has(item.theme);
                                  const isGoal = groupName === 'Patient goal for treatment:'
                                    || [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
                                      .filter(Boolean)
                                      .some(value => {
                                        const lowered = String(value).toLowerCase();
                                        return lowered.includes('painanamnesis_selfratedfunction_maingoal') || lowered === 'function_goal';
                                      });
                                  const sanitizeDesc = (s) => (s || '').replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ').replace(/\s{2,}/g, ' ').trim();
                                  const rawDesc = sanitizeDesc(((meta || '') + ' ' + (journal || '')).trim());
                                  // Custom baselines created via the creator form persist as
                                  // either "Patient reported: <activity>" (after the colon
                                  // fix in updateBaselineEditableValues) or the legacy
                                  // colon-less "Patient reported <activity>". Honor BOTH as
                                  // an equivalent label/activity boundary so the activity
                                  // field repopulates with the full custom metric name
                                  // (e.g. "pain with benchpress") on re-edit. The regex
                                  // intentionally does NOT consume "pain" — that word is
                                  // part of the clinician-defined metric, not the
                                  // boilerplate prefix.
                                  const colonlessPainPrefix = /^patient reported\s+/i;
                                  const hasColonlessPain = rawDesc.match(colonlessPainPrefix);
                                  const descColonIdx = rawDesc.indexOf(':');
                                  const labelText = isGoal
                                    ? 'Patient described functional goal (PSFS):'
                                    : isFrequency
                                    ? 'Patient reported frequency of symptoms:'
                                    : (descColonIdx >= 0
                                        ? rawDesc.slice(0, descColonIdx + 1).trim()
                                        : (hasColonlessPain ? hasColonlessPain[0].trim().replace(/\s*$/, ':') : rawDesc));
                                  const inlineActivityFromDesc = isGoal
                                    ? rawDesc
                                      .replace(/^patient described functional goal \(psfs\):\s*/i, '')
                                      .replace(/^the patient described their main functional goal \(psfs\) as:\s*/i, '')
                                      .trim()
                                    : (descColonIdx >= 0
                                        ? rawDesc.slice(descColonIdx + 1).trim()
                                        : (hasColonlessPain
                                            ? rawDesc.slice(hasColonlessPain[0].length).trim()
                                            : ''));
                                  const answerVal = (editVals && (editVals.answer_text !== undefined ? editVals.answer_text : ans)) || '';
                                  const activityVal = isFrequency
                                    ? ''
                                    : ((answerVal || '').toString().trim() !== '' ? answerVal : inlineActivityFromDesc);
                                  const numericVal = (editVals && (editVals.numerical_value !== undefined ? editVals.numerical_value : num)) || '';
                                  return (
                                    <Box key={item.question_id || idx} style={{ marginLeft: 18, marginBottom: 0, display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', minWidth: 0 }}>
                                      <TextField
                                        value={(editVals && editVals.custom_label !== undefined) ? editVals.custom_label : labelText}
                                        onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), custom_label: e.target.value } }))}
                                        margin="dense"
                                        multiline
                                        rowsMax={2}
                                        InputProps={{ disableUnderline: true, style: { fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)', color: '#324158', fontWeight: 500, padding: '2px 0' } }}
                                        style={{ flex: '0 1 500px', minWidth: 110, maxWidth: 250 }}
                                      />
                                      <TextField
                                        value={activityVal}
                                        onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), answer_text: e.target.value } }))}
                                        margin="dense"
                                        placeholder="describe activity"
                                        multiline
                                        rowsMax={2}
                                        style={{ flex: '1 1 220px', minWidth: 0 }}
                                        disabled={isFrequency}
                                        InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.4', color: '#324158' } }}
                                      />
                                      <Box style={{ marginLeft: 4, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                                        <TextField
                                          value={numericVal != null ? String(numericVal) : ''}
                                          onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [id]: { ...(prev && prev[id] ? prev[id] : {}), numerical_value: parseNumericSafe(e.target.value) } }))}
                                          placeholder="Score"
                                          margin="dense"
                                          InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.4', color: '#324158', width: 35  } }}
                                        />
                                        <Typography variant="body2" style={{ color: '#666', minWidth: 35 }}>{isFrequency ? ' / 7d' : ' / 100'}</Typography>
                                        {kpiState.isKpi && (
                                          <>
                                            <Tooltip title={kpiState.isPaused ? 'Resume in follow-up' : 'Pause from follow-up'}>
                                              <IconButton size="small" onClick={() => toggleExistingKpiPaused(item, groupName)}>
                                                {kpiState.isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete KPI">
                                              <IconButton size="small" onClick={() => markExistingKpiDeleted(item, groupName)}>
                                                <RemoveIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </>
                                        )}
                                      </Box>
                                      {/* global save on finish; inline Save/Cancel removed */}
                                    </Box>
                                  );
                                }

                              const displayNode = (
                                <>
                                  {(() => {
                                    const isGoalItem = groupName === 'Patient goal for treatment:'
                                      || [item.mapped_key, item.identifier, item.subgroup, item.theme, item.kpi_type]
                                        .filter(Boolean)
                                        .some(value => {
                                          const lowered = String(value).toLowerCase();
                                          return lowered.includes('painanamnesis_selfratedfunction_maingoal') || lowered === 'function_goal';
                                        });
                                    const isFrequencyItem = [item.subgroup, item.identifier, item.theme, item.mapped_key]
                                      .filter(Boolean)
                                      .some(value => String(value).toLowerCase().includes('frequency'));
                                    if (isFrequencyItem) {
                                      const frequencyParts = ['Patient reported frequency of symptoms:'];
                                      if (num) frequencyParts.push(`${num} / 7 days`);
                                      return frequencyParts.join(' — ');
                                    }
                                    if (!isGoalItem) return null;
                                    const goalLabel = 'Patient described functional goal (PSFS):';
                                    const rawActivity = (ans || journal || '').toString().trim();
                                    const activityText = rawActivity
                                      .replace(/^patient described functional goal \(psfs\):\s*/i, '')
                                      .replace(/^the patient described their main functional goal \(psfs\) as:\s*/i, '')
                                      .trim();
                                    const goalParts = [goalLabel];
                                    if (activityText) goalParts.push(activityText);
                                    if (num) goalParts.push(`${num} / 100`);
                                    return goalParts.join(' — ').replace(': —', ':');
                                  })() || (
                                    <>
                                  {parts.length > 0 && (
                                    <>
                                      {parts.map((p, i) => (
                                        <span key={i}>
                                          {i > 0 ? ' ' : ''}
                                          {p === ans ? <strong>{p}</strong> : p}
                                        </span>
                                      ))}
                                    </>
                                  )}
                                  {num ? (
                                    <>
                                      {parts.length ? ' ' : ''}
                                      {(() => {
                                        const freqKeys = new Set(['painAnamnesis_frequency', 'frequency']);
                                        const isFrequency = freqKeys.has(item.subgroup) || freqKeys.has(item.identifier) || freqKeys.has(item.theme);
                                        return <strong>{num}{isFrequency ? ' / 7 days' : ' / 100'}</strong>;
                                      })()}
                                    </>
                                  ) : null}
                                  {/* global edit toggles all fields */}
                                    </>
                                  )}
                                </>
                              );

                              return (
                                <Typography key={item.question_id || idx} variant="body2" className={classes.compactText}>
                                  {displayNode}
                                </Typography>
                              );
                            }

                            const matched = findMatchedAnswer(item) || item.selected_answer_meta || null;
                            const getField = (obj, ...keys) => {
                              if (!obj) return null;
                              for (const k of keys) {
                                if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
                              }
                              return null;
                            };
                            const answerMetaJP = getField(matched, 'metaJournalPhrase', 'meta_journalPhrase');
                            const answerJP = getField(matched, 'journalPhrase', 'journal_phrase');
                            const questionMetaJP = getField(item.question, 'metaJournalPhrase', 'meta_journalPhrase');
                            const questionJP = getField(item.question, 'journalPhrase', 'journal_phrase');
                            const itemMetaJP = getField(item, 'metaJournalPhrase', 'meta_journalPhrase', 'meta_journal_phrase');
                            const itemJP = getField(item, 'journalPhrase', 'journal_phrase');
                            const identifierBlob = [
                              item && item.identifier,
                              item && item.subgroup,
                              item && item.theme,
                              item && item.mapped_key,
                              item && item.question_id,
                              item && item.question && item.question.identifier,
                            ]
                              .filter(Boolean)
                              .map(v => String(v).toLowerCase())
                              .join(' ');
                            const redflagLabel = identifierBlob.includes('accompaniedbysystemicsymptoms')
                              ? 'Accompanying systemic symptoms:'
                              : ((identifierBlob.includes('redflags_1') || identifierBlob.includes('neurological redflag'))
                                ? 'Patient reported neurological redflag symptoms:'
                                : null);

                            let mainText = null;
                            if (redflagLabel) {
                              const redflagValue = [
                                answerJP,
                                getField(item, 'answer_text', 'answerText'),
                                itemJP,
                                questionJP,
                              ]
                                .find(v => v !== undefined && v !== null && String(v).trim() !== '');
                              if (redflagValue) {
                                mainText = `${redflagLabel} ${String(redflagValue).trim()}`;
                              } else if (questionMetaJP) {
                                mainText = String(questionMetaJP).trim();
                              }
                            } else if (answerMetaJP !== undefined && answerMetaJP !== null && answerMetaJP !== '') {
                              mainText = String(answerMetaJP) + (answerJP ? (' ' + String(answerJP)) : '');
                            } else if (answerJP !== undefined && answerJP !== null && answerJP !== '') {
                              mainText = String(answerJP);
                            } else if (itemMetaJP !== undefined && itemMetaJP !== null && itemMetaJP !== '') {
                              mainText = String(itemMetaJP) + (itemJP ? (' ' + String(itemJP)) : '');
                            } else if (itemJP !== undefined && itemJP !== null && itemJP !== '') {
                              mainText = String(itemJP);
                            } else if (questionMetaJP !== undefined && questionMetaJP !== null && questionMetaJP !== '') {
                              mainText = String(questionMetaJP) + (questionJP ? (' ' + String(questionJP)) : '');
                            } else if (questionJP !== undefined && questionJP !== null && questionJP !== '') {
                              mainText = String(questionJP);
                            }

                            if (mainText === null || mainText === undefined || mainText === '') return null;
                            mainText = mainText
                              .replace(/\s*and baseline scored it at:\s*[\u2014\u2013-]?\s*/gi, ' ')
                              .replace(/(\d)\s*\/(\d)/g, '$1 / $2')
                              .replace(/\s{2,}/g, ' ')
                              .trim();
                            if (!mainText) return null;
                            const gid = idForItem(item);
                            const gIsEditing = (globalEditMode || !!editingItems[gid]);
                            const gVals = editableValues[gid] || {};
                            if (gIsEditing) {
                              const hasLocalJournal = !!(gVals && Object.prototype.hasOwnProperty.call(gVals, 'journalPhrase'));
                              const localJournal = hasLocalJournal ? String(gVals.journalPhrase || '') : '';
                              const localLower = localJournal.trim().toLowerCase();
                              const labelLower = (redflagLabel || '').toLowerCase();
                              const shouldUseMainForRedflag = !!(redflagLabel && localJournal.trim() && !localLower.startsWith(labelLower));
                              const editValue = shouldUseMainForRedflag
                                ? mainText
                                : (hasLocalJournal ? localJournal : mainText);
                              return (
                                <Box key={item.question_id || idx} style={{ marginLeft: 18, marginBottom: 0 }}>
                                  <TextField
                                    value={editValue}
                                    onChange={(e) => setEditableValues(prev => ({ ...(prev||{}), [gid]: { ...(prev && prev[gid] ? prev[gid] : {}), journalPhrase: e.target.value, metaJournalPhrase: '' } }))}
                                    fullWidth
                                    margin="dense"
                                    multiline
                                    InputProps={{ disableUnderline: true, style: { fontSize: 14, lineHeight: '1.5', color: '#324158', padding: 1 } }}
                                    inputProps={{ style: { padding: '1px 1px', lineHeight: '1.5' } }}
                                  />
                                  {/* global save on finish; inline Save/Cancel removed */}
                                </Box>
                              );
                            }
                              return (
                              <Typography key={item.question_id || idx} variant="body2" className={classes.compactText}>
                                {mainText}
                                {/* single global edit toggles all fields */}
                              </Typography>
                            );
                          })}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    ));
                  })()}
                </Box>
          </Grid>
        </Grid>
        
        </Grid>            
        )}
        
        {headerView === 'Epicrisis' && (
          <EpicrisisView 
            selectedPatient={selectedPatient}
            userToken={userToken}
          />
        )}
        
        {headerView === 'Status Rapport' && (
          <StatusRapportView 
            selectedPatient={selectedPatient}
            userToken={userToken}
          />
        )}

        {headerView === 'Development' && (
          <DevelopmentView
            selectedPatient={selectedPatient}
            userToken={userToken}
            diagnosis={appliedDiagnosis || (Array.isArray(diagnosis) ? diagnosis[0] : diagnosis)}
            setActiveTab={setActiveTab}
          />
        )}

        {headerView === 'Questionnaires' && (
          <Paper elevation={0} style={{ borderRadius: 12, border: '1px solid rgba(0,45,85,0.12)', padding: 16, background: '#fff' }}>
            <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
              Questionnaires
            </Typography>
            <Typography variant="body2" style={{ color: '#486d86', marginBottom: 12 }}>
              Open the questionnaire pages directly from this panel.
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 10 }}>
              <Button
                variant="contained"
                className={classes.headerPrimaryButton}
                onClick={() => { window.location.href = '/assessment'; }}
              >
                Initial Questionnaire
              </Button>
              <Button
                variant="outlined"
                className={classes.headerSecondaryButton}
                onClick={() => { window.location.href = '/FollowUpAssessment_questionaire'; }}
              >
                Follow-up Questionnaire
              </Button>
            </Box>
          </Paper>
        )}
      </CardContent>

      <Dialog
        open={unsavedJournalDialogOpen}
        onClose={handleUnsavedJournalCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle>
          <div className={classes.dialogTitleRow}>
            <span>Unsaved Journal Changes</span>
            <IconButton
              size="small"
              onClick={handleUnsavedJournalCancel}
              className={classes.dialogCloseButton}
              disabled={unsavedJournalDialogBusy}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <Typography variant="body1" style={{ color: '#002D55' }}>
            You have unsaved journal changes. Do you want to save them before {unsavedJournalDialogLabel || 'continuing'}?
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActionsRow}>
          <Button
            onClick={handleUnsavedJournalDiscard}
            className={classes.headerSecondaryButton}
            variant="contained"
            disabled={unsavedJournalDialogBusy}
          >
            Don't save
          </Button>
          <Button
            onClick={handleUnsavedJournalSave}
            className={classes.headerPrimaryButton}
            variant="contained"
            disabled={unsavedJournalDialogBusy}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Treatment preview / auto-generate confirmation dialog */}
      <Dialog
        open={showAutoGenDialog}
        onClose={() => {
          clearDiagnosisPreviewState();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle>
          <Box className={classes.diagnosisPreviewPhaseHeader}>
            <Typography variant="h5" style={{ color: '#002D55', fontWeight: 'bold' }}>
              {pendingDiagnosisChange && pendingDiagnosisChange.type === 'existing'
                ? 'Preview Treatment Program'
                : 'Auto-generate Treatment Program'}
            </Typography>
            {pendingDiagnosisChange && pendingDiagnosisChange.type === 'existing' && activeDiagnosisPreview && activeDiagnosisPreview.diagnosis && (
              <Typography variant="body2" className={classes.diagnosisPreviewMuted} style={{ marginTop: 4 }}>
                {activeDiagnosisPreview.diagnosis.title || activeDiagnosisPreview.diagnosis.diagnosis}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent className={classes.diagnosisPreviewDialogContent}>
          {pendingDiagnosisChange && pendingDiagnosisChange.type === 'existing' ? (
            <>
              <Box className={classes.diagnosisPreviewSummary}>
                <Typography variant="body1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                  Review the linked treatment program before changing the diagnosis.
                </Typography>
                <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                  This preview shows the treatment that will be applied when you confirm the change with program generation.
                </Typography>
                {diagnosisPreviewLoading && (
                  <Typography variant="body2" style={{ marginTop: 10, color: '#002D55' }}>
                    Loading treatment preview...
                  </Typography>
                )}
                {diagnosisPreviewError && (
                  <Typography variant="body2" color="error" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
                    {diagnosisPreviewError}
                  </Typography>
                )}
                {!diagnosisPreviewLoading && activeDiagnosisPreview && activeDiagnosisPreview.progression_rules && (
                  <Typography variant="body2" style={{ marginTop: 10, color: '#002D55' }}>
                    Program phases: {Array.isArray(activeDiagnosisPreview.phases) ? activeDiagnosisPreview.phases.length : 0}
                  </Typography>
                )}
              </Box>

              {Array.isArray(activeDiagnosisPreview && activeDiagnosisPreview.phases) && activeDiagnosisPreview.phases.map((phase, phaseIndex) => {
                const transition = Array.isArray(activeDiagnosisPreview.progression_rules && activeDiagnosisPreview.progression_rules.transitions)
                  ? activeDiagnosisPreview.progression_rules.transitions.find((item) => String(item.source_phase_index) === String(phase.index))
                  : null;
                const metricLabels = Array.isArray(activeDiagnosisPreview.progression_rules && activeDiagnosisPreview.progression_rules.available_metrics)
                  ? activeDiagnosisPreview.progression_rules.available_metrics.reduce((acc, metric) => {
                    if (metric && metric.key) acc[metric.key] = metric.label || metric.key;
                    return acc;
                  }, {})
                  : {};
                const ruleLines = formatPreviewRuleLines(transition, metricLabels);
                const phaseExercises = Array.isArray(phase.exercises) ? phase.exercises : [];
                const phaseAdvices = Array.isArray(phase.advices) ? phase.advices : [];
                const activePhaseTab = previewPhaseTabByIndex[phaseIndex] || 0;
                return (
                  <Box key={phase.id || `${phase.index}-${phaseIndex}`} className={classes.diagnosisPreviewPhaseCard}>
                    <Box className={classes.diagnosisPreviewPhaseHeader}>
                      <div>
                        <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700 }}>
                          {phase.name || `Phase ${phase.index + 1}`}
                        </Typography>
                        <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                          {phase.numberOfSessions ? `${phase.numberOfSessions} sessions` : 'Sessions not specified'}
                          {phase.description ? ` · ${phase.description}` : ''}
                        </Typography>
                      </div>
                    </Box>
                    <Box mb={1.5}>
                      <Tabs
                        value={activePhaseTab}
                        onChange={(event, nextValue) => {
                          setPreviewPhaseTabByIndex((prev) => ({
                            ...prev,
                            [phaseIndex]: nextValue,
                          }));
                        }}
                        className={classes.diagnosisPreviewPhaseTabs}
                        variant="standard"
                        textColor="inherit"
                        indicatorColor="secondary"
                      >
                        <Tab
                          label="Exercises"
                          disableRipple
                          classes={{ root: classes.diagnosisPreviewPhaseTab, selected: classes.diagnosisPreviewPhaseTabSelected }}
                        />
                        <Tab
                          label="Advices"
                          disableRipple
                          classes={{ root: classes.diagnosisPreviewPhaseTab, selected: classes.diagnosisPreviewPhaseTabSelected }}
                        />
                      </Tabs>

                      {activePhaseTab === 0 ? (
                        <>
                          <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel}>
                            Exercises
                          </Typography>
                          {phaseExercises.length ? (
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {phaseExercises.map((exercise, exerciseIndex) => {
                                const exerciseImageSrc = exercise?.picture || exercise?.img || '';
                                return (
                                  <Card
                                    key={exercise.id || exercise.identifier || `${phase.id}-ex-${exerciseIndex}`}
                                    variant="outlined"
                                    className={classes.diagnosisPreviewItemCard}
                                  >
                                    <CardContent style={{ paddingBottom: 12 }}>
                                      <Box className={classes.diagnosisPreviewItemTopRow}>
                                        <Box className={classes.diagnosisPreviewItemTitleRow}>
                                          {exerciseImageSrc ? (
                                            <Avatar
                                              variant="rounded"
                                              src={exerciseImageSrc}
                                              className={classes.diagnosisPreviewItemAvatar}
                                              imgProps={{ alt: exercise.title || exercise.identifier || 'Exercise' }}
                                            />
                                          ) : (
                                            <Avatar variant="rounded" className={classes.diagnosisPreviewItemAvatar}>
                                              <CheckIcon style={{ fontSize: 18 }} />
                                            </Avatar>
                                          )}
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Typography className={classes.diagnosisPreviewItemTitle}>
                                              {exercise.title || exercise.identifier || 'Exercise'}
                                            </Typography>
                                            <Typography className={classes.diagnosisPreviewItemBody} style={{ marginTop: 4 }}>
                                              {formatPreviewExerciseDetails(exercise) || 'No repetitions or frequency details available.'}
                                            </Typography>
                                            {exercise.description ? (
                                              <Typography className={clsx(classes.diagnosisPreviewItemBody, classes.diagnosisPreviewItemBodyClamped)}>
                                                {exercise.description}
                                              </Typography>
                                            ) : null}
                                          </Box>
                                        </Box>
                                        <Box className={classes.diagnosisPreviewCardActions} onClick={(event) => event.stopPropagation()}>
                                          <Chip label={`#${exerciseIndex + 1}`} size="small" className={classes.diagnosisPreviewItemBadge} />
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            className={classes.diagnosisPreviewActionButton}
                                            onClick={() => openPreviewExerciseEditDialog(phaseIndex, exerciseIndex, exercise)}
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            className={classes.diagnosisPreviewActionButton}
                                            onClick={() => openPreviewExerciseLibraryDrawer({ mode: 'replace', phaseIndex, exerciseIndex })}
                                          >
                                            Replace
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            className={classes.diagnosisPreviewActionButton}
                                            onClick={() => removePreviewExercise(phaseIndex, exerciseIndex)}
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                              No exercises defined for this phase.
                            </Typography>
                          )}
                          <Box style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.diagnosisPreviewActionButton}
                              onClick={() => openPreviewExerciseLibraryDrawer({ mode: 'add', phaseIndex })}
                            >
                              Add exercise
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel}>
                            Advices
                          </Typography>
                          {phaseAdvices.length ? (
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {phaseAdvices.map((advice, adviceIndex) => (
                                <Card
                                  key={`${phase.id}-adv-${adviceIndex}`}
                                  variant="outlined"
                                  className={classes.diagnosisPreviewItemCard}
                                >
                                  <CardContent style={{ paddingBottom: 12 }}>
                                    <Box className={classes.diagnosisPreviewItemTopRow}>
                                      <Box className={classes.diagnosisPreviewItemTitleRow}>
                                        <Avatar className={classes.diagnosisPreviewItemAvatar}>
                                          <CheckIcon style={{ fontSize: 20 }} />
                                        </Avatar>
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                          <Typography className={classes.diagnosisPreviewItemTitle}>
                                            {advice.title || 'Advice'}
                                          </Typography>
                                          <Typography className={clsx(classes.diagnosisPreviewItemBody, classes.diagnosisPreviewItemBodyClamped)}>
                                            {advice.advice || 'No advice text available.'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box className={classes.diagnosisPreviewCardActions} onClick={(event) => event.stopPropagation()}>
                                        <Chip label={`#${adviceIndex + 1}`} size="small" className={classes.diagnosisPreviewItemBadge} />
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          className={classes.diagnosisPreviewActionButton}
                                          onClick={() => openPreviewAdviceEditDialog(phaseIndex, adviceIndex, advice)}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          className={classes.diagnosisPreviewActionButton}
                                          onClick={() => openPreviewAdviceLibraryDrawer({ mode: 'replace', phaseIndex, adviceIndex })}
                                        >
                                          Replace
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          className={classes.diagnosisPreviewActionButton}
                                          onClick={() => removePreviewAdvice(phaseIndex, adviceIndex)}
                                        >
                                          Remove
                                        </Button>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                              No advices defined for this phase.
                            </Typography>
                          )}
                          <Box style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.diagnosisPreviewActionButton}
                              onClick={() => openPreviewAdviceLibraryDrawer({ mode: 'add', phaseIndex })}
                            >
                              Add advice
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>

                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, marginBottom: 6 }}>
                        <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel} style={{ marginBottom: 0 }}>
                          Phase Progression Criteria
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.headerSecondaryButton}
                          classes={{ root: classes.headerControlButton }}
                          onClick={() => openPreviewPhaseProgressionEditDialog(phaseIndex, transition)}
                        >
                          Edit criteria
                        </Button>
                      </Box>
                      {ruleLines && ruleLines.length ? (
                        <Card variant="outlined" style={{ borderRadius: 14, borderColor: 'rgba(0,45,85,0.12)', boxShadow: 'none' }}>
                          <CardContent style={{ paddingBottom: 12 }}>
                            <ul className={classes.diagnosisPreviewList}>
                              {ruleLines.map((line, lineIndex) => (
                                <li key={`${phase.id}-rule-${lineIndex}`} className={classes.diagnosisPreviewListItem}>
                                  <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                                    {line}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      ) : (
                        <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                          No progression criteria defined.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {activeDiagnosisPreview && activeDiagnosisPreview.treatment && (
                <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                  Default treatment template: {activeDiagnosisPreview.treatment.phase_count || 0} phases prepared for {activeDiagnosisPreview.treatment.name || 'this diagnosis'}.
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1" style={{ marginBottom: 16 }}>
                Would you like to automatically generate a default treatment program and advices for this diagnosis?
              </Typography>
              <Typography variant="body2" style={{ color: '#666' }}>
                This will create a standard treatment plan based on the diagnosis template. You can customize it later in the program builder.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => {
              clearDiagnosisPreviewState();
              setSelectedTemplate("");
              setShowTemplateSelector(false);
            }}
            className={classes.headerSecondaryButton}
            classes={{ root: classes.headerControlButton }}
            variant="outlined"
          >
            Cancel
          </Button>
          {pendingDiagnosisChange && pendingDiagnosisChange.type === 'existing' ? (
            <>
              <Button
                onClick={() => handleConfirmDiagnosisChange(false)}
                className={classes.headerSecondaryButton}
                classes={{ root: classes.headerControlButton }}
                variant="outlined"
                disabled={diagnosisPreviewLoading}
              >
                Change without program
              </Button>
              <Tooltip title="Applying the linked treatment template will replace the current exercises, advices, and progression criteria.">
                <span>
                  <Button
                    onClick={() => handleConfirmDiagnosisChange(true)}
                    className={classes.headerPrimaryButton}
                    classes={{ root: classes.headerControlButton }}
                    variant="contained"
                    autoFocus
                    disabled={diagnosisPreviewLoading}
                  >
                    Change and apply program
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                onClick={() => handleConfirmDiagnosisChange(false)}
                className={classes.headerSecondaryButton}
                classes={{ root: classes.headerControlButton }}
                variant="outlined"
              >
                No, Skip.
              </Button>
              <Tooltip title="Generating a default treatment program will overwrite the current exercises and advices.">
                <Button
                  onClick={() => handleConfirmDiagnosisChange(true)}
                  className={classes.headerPrimaryButton}
                  classes={{ root: classes.headerControlButton }}
                  variant="contained"
                  autoFocus
                >
                  Yes, Generate.
                </Button>
              </Tooltip>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewPhaseProgressionDialogOpen}
        onClose={closePreviewPhaseProgressionEditDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 18, maxWidth: 920 } }}
      >
        <DialogTitle>
          <Box className={classes.dialogTitleRow}>
            <span>Phase Progression Criteria</span>
            <IconButton size="small" onClick={closePreviewPhaseProgressionEditDialog} className={classes.dialogCloseButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.diagnosisPreviewDialogContent}>
          {previewPhaseProgressionToEdit ? (
            <>
              <Box className={classes.diagnosisPreviewSummary}>
                <Typography variant="body1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                  Edit the progression criteria that will be applied to this phase transition.
                </Typography>
                <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                  {previewPhaseProgressionToEdit.source_phase_name || `Phase ${previewPhaseProgressionEditContext?.sourcePhaseIndex ?? ''}`}
                  {' '}->{' '}
                  {previewPhaseProgressionToEdit.target_phase_name || `Phase ${previewPhaseProgressionEditContext?.targetPhaseIndex ?? ''}`}
                </Typography>
              </Box>

              {previewPhaseProgressionDraftCombos.map((combo, comboIndex) => (
                <Box key={combo.localId} style={{ border: '1px solid rgba(0,45,85,0.12)', borderRadius: 14, padding: 12, background: '#fff' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    <Typography style={{ fontSize: 13, fontWeight: 700, color: '#002D55' }}>
                      Rule combo {comboIndex + 1}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.headerSecondaryButton}
                      classes={{ root: classes.headerControlButton }}
                      onClick={() => removePreviewPhaseProgressionCombo(combo.localId)}
                      disabled={previewPhaseProgressionDraftCombos.length <= 1}
                    >
                      Remove combo
                    </Button>
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {combo.conditions.map((condition, conditionIndex) => (
                      <Box key={`${combo.localId}-condition-${conditionIndex}`} style={{ border: '1px solid #d8e2ef', borderRadius: 14, padding: 12, background: '#f7faff' }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={7}>
                            <TextField
                              select
                              fullWidth
                              label={conditionIndex === 0 ? 'Time' : 'Criteria'}
                              value={condition.metric_key}
                              onChange={(event) => updatePreviewPhaseProgressionDraftCondition(combo.localId, conditionIndex, 'metric_key', event.target.value)}
                              variant="outlined"
                              size="small"
                              className={`${classes.orangeFocused} ${classes.diagnosisOtherTextField}`}
                              InputLabelProps={{ shrink: true }}
                            >
                              {getPreviewPhaseProgressionMetricOptions(combo.conditions, conditionIndex).map((option) => (
                                <MenuItem key={option.key} value={option.key} className={classes.menuItem}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              select
                              fullWidth
                              label="Operator"
                              value={condition.operator}
                              onChange={(event) => updatePreviewPhaseProgressionDraftCondition(combo.localId, conditionIndex, 'operator', event.target.value)}
                              variant="outlined"
                              size="small"
                              className={`${classes.orangeFocused} ${classes.diagnosisOtherTextField}`}
                              InputLabelProps={{ shrink: true }}
                            >
                              <MenuItem value="gte" className={classes.menuItem}>At least</MenuItem>
                              <MenuItem value="lte" className={classes.menuItem}>At most</MenuItem>
                              <MenuItem value="eq" className={classes.menuItem}>Exactly</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label={getPreviewPhaseProgressionThresholdLabel(condition)}
                              type="number"
                              value={condition.threshold_value}
                              onChange={(event) => updatePreviewPhaseProgressionDraftCondition(combo.localId, conditionIndex, 'threshold_value', event.target.value)}
                              variant="outlined"
                              size="small"
                              className={`${classes.orangeFocused} ${classes.diagnosisOtherTextField}`}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <Box display="flex" justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={() => removePreviewPhaseProgressionCondition(combo.localId, conditionIndex)}
                                disabled={combo.conditions.length <= 1}
                                style={{ color: '#FF7F27' }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                          {condition.metric_key === 'exercise_level_progressions' && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Exercise identifiers (comma separated)"
                                value={condition.exercise_identifiers_text || ''}
                                onChange={(event) => updatePreviewPhaseProgressionDraftCondition(combo.localId, conditionIndex, 'exercise_identifiers_text', event.target.value)}
                                variant="outlined"
                                size="small"
                                className={`${classes.orangeFocused} ${classes.diagnosisOtherTextField}`}
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional: limit this rule to specific exercises."
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
                    <Button
                      onClick={() => addPreviewPhaseProgressionCondition(combo.localId)}
                      variant="outlined"
                      className={classes.headerSecondaryButton}
                      classes={{ root: classes.headerControlButton }}
                    >
                      Add criterion
                    </Button>
                    <Button
                      onClick={() => addPreviewPhaseProgressionConditionCombo()}
                      variant="outlined"
                      className={classes.headerSecondaryButton}
                      classes={{ root: classes.headerControlButton }}
                    >
                      Add combo
                    </Button>
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
              No progression criteria are available for this phase transition.
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={closePreviewPhaseProgressionEditDialog}
            className={classes.headerSecondaryButton}
            classes={{ root: classes.headerControlButton }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={applyPreviewPhaseProgressionDraft}
            className={classes.headerPrimaryButton}
            classes={{ root: classes.headerControlButton }}
            variant="contained"
          >
            Save criteria
          </Button>
        </DialogActions>
      </Dialog>

      <ExerciseEditDialog
        open={previewExerciseDialogOpen}
        onClose={closePreviewExerciseDialog}
        onConfirm={handleConfirmPreviewExerciseEdit}
        selectedExercise={previewExerciseToEdit}
        editedParams={previewEditedExerciseParams}
        onParamChange={handlePreviewExerciseParamChange}
        dialogTitle="Edit preview exercise"
        libraryExercises={[]}
      />

      <Dialog
        open={previewAdviceDialogOpen}
        onClose={closePreviewAdviceDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 18, maxWidth: 780 } }}
      >
        <DialogTitle>
          <Box className={classes.dialogTitleRow}>
            <span>Edit preview advice</span>
            <IconButton size="small" onClick={closePreviewAdviceDialog} className={classes.dialogCloseButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TextField
            label="Title"
            value={previewEditedAdviceParams.title}
            onChange={(event) => handlePreviewAdviceParamChange('title', event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.diagnosisOtherTextField}
          />
          <TextField
            label="Advice"
            value={previewEditedAdviceParams.advice}
            onChange={(event) => handlePreviewAdviceParamChange('advice', event.target.value)}
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            size="small"
            className={classes.diagnosisOtherTextField}
          />
          <TextField
            label="Importance"
            type="number"
            value={previewEditedAdviceParams.importance}
            onChange={(event) => handlePreviewAdviceParamChange('importance', event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            inputProps={{ min: 1 }}
            className={classes.diagnosisOtherTextField}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActionsRow}>
          <Button onClick={closePreviewAdviceDialog} className={classes.Button} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmPreviewAdviceEdit} className={classes.Button2} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={previewExerciseLibraryOpen}
        onClose={closePreviewExerciseLibraryDrawer}
        PaperProps={{ style: { width: '75vw', background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
      >
        <Box style={{ padding: 18, flex: 1, overflowY: 'auto' }}>
          <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
            {previewLibraryMode === 'replace' ? 'Replace exercise in preview' : 'Add exercise to preview'}
          </Typography>
          {previewLibraryExercisesLoading && (
            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginBottom: 12 }}>
              Loading exercises...
            </Typography>
          )}
          {previewLibraryExercisesError && (
            <Typography style={{ color: '#c62828', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginBottom: 12, whiteSpace: 'pre-wrap' }}>
              {previewLibraryExercisesError}
            </Typography>
          )}
          <ExerciseLibrary
            onAddExercise={applyPreviewExerciseSelection}
            onExerciseSelect={applyPreviewExerciseSelection}
            selectionActionLabel={previewLibraryMode === 'replace' ? 'Replace' : 'Add'}
            exercises={previewLibraryExercises}
            title="Exercise library"
          />
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={previewAdviceLibraryOpen}
        onClose={closePreviewAdviceLibraryDrawer}
        PaperProps={{ style: { width: '75vw', background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
      >
        <Box style={{ padding: 18, flex: 1, overflowY: 'auto' }}>
          <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
            {previewLibraryMode === 'replace' ? 'Replace advice in preview' : 'Add advice to preview'}
          </Typography>
          <AdviceLibrary
            onAddAdvice={applyPreviewAdviceSelection}
            handleRemoveAdviceFrontend={() => {}}
          />
        </Box>
      </Drawer>

      {/* Template preview dialog */}
      <Dialog
        open={showTemplateAutoGenDialog}
        onClose={() => {
          clearDiagnosisPreviewState();
          setSelectedTemplate("");
          setShowTemplateSelector(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 18 } }}
      >
        <DialogTitle>
          <Box className={classes.diagnosisPreviewPhaseHeader}>
            <Typography variant="h5" style={{ color: '#002D55', fontWeight: 'bold' }}>
              Preview Treatment Template
            </Typography>
            {activeDiagnosisPreview && activeDiagnosisPreview.diagnosis && (
              <Typography variant="body2" className={classes.diagnosisPreviewMuted} style={{ marginTop: 4 }}>
                {activeDiagnosisPreview.diagnosis.title || activeDiagnosisPreview.diagnosis.diagnosis}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent className={classes.diagnosisPreviewDialogContent}>
          <Box className={classes.diagnosisPreviewSummary}>
            <Typography variant="body1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
              Review the template before applying it to the current treatment.
            </Typography>
            <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
              The diagnosis will remain unchanged. Only the treatment phases, exercises, advices, and progression criteria will be updated.
            </Typography>
            {diagnosisPreviewLoading && (
              <Typography variant="body2" style={{ marginTop: 10, color: '#002D55' }}>
                Loading template preview...
              </Typography>
            )}
            {diagnosisPreviewError && (
              <Typography variant="body2" color="error" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
                {diagnosisPreviewError}
              </Typography>
            )}
          </Box>

          {Array.isArray(activeDiagnosisPreview && activeDiagnosisPreview.phases) && activeDiagnosisPreview.phases.map((phase, phaseIndex) => {
            const transition = Array.isArray(activeDiagnosisPreview.progression_rules && activeDiagnosisPreview.progression_rules.transitions)
              ? activeDiagnosisPreview.progression_rules.transitions.find((item) => String(item.source_phase_index) === String(phase.index))
              : null;
            const metricLabels = Array.isArray(activeDiagnosisPreview.progression_rules && activeDiagnosisPreview.progression_rules.available_metrics)
              ? activeDiagnosisPreview.progression_rules.available_metrics.reduce((acc, metric) => {
                if (metric && metric.key) acc[metric.key] = metric.label || metric.key;
                return acc;
              }, {})
              : {};
            const ruleLines = formatPreviewRuleLines(transition, metricLabels);
            const phaseExercises = Array.isArray(phase.exercises) ? phase.exercises : [];
            const phaseAdvices = Array.isArray(phase.advices) ? phase.advices : [];
            const activePhaseTab = previewPhaseTabByIndex[phaseIndex] || 0;
            return (
              <Box key={phase.id || `${phase.index}-${phaseIndex}`} className={classes.diagnosisPreviewPhaseCard}>
                <Box className={classes.diagnosisPreviewPhaseHeader}>
                  <div>
                    <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700 }}>
                      {phase.name || `Phase ${phase.index + 1}`}
                    </Typography>
                    <Typography variant="body2" className={classes.diagnosisPreviewMuted}>
                      {phase.numberOfSessions ? `${phase.numberOfSessions} sessions` : 'Sessions not specified'}
                      {phase.description ? ` · ${phase.description}` : ''}
                    </Typography>
                  </div>
                </Box>
                <Box mb={1.5}>
                  <Tabs
                    value={activePhaseTab}
                    onChange={(event, nextValue) => {
                      setPreviewPhaseTabByIndex((prev) => ({
                        ...prev,
                        [phaseIndex]: nextValue,
                      }));
                    }}
                    className={classes.diagnosisPreviewPhaseTabs}
                    variant="standard"
                    textColor="inherit"
                    indicatorColor="secondary"
                  >
                    <Tab label="Exercises" disableRipple classes={{ root: classes.diagnosisPreviewPhaseTab, selected: classes.diagnosisPreviewPhaseTabSelected }} />
                    <Tab label="Advices" disableRipple classes={{ root: classes.diagnosisPreviewPhaseTab, selected: classes.diagnosisPreviewPhaseTabSelected }} />
                  </Tabs>

                  {activePhaseTab === 0 ? (
                    <>
                      <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel}>Exercises</Typography>
                      {phaseExercises.length ? (
                        <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {phaseExercises.map((exercise, exerciseIndex) => {
                            const exerciseImageSrc = exercise?.picture || exercise?.img || '';
                            return (
                              <Card key={exercise.id || exercise.identifier || `${phase.id}-ex-${exerciseIndex}`} variant="outlined" className={classes.diagnosisPreviewItemCard}>
                                <CardContent style={{ paddingBottom: 12 }}>
                                  <Box className={classes.diagnosisPreviewItemTopRow}>
                                    <Box className={classes.diagnosisPreviewItemTitleRow}>
                                      {exerciseImageSrc ? (
                                        <Avatar variant="rounded" src={exerciseImageSrc} className={classes.diagnosisPreviewItemAvatar} imgProps={{ alt: exercise.title || exercise.identifier || 'Exercise' }} />
                                      ) : (
                                        <Avatar variant="rounded" className={classes.diagnosisPreviewItemAvatar}>
                                          <CheckIcon style={{ fontSize: 18 }} />
                                        </Avatar>
                                      )}
                                      <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Typography className={classes.diagnosisPreviewItemTitle}>{exercise.title || exercise.identifier || 'Exercise'}</Typography>
                                        <Typography className={classes.diagnosisPreviewItemBody} style={{ marginTop: 4 }}>
                                          {formatPreviewExerciseDetails(exercise) || 'No repetitions or frequency details available.'}
                                        </Typography>
                                        {exercise.description ? (
                                          <Typography className={clsx(classes.diagnosisPreviewItemBody, classes.diagnosisPreviewItemBodyClamped)}>{exercise.description}</Typography>
                                        ) : null}
                                      </Box>
                                    </Box>
                                    <Box className={classes.diagnosisPreviewCardActions} onClick={(event) => event.stopPropagation()}>
                                      <Chip label={`#${exerciseIndex + 1}`} size="small" className={classes.diagnosisPreviewItemBadge} />
                                      <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewExerciseEditDialog(phaseIndex, exerciseIndex, exercise)}>Edit</Button>
                                      <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewExerciseLibraryDrawer({ mode: 'replace', phaseIndex, exerciseIndex })}>Replace</Button>
                                      <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => removePreviewExercise(phaseIndex, exerciseIndex)}>Remove</Button>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" className={classes.diagnosisPreviewMuted}>No exercises defined for this phase.</Typography>
                      )}
                      <Box style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewExerciseLibraryDrawer({ mode: 'add', phaseIndex })}>Add exercise</Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel}>Advices</Typography>
                      {phaseAdvices.length ? (
                        <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {phaseAdvices.map((advice, adviceIndex) => (
                            <Card key={`${phase.id}-adv-${adviceIndex}`} variant="outlined" className={classes.diagnosisPreviewItemCard}>
                              <CardContent style={{ paddingBottom: 12 }}>
                                <Box className={classes.diagnosisPreviewItemTopRow}>
                                  <Box className={classes.diagnosisPreviewItemTitleRow}>
                                    <Avatar className={classes.diagnosisPreviewItemAvatar}><CheckIcon style={{ fontSize: 20 }} /></Avatar>
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                      <Typography className={classes.diagnosisPreviewItemTitle}>{advice.title || 'Advice'}</Typography>
                                      <Typography className={clsx(classes.diagnosisPreviewItemBody, classes.diagnosisPreviewItemBodyClamped)}>{advice.advice || 'No advice text available.'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box className={classes.diagnosisPreviewCardActions} onClick={(event) => event.stopPropagation()}>
                                    <Chip label={`#${adviceIndex + 1}`} size="small" className={classes.diagnosisPreviewItemBadge} />
                                    <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewAdviceEditDialog(phaseIndex, adviceIndex, advice)}>Edit</Button>
                                    <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewAdviceLibraryDrawer({ mode: 'replace', phaseIndex, adviceIndex })}>Replace</Button>
                                    <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => removePreviewAdvice(phaseIndex, adviceIndex)}>Remove</Button>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" className={classes.diagnosisPreviewMuted}>No advices defined for this phase.</Typography>
                      )}
                      <Box style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" className={classes.diagnosisPreviewActionButton} onClick={() => openPreviewAdviceLibraryDrawer({ mode: 'add', phaseIndex })}>Add advice</Button>
                      </Box>
                    </>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8, marginBottom: 6 }}>
                    <Typography variant="body2" className={classes.diagnosisPreviewSectionLabel} style={{ marginBottom: 0 }}>
                      Phase Progression Criteria
                    </Typography>
                    <Button size="small" variant="outlined" className={classes.headerSecondaryButton} classes={{ root: classes.headerControlButton }} onClick={() => openPreviewPhaseProgressionEditDialog(phaseIndex, transition)}>Edit criteria</Button>
                  </Box>
                  {ruleLines && ruleLines.length ? (
                    <Card variant="outlined" style={{ borderRadius: 14, borderColor: 'rgba(0,45,85,0.12)', boxShadow: 'none' }}>
                      <CardContent style={{ paddingBottom: 12 }}>
                        <ul className={classes.diagnosisPreviewList}>
                          {ruleLines.map((line, lineIndex) => (
                            <li key={`${phase.id}-rule-${lineIndex}`} className={classes.diagnosisPreviewListItem}>
                              <Typography variant="body2" className={classes.diagnosisPreviewMuted}>{line}</Typography>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ) : (
                    <Typography variant="body2" className={classes.diagnosisPreviewMuted}>No progression criteria defined.</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => {
              clearDiagnosisPreviewState();
              setSelectedTemplate("");
              setShowTemplateSelector(false);
            }}
            className={classes.headerSecondaryButton}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={requestTemplateApplication}
            className={classes.headerPrimaryButton}
            variant="contained"
            autoFocus
            disabled={diagnosisPreviewLoading}
          >
            Apply template
          </Button>
        </DialogActions>
      </Dialog>
      <ClinicianFollowupDialog
        open={followupDialogOpen}
        onClose={() => setFollowupDialogOpen(false)}
        onSaved={() => setFollowupDialogOpen(false)}
        patientId={selectedPatient && (selectedPatient.user_id || selectedPatient.id)}
        diagnosisIdentifier={diagnosis && (diagnosis.diagnosis || diagnosis.diagnosis_identifier)}
        treatmentId={props.treatmentId || (selectedPatient && selectedPatient.treatment_id)}
        userToken={userToken}
      />
    </Card>
    </>
  );
};

SelectedPatientDiagnosis.propTypes = {
  className: PropTypes.string,
  selectedPatient: PropTypes.object,
  diagnosis: PropTypes.object,
  userToken: PropTypes.string,
  // ✅ Phase 1: New consolidated props from parent
  assessmentDate: PropTypes.object,     // { assessment_date: string } - from /treatment/answered_assessment_date/
  patientAdvices: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), // from /treatment/advices/patient/
  patientPhases: PropTypes.array,       // from /treatment/phases/patient/
  onEditInProgramBuilder: PropTypes.func,
  setActiveTab: PropTypes.func,
  setShowAdviceInExerciseTab: PropTypes.func,
  onDiagnosisChanged: PropTypes.func,    // Callback to notify parent to refetch data
  onOpenReportDialog: PropTypes.func,    // Handler to open report dialog
  onJournalSectionsUpdate: PropTypes.func, // Callback to update parent with journal sections
  onDiagnosisUpdate: PropTypes.func      // Callback to update parent with applied diagnosis
};

export default SelectedPatientDiagnosis;